/**
 * 2026 Career Expansion v2: 202 additional careers taking the total
 * from 298 -> 500. Each seed authored with category-calibrated weights
 * + per-career overrides. Twelve priority careers include soloMeta.
 *
 * AUTOGENERATED by scripts/build-careers-500.mjs - edit the generator,
 * not this file directly.
 */
import type { CareerSeed } from "./careers";

export const CAREER_EXPANSION_V2_SEEDS: CareerSeed[] = [
  {
    "name": "AI Consultant",
    "category": "AI, Data and Automation",
    "shortDescription": "Help businesses figure out where AI actually helps - then design and ship the workflow that delivers it.",
    "whyItFits": "You like translating fuzzy business problems into clear, working systems and explaining tech to non-technical people.",
    "startingSalary": "£45,000 - £65,000",
    "midSalary": "£75,000 - £120,000",
    "seniorSalary": "£140,000 - £250,000+",
    "timeToEntry": "2 - 5 years (usually after a tech or consulting role)",
    "degreeRequired": "Helpful, not required - track record matters more",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "income": 3,
      "freedom": 2,
      "people": 2,
      "structure": 1,
      "creativity": 1
    },
    "specialisations": [
      {
        "title": "AI Strategy",
        "shortDescription": "Define where AI delivers the most value across a business and sequence the roadmap.",
        "typicalEntryRoute": "Consulting or senior tech background + portfolio of outcomes.",
        "salaryRange": "£600 - £1,500 / day"
      },
      {
        "title": "AI Workflow Automation",
        "shortDescription": "Design and ship LLM + no-code workflows that replace manual ops work.",
        "typicalEntryRoute": "Tech or ops background + Zapier/Make/n8n + prompt skills.",
        "salaryRange": "£500 - £1,200 / day"
      },
      {
        "title": "AI Governance",
        "shortDescription": "Set up responsible-use policies, model registers and approval workflows.",
        "typicalEntryRoute": "Risk, compliance or legal background + AI fluency.",
        "salaryRange": "£70,000 - £150,000+"
      },
      {
        "title": "AI Training and Enablement",
        "shortDescription": "Run hands-on programmes that get teams using AI confidently in weeks.",
        "typicalEntryRoute": "Training, L&D or consulting + heavy LLM experience.",
        "salaryRange": "£400 - £1,000 / day"
      },
      {
        "title": "AI Product Implementation",
        "shortDescription": "Embed third-party AI tools (Copilot, ChatGPT Enterprise, Claude) into a company stack.",
        "typicalEntryRoute": "Solutions consulting or IT background + change management.",
        "salaryRange": "£70,000 - £130,000"
      }
    ],
    "skillsNeeded": [
      "Discovery interviews",
      "Prompt engineering",
      "Process mapping",
      "ROI modelling",
      "Change management"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£500 - £2,000 (laptop you already have, ChatGPT/Claude API credits, basic LLC/sole trader setup, website)",
      "timeToFirstClient": "2 - 8 weeks if you already have a network",
      "monthOneIncome": "£0 - £3,000",
      "yearOneIncome": "£40,000 - £120,000 full-time",
      "scaledIncome": "£150,000 - £400,000+ with retainers and productised offers",
      "commonPitfalls": [
        "Pitching 'AI strategy' instead of one concrete, painful workflow",
        "Underpricing day rates as a generalist",
        "Building bespoke tools when GPT/Claude + a workflow tool would do",
        "No case study after the first project - kills referrals"
      ],
      "firstFiveClients": [
        "Old employer or manager - offer one workflow audit free",
        "LinkedIn outbound to 50 ops/marketing leaders in one niche",
        "Speak at one local business meetup with a live demo",
        "Partner with an agency that needs an AI specialist on call",
        "Write one case study showing hours/£ saved and post it weekly"
      ],
      "toolsAndCosts": [
        "ChatGPT Team £20/mo",
        "Claude Pro £18/mo",
        "Zapier/Make £20-£50/mo",
        "Notion £8/mo",
        "Loom £12/mo"
      ],
      "legalBasics": [
        "Register as sole trader or Ltd with HMRC",
        "Professional indemnity insurance £15-£40/mo",
        "Data processing agreement template",
        "VAT registration once turnover hits £90k"
      ]
    },
    "similarCareers": [
      "AI Prompt Engineer",
      "Management Consultant",
      "Solutions Consultant"
    ],
    "higherProgressionCareers": [
      "AI Solutions Architect",
      "Chief AI Officer"
    ]
  },
  {
    "name": "AI Trainer",
    "category": "AI, Data and Automation",
    "shortDescription": "Run hands-on training that turns non-technical teams into confident AI users in days, not months.",
    "whyItFits": "You enjoy teaching and can break down complex tools into simple, immediately useful steps.",
    "startingSalary": "£35,000 - £50,000",
    "midSalary": "£55,000 - £85,000",
    "seniorSalary": "£90,000 - £160,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "people": 3,
      "creativity": 2,
      "income": 1,
      "freedom": 2,
      "structure": 1
    },
    "specialisations": [
      {
        "title": "Executive AI Training",
        "shortDescription": "1:1 and small-group coaching for leadership teams on practical AI use.",
        "typicalEntryRoute": "Senior trainer / coach background + AI portfolio.",
        "salaryRange": "£800 - £2,000 / day"
      },
      {
        "title": "Team Productivity Workshops",
        "shortDescription": "Run intensive workshops for marketing, ops or sales teams.",
        "typicalEntryRoute": "Trainer or consultant + LLM track record.",
        "salaryRange": "£1,500 - £4,000 / day rate"
      },
      {
        "title": "Prompt Engineering Bootcamps",
        "shortDescription": "Multi-day cohorts teaching production prompt techniques.",
        "typicalEntryRoute": "Strong LLM and curriculum design background.",
        "salaryRange": "£500 - £1,500 / day"
      },
      {
        "title": "AI for Educators",
        "shortDescription": "Train teachers, lecturers and L&D professionals on classroom-safe AI use.",
        "typicalEntryRoute": "Teaching background + AI fluency.",
        "salaryRange": "£40,000 - £70,000"
      }
    ],
    "skillsNeeded": [
      "Facilitation",
      "Prompt design",
      "Curriculum building",
      "Slide design",
      "Stakeholder management"
    ],
    "selfEmployable": true,
    "similarCareers": [
      "AI Consultant",
      "Corporate Trainer",
      "L&D Specialist"
    ]
  },
  {
    "name": "AI Implementation Specialist",
    "category": "AI, Data and Automation",
    "shortDescription": "Take an AI strategy on paper and actually ship it into a company's day-to-day tools and workflows.",
    "whyItFits": "You like rolling sleeves up, integrating systems, and seeing real adoption rather than slide decks.",
    "startingSalary": "£40,000 - £55,000",
    "midSalary": "£65,000 - £95,000",
    "seniorSalary": "£100,000 - £160,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "people": 1,
      "creativity": 1,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Microsoft Copilot Rollouts",
        "shortDescription": "Plan and ship M365 Copilot across enterprise tenants.",
        "typicalEntryRoute": "M365 admin background + change management.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "ChatGPT Enterprise Deployment",
        "shortDescription": "Configure tenants, GPTs and governance for enterprise OpenAI rollouts.",
        "typicalEntryRoute": "Solutions engineering background.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Custom GPTs and Agents",
        "shortDescription": "Build internal GPTs and agents for repeatable workflows.",
        "typicalEntryRoute": "Developer or technical PM background.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "AI Adoption Programmes",
        "shortDescription": "Run change-mgmt sprints driving usage from <10% to majority active.",
        "typicalEntryRoute": "L&D, change or PM background.",
        "salaryRange": "£50,000 - £90,000"
      }
    ],
    "skillsNeeded": [
      "APIs",
      "Workflow automation",
      "Change management",
      "QA testing",
      "Documentation"
    ]
  },
  {
    "name": "AI Workflow Designer",
    "category": "AI, Data and Automation",
    "shortDescription": "Design end-to-end AI-assisted workflows that combine LLMs, automation tools and human checkpoints.",
    "whyItFits": "You think in systems and like designing the choreography between humans and AI.",
    "startingSalary": "£40,000 - £55,000",
    "midSalary": "£60,000 - £90,000",
    "seniorSalary": "£100,000 - £150,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "creativity": 2,
      "income": 2,
      "people": 0,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "Zapier / Make Pipelines",
        "shortDescription": "Multi-step automations linking LLMs with CRM, email, docs and data.",
        "typicalEntryRoute": "No-code automation background.",
        "salaryRange": "£40,000 - £80,000"
      },
      {
        "title": "n8n / Open-Source Agents",
        "shortDescription": "Self-hosted agent workflows for teams that need data control.",
        "typicalEntryRoute": "Developer + DevOps background.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "LangChain / LlamaIndex Pipelines",
        "shortDescription": "Production RAG and tool-use chains for engineering teams.",
        "typicalEntryRoute": "Python developer + LLM experience.",
        "salaryRange": "£70,000 - £130,000"
      },
      {
        "title": "Human-in-the-loop Flows",
        "shortDescription": "Workflows with review queues, approvals and audit trails.",
        "typicalEntryRoute": "Process design + LLM background.",
        "salaryRange": "£55,000 - £100,000"
      }
    ],
    "skillsNeeded": [
      "Process design",
      "Zapier/Make/n8n",
      "Prompt chaining",
      "User research",
      "Diagramming"
    ],
    "selfEmployable": true
  },
  {
    "name": "AI Automation Consultant",
    "category": "AI, Data and Automation",
    "shortDescription": "Audit a company's manual work and replace the repetitive parts with AI + automation that pays for itself in months.",
    "whyItFits": "You're commercial, practical, and like building things that obviously save time and money.",
    "startingSalary": "£45,000 - £60,000",
    "midSalary": "£70,000 - £110,000",
    "seniorSalary": "£130,000 - £220,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "income": 3,
      "freedom": 2,
      "structure": 2,
      "people": 1,
      "creativity": 1
    },
    "specialisations": [
      {
        "title": "Ops Automation",
        "shortDescription": "Replace repetitive back-office processes with AI + RPA.",
        "typicalEntryRoute": "Ops or consulting + automation skills.",
        "salaryRange": "£600 - £1,200 / day"
      },
      {
        "title": "Sales Automation",
        "shortDescription": "Automate prospecting, enrichment and CRM hygiene with LLMs.",
        "typicalEntryRoute": "RevOps background + LLM fluency.",
        "salaryRange": "£500 - £1,000 / day"
      },
      {
        "title": "Customer Support Automation",
        "shortDescription": "Deploy AI agents for tier-1 support with safe handoff.",
        "typicalEntryRoute": "Support ops + automation background.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Finance and Reporting Automation",
        "shortDescription": "Automate reconciliation, reporting and finance ops.",
        "typicalEntryRoute": "Finance + automation background.",
        "salaryRange": "£60,000 - £120,000"
      }
    ],
    "skillsNeeded": [
      "Process audits",
      "ROI modelling",
      "No-code tooling",
      "Client comms",
      "Prompt engineering"
    ],
    "selfEmployable": true,
    "similarCareers": [
      "AI Consultant",
      "No-Code Automation Specialist",
      "RPA Developer"
    ]
  },
  {
    "name": "AI Content Strategist",
    "category": "AI, Data and Automation",
    "shortDescription": "Plan and run content systems that use AI to scale output without losing brand voice or quality.",
    "whyItFits": "You combine editorial instincts with a willingness to operate AI tools at production scale.",
    "startingSalary": "£35,000 - £50,000",
    "midSalary": "£55,000 - £85,000",
    "seniorSalary": "£95,000 - £150,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "creativity": 3,
      "structure": 2,
      "income": 1,
      "freedom": 2,
      "people": 1
    },
    "specialisations": [
      {
        "title": "Brand-safe Content Systems",
        "shortDescription": "Build prompt libraries and style guides that protect brand voice.",
        "typicalEntryRoute": "Content strategy + LLM experience.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "SEO at Scale",
        "shortDescription": "Programmatic and topical SEO content systems using LLMs.",
        "typicalEntryRoute": "SEO background + prompt skills.",
        "salaryRange": "£45,000 - £90,000"
      },
      {
        "title": "Lifecycle and Email",
        "shortDescription": "AI-assisted lifecycle email content and personalisation.",
        "typicalEntryRoute": "Lifecycle marketer + LLM fluency.",
        "salaryRange": "£45,000 - £80,000"
      },
      {
        "title": "Sales Enablement Content",
        "shortDescription": "AI-built proposals, decks and battle cards.",
        "typicalEntryRoute": "Product marketing + LLM skills.",
        "salaryRange": "£55,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "Editorial judgement",
      "Brand voice",
      "Prompt design",
      "SEO basics",
      "Content ops"
    ],
    "selfEmployable": true
  },
  {
    "name": "AI Governance Specialist",
    "category": "AI, Data and Automation",
    "shortDescription": "Set the rules for how an organisation uses AI safely, legally and in line with regulations like the EU AI Act.",
    "whyItFits": "You're detail-oriented, comfortable with policy work, and able to influence senior stakeholders.",
    "startingSalary": "£45,000 - £60,000",
    "midSalary": "£70,000 - £100,000",
    "seniorSalary": "£110,000 - £170,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "AI Policy and Standards",
        "shortDescription": "Author internal AI use policies aligned to ISO 42001 / EU AI Act.",
        "typicalEntryRoute": "Compliance, risk or legal background.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Model Risk Management",
        "shortDescription": "Run model registers, risk tiers and approval workflows.",
        "typicalEntryRoute": "Risk background + AI literacy.",
        "salaryRange": "£65,000 - £120,000"
      },
      {
        "title": "Responsible AI Audits",
        "shortDescription": "Bias, fairness and explainability audits of deployed models.",
        "typicalEntryRoute": "Data science + ethics background.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Vendor and Third-party AI Risk",
        "shortDescription": "Assess third-party AI tools before procurement.",
        "typicalEntryRoute": "Vendor risk + AI background.",
        "salaryRange": "£55,000 - £100,000"
      }
    ],
    "skillsNeeded": [
      "Policy writing",
      "Risk frameworks",
      "AI literacy",
      "Stakeholder management",
      "Audit"
    ]
  },
  {
    "name": "AI Risk Analyst",
    "category": "AI, Data and Automation",
    "shortDescription": "Assess where AI use creates business, legal or reputational risk and propose controls.",
    "whyItFits": "You think in worst-case scenarios and can quantify ambiguous risks clearly.",
    "startingSalary": "£40,000 - £55,000",
    "midSalary": "£60,000 - £85,000",
    "seniorSalary": "£95,000 - £140,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 1,
      "creativity": 0,
      "people": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Model Risk",
        "shortDescription": "Assess accuracy, drift and operational risk of deployed models.",
        "typicalEntryRoute": "Quant or data science background.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "Regulatory Risk",
        "shortDescription": "Map AI use cases to FCA, ICO and EU AI Act obligations.",
        "typicalEntryRoute": "Compliance + AI literacy.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Ethical Risk Reviews",
        "shortDescription": "Pre-deployment reviews for bias, fairness and societal impact.",
        "typicalEntryRoute": "Ethics or social science + technical fluency.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "Operational Resilience",
        "shortDescription": "Test what happens when models or AI vendors fail.",
        "typicalEntryRoute": "Operational risk background.",
        "salaryRange": "£55,000 - £105,000"
      }
    ],
    "skillsNeeded": [
      "Risk scoring",
      "Model evaluation",
      "Regulatory reading",
      "Reporting",
      "Critical thinking"
    ]
  },
  {
    "name": "AI Solutions Architect",
    "category": "AI, Data and Automation",
    "shortDescription": "Design the full technical stack behind enterprise AI products - models, data, infra and integrations.",
    "whyItFits": "You're a senior engineer who likes high-level design and translating business goals into systems.",
    "startingSalary": "£70,000 - £95,000",
    "midSalary": "£100,000 - £150,000",
    "seniorSalary": "£170,000 - £280,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 3,
      "creativity": 1,
      "people": 1,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Enterprise LLM Platforms",
        "shortDescription": "Reference architectures for enterprise-wide LLM use.",
        "typicalEntryRoute": "Senior solutions architect background.",
        "salaryRange": "£90,000 - £170,000"
      },
      {
        "title": "RAG and Knowledge Systems",
        "shortDescription": "Build retrieval architectures over private corporate data.",
        "typicalEntryRoute": "Architect + ML engineering.",
        "salaryRange": "£85,000 - £160,000"
      },
      {
        "title": "Agentic Systems",
        "shortDescription": "Design multi-agent systems with tool use and orchestration.",
        "typicalEntryRoute": "Senior engineer + LLM experience.",
        "salaryRange": "£100,000 - £180,000"
      },
      {
        "title": "Edge and On-prem AI",
        "shortDescription": "Architect for clients who can't use public cloud LLMs.",
        "typicalEntryRoute": "Cloud + on-prem architecture background.",
        "salaryRange": "£90,000 - £170,000"
      }
    ],
    "skillsNeeded": [
      "Cloud architecture",
      "LLM ecosystems",
      "Vector DBs",
      "Security",
      "Stakeholder design reviews"
    ]
  },
  {
    "name": "AI Research Scientist",
    "category": "AI, Data and Automation",
    "shortDescription": "Run original research that pushes the frontier of machine learning models and methods.",
    "whyItFits": "You enjoy deep technical problems, mathematics, and publishing work that others build on.",
    "startingSalary": "£60,000 - £90,000",
    "midSalary": "£110,000 - £170,000",
    "seniorSalary": "£200,000 - £500,000+",
    "timeToEntry": "5 - 8 years (often PhD)",
    "degreeRequired": "PhD usually required",
    "difficulty": "High",
    "competitionLevel": "Extremely High",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "income": 3,
      "creativity": 2,
      "people": -1,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "Foundation Model Research",
        "shortDescription": "Pre-training and fine-tuning research at scale.",
        "typicalEntryRoute": "PhD + publications in ML/NLP.",
        "salaryRange": "£120,000 - £300,000+"
      },
      {
        "title": "Alignment and Safety Research",
        "shortDescription": "Research on RLHF, constitutional AI and steerability.",
        "typicalEntryRoute": "PhD or strong ML research background.",
        "salaryRange": "£100,000 - £250,000+"
      },
      {
        "title": "Multimodal Research",
        "shortDescription": "Vision-language and audio-language model research.",
        "typicalEntryRoute": "PhD in ML / CV / speech.",
        "salaryRange": "£110,000 - £260,000+"
      },
      {
        "title": "Applied Research",
        "shortDescription": "Translate research into product features and benchmarks.",
        "typicalEntryRoute": "MSc/PhD + product mindset.",
        "salaryRange": "£90,000 - £200,000+"
      }
    ],
    "skillsNeeded": [
      "PyTorch / JAX",
      "Statistics",
      "Research design",
      "Paper writing",
      "Mathematical maturity"
    ]
  },
  {
    "name": "AI Safety Researcher",
    "category": "AI, Data and Automation",
    "shortDescription": "Research how to keep increasingly capable AI systems aligned, controllable and safe.",
    "whyItFits": "You care about long-term consequences and enjoy hard, open-ended technical and conceptual problems.",
    "startingSalary": "£60,000 - £90,000",
    "midSalary": "£110,000 - £170,000",
    "seniorSalary": "£200,000 - £500,000+",
    "timeToEntry": "4 - 8 years",
    "degreeRequired": "Strong technical background usually expected",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "income": 3,
      "creativity": 2,
      "people": 0,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "Red Teaming",
        "shortDescription": "Adversarial testing of frontier models for harmful outputs.",
        "typicalEntryRoute": "Security or ML background + creative thinking.",
        "salaryRange": "£80,000 - £180,000"
      },
      {
        "title": "Evaluations",
        "shortDescription": "Build benchmarks and evals for capability and safety.",
        "typicalEntryRoute": "ML engineering + research mindset.",
        "salaryRange": "£80,000 - £170,000"
      },
      {
        "title": "Alignment Engineering",
        "shortDescription": "Implement and test alignment techniques on real models.",
        "typicalEntryRoute": "Strong ML engineering.",
        "salaryRange": "£100,000 - £220,000"
      },
      {
        "title": "Policy and Governance",
        "shortDescription": "Translate research into policy recommendations.",
        "typicalEntryRoute": "Policy + ML literacy.",
        "salaryRange": "£70,000 - £140,000"
      }
    ],
    "skillsNeeded": [
      "ML fundamentals",
      "Interpretability",
      "Red-teaming",
      "Research writing",
      "Critical reasoning"
    ]
  },
  {
    "name": "AI Model Evaluator",
    "category": "AI, Data and Automation",
    "shortDescription": "Design tests and rubrics that measure how well AI models really perform on real tasks.",
    "whyItFits": "You're meticulous, sceptical of marketing claims, and enjoy designing tests that actually matter.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£85,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 1,
      "creativity": 1,
      "people": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Capability Evaluations",
        "shortDescription": "Design tests to measure reasoning, coding and tool-use ability.",
        "typicalEntryRoute": "ML engineer or strong technical background.",
        "salaryRange": "£55,000 - £110,000"
      },
      {
        "title": "Safety Evaluations",
        "shortDescription": "Build harm, bias and misuse evals.",
        "typicalEntryRoute": "ML + safety background.",
        "salaryRange": "£60,000 - £120,000"
      },
      {
        "title": "Domain Evaluations",
        "shortDescription": "Evals for legal, medical, financial domain accuracy.",
        "typicalEntryRoute": "Subject expert + technical literacy.",
        "salaryRange": "£60,000 - £130,000"
      },
      {
        "title": "Eval Tooling",
        "shortDescription": "Build internal eval platforms and dashboards.",
        "typicalEntryRoute": "Software engineering background.",
        "salaryRange": "£70,000 - £140,000"
      }
    ],
    "skillsNeeded": [
      "Eval design",
      "Stats",
      "Python",
      "Annotation guidelines",
      "Bias testing"
    ]
  },
  {
    "name": "AI Data Annotator",
    "category": "AI, Data and Automation",
    "shortDescription": "Label the text, images and audio that AI models learn from - accurately and at speed.",
    "whyItFits": "You're detail-driven, patient, and like clear rules you can follow consistently.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £40,000",
    "seniorSalary": "£45,000 - £60,000",
    "timeToEntry": "0 - 3 months",
    "degreeRequired": "Not required",
    "difficulty": "Low",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 0,
      "creativity": 0,
      "people": -1,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "RLHF Annotation",
        "shortDescription": "High-quality preference data for fine-tuning.",
        "typicalEntryRoute": "Strong writing / reasoning + training.",
        "salaryRange": "£15 - £40 / hr"
      },
      {
        "title": "Domain-expert Annotation",
        "shortDescription": "Legal, medical or coding annotation requiring expertise.",
        "typicalEntryRoute": "Domain qualification + onboarding.",
        "salaryRange": "£25 - £80 / hr"
      },
      {
        "title": "Red-team Annotation",
        "shortDescription": "Generate adversarial prompts and rate model responses.",
        "typicalEntryRoute": "Creative + analytical thinker.",
        "salaryRange": "£18 - £45 / hr"
      },
      {
        "title": "Annotation QA",
        "shortDescription": "Audit annotator work and maintain rubrics.",
        "typicalEntryRoute": "Experienced annotator + quality background.",
        "salaryRange": "£30,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Attention to detail",
      "Following guidelines",
      "Basic data tools",
      "Consistency",
      "English fluency"
    ]
  },
  {
    "name": "AI Integration Engineer",
    "category": "AI, Data and Automation",
    "shortDescription": "Plug LLMs and AI APIs into real production systems so they work reliably at scale.",
    "whyItFits": "You're a working engineer who enjoys glue code, APIs, and squeezing real performance out of new tools.",
    "startingSalary": "£45,000 - £60,000",
    "midSalary": "£70,000 - £100,000",
    "seniorSalary": "£120,000 - £180,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "creativity": 1,
      "people": 0,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "LLM API Integration",
        "shortDescription": "Wire OpenAI / Anthropic / Bedrock APIs into apps.",
        "typicalEntryRoute": "Backend developer background.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Workflow Tool Integration",
        "shortDescription": "Connect LLMs to Slack, M365, Salesforce, HubSpot.",
        "typicalEntryRoute": "Integration engineering background.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "Vector DB and RAG",
        "shortDescription": "Build retrieval layers with Pinecone, Weaviate or pgvector.",
        "typicalEntryRoute": "Backend + ML engineering.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Enterprise SSO and Security",
        "shortDescription": "Integrate AI tools with SSO, DLP and audit logging.",
        "typicalEntryRoute": "Enterprise integration background.",
        "salaryRange": "£60,000 - £115,000"
      }
    ],
    "skillsNeeded": [
      "Python",
      "APIs",
      "Cloud (AWS/GCP)",
      "Vector DBs",
      "Observability"
    ]
  },
  {
    "name": "AI Tutor",
    "category": "AI, Data and Automation",
    "shortDescription": "Teach individuals and small groups how to use AI tools effectively - online or in person.",
    "whyItFits": "You like teaching, enjoy AI tools, and want a flexible income without managing a big team.",
    "startingSalary": "£25,000 - £35,000 (part-time start)",
    "midSalary": "£40,000 - £60,000",
    "seniorSalary": "£70,000 - £120,000",
    "timeToEntry": "0 - 6 months",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "people": 3,
      "freedom": 3,
      "creativity": 2,
      "income": 0,
      "structure": 0
    },
    "specialisations": [
      {
        "title": "Subject AI Tutoring",
        "shortDescription": "1:1 tutoring augmented by AI tools for GCSE/A-level.",
        "typicalEntryRoute": "Teaching background.",
        "salaryRange": "£25 - £60 / hr"
      },
      {
        "title": "AI Tutor Content Author",
        "shortDescription": "Author exemplar dialogues and curricula for AI tutor apps.",
        "typicalEntryRoute": "Teaching + content design.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "AI Tutor Evaluator",
        "shortDescription": "Evaluate AI tutor quality and pedagogy.",
        "typicalEntryRoute": "Education + research background.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Higher-Ed AI Coaching",
        "shortDescription": "Coach university students using AI for study.",
        "typicalEntryRoute": "HE teaching background.",
        "salaryRange": "£35,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "Teaching",
      "Curriculum design",
      "Patience",
      "Marketing yourself",
      "Live demos"
    ],
    "selfEmployable": true
  },
  {
    "name": "Computer Vision Engineer",
    "category": "AI, Data and Automation",
    "shortDescription": "Build systems that understand images and video - from medical scans to self-driving cars.",
    "whyItFits": "You love working with images, geometry and ML, and like turning research into shipping product.",
    "startingSalary": "£45,000 - £65,000",
    "midSalary": "£80,000 - £120,000",
    "seniorSalary": "£140,000 - £220,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "income": 3,
      "creativity": 1,
      "people": -1,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Object Detection",
        "shortDescription": "Build detection systems for retail, automotive or security.",
        "typicalEntryRoute": "ML engineering + PyTorch.",
        "salaryRange": "£65,000 - £120,000"
      },
      {
        "title": "Medical Imaging",
        "shortDescription": "Build CV models for diagnostic imaging.",
        "typicalEntryRoute": "MSc/PhD + healthcare regulations.",
        "salaryRange": "£70,000 - £140,000"
      },
      {
        "title": "Edge / Embedded Vision",
        "shortDescription": "Deploy CV on cameras, drones and devices.",
        "typicalEntryRoute": "Embedded + ML background.",
        "salaryRange": "£70,000 - £130,000"
      },
      {
        "title": "Generative Vision",
        "shortDescription": "Diffusion model fine-tuning for product / creative use.",
        "typicalEntryRoute": "Strong CV + generative ML.",
        "salaryRange": "£75,000 - £150,000"
      }
    ],
    "skillsNeeded": [
      "Python",
      "PyTorch",
      "OpenCV",
      "Maths",
      "Linux"
    ]
  },
  {
    "name": "NLP Engineer",
    "category": "AI, Data and Automation",
    "shortDescription": "Build systems that read, summarise, classify or generate human language at scale.",
    "whyItFits": "You love language and statistics in equal measure and enjoy building things people actually talk to.",
    "startingSalary": "£45,000 - £65,000",
    "midSalary": "£80,000 - £120,000",
    "seniorSalary": "£140,000 - £220,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "income": 3,
      "creativity": 1,
      "people": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "LLM Fine-tuning",
        "shortDescription": "Domain fine-tuning and instruction-tuning of open models.",
        "typicalEntryRoute": "ML engineering + LLM experience.",
        "salaryRange": "£75,000 - £150,000"
      },
      {
        "title": "Information Extraction",
        "shortDescription": "Extract structured data from documents at scale.",
        "typicalEntryRoute": "NLP + Python.",
        "salaryRange": "£60,000 - £115,000"
      },
      {
        "title": "Conversational AI",
        "shortDescription": "Build production chat experiences with tool use.",
        "typicalEntryRoute": "NLP + product engineering.",
        "salaryRange": "£65,000 - £125,000"
      },
      {
        "title": "Search and Retrieval",
        "shortDescription": "Hybrid lexical + semantic search for enterprise.",
        "typicalEntryRoute": "Search engineering background.",
        "salaryRange": "£65,000 - £125,000"
      }
    ],
    "skillsNeeded": [
      "Transformers",
      "Python",
      "Linguistics basics",
      "Evaluation",
      "Fine-tuning"
    ]
  },
  {
    "name": "Robotics Engineer",
    "category": "AI, Data and Automation",
    "shortDescription": "Design and build robots and the software that lets them sense, plan and move in the real world.",
    "whyItFits": "You like hardware as much as software and enjoy seeing your code move physical things.",
    "startingSalary": "£35,000 - £50,000",
    "midSalary": "£55,000 - £85,000",
    "seniorSalary": "£95,000 - £150,000+",
    "timeToEntry": "3 - 6 years",
    "degreeRequired": "Usually required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "creativity": 2,
      "people": -1,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Industrial Robotics",
        "shortDescription": "Program and integrate factory and warehouse robots.",
        "typicalEntryRoute": "Robotics or controls engineering.",
        "salaryRange": "£40,000 - £85,000"
      },
      {
        "title": "Mobile Robotics",
        "shortDescription": "Build AMRs and self-driving platforms.",
        "typicalEntryRoute": "Robotics + computer vision.",
        "salaryRange": "£55,000 - £110,000"
      },
      {
        "title": "Surgical / Medical Robotics",
        "shortDescription": "Develop robots for surgical and clinical settings.",
        "typicalEntryRoute": "Robotics + medical device experience.",
        "salaryRange": "£60,000 - £130,000"
      },
      {
        "title": "Robot Software Engineering",
        "shortDescription": "ROS / ROS2 stacks, simulation and integration.",
        "typicalEntryRoute": "Software engineering + robotics.",
        "salaryRange": "£55,000 - £115,000"
      }
    ],
    "skillsNeeded": [
      "ROS",
      "C++ / Python",
      "Control theory",
      "Mechanical sense",
      "Debugging hardware"
    ]
  },
  {
    "name": "MLOps Engineer",
    "category": "AI, Data and Automation",
    "shortDescription": "Run the pipelines, monitoring and infrastructure that keep ML models healthy in production.",
    "whyItFits": "You're a strong infra engineer who likes the ML domain and the operational side of model life.",
    "startingSalary": "£50,000 - £70,000",
    "midSalary": "£85,000 - £120,000",
    "seniorSalary": "£140,000 - £200,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "people": -1,
      "creativity": 1,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Training Infrastructure",
        "shortDescription": "Build GPU-cluster training pipelines.",
        "typicalEntryRoute": "DevOps + ML engineering.",
        "salaryRange": "£80,000 - £150,000"
      },
      {
        "title": "Model Serving",
        "shortDescription": "Low-latency serving with vLLM, Triton or SageMaker.",
        "typicalEntryRoute": "Backend + ML engineering.",
        "salaryRange": "£70,000 - £135,000"
      },
      {
        "title": "Feature Stores and Pipelines",
        "shortDescription": "Build feature platforms and orchestration.",
        "typicalEntryRoute": "Data engineering background.",
        "salaryRange": "£65,000 - £125,000"
      },
      {
        "title": "Model Monitoring",
        "shortDescription": "Drift, quality and cost monitoring for production models.",
        "typicalEntryRoute": "MLOps tooling background.",
        "salaryRange": "£70,000 - £130,000"
      }
    ],
    "skillsNeeded": [
      "Docker/K8s",
      "Cloud (AWS/GCP)",
      "CI/CD",
      "Monitoring",
      "Python"
    ]
  },
  {
    "name": "Python Developer",
    "category": "AI, Data and Automation",
    "shortDescription": "Build backend services, data tools and automation in Python across web, data and AI teams.",
    "whyItFits": "You like a versatile language that opens doors in data, ML and web work.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £140,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "income": 2,
      "creativity": 1,
      "people": -1,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "Backend APIs",
        "shortDescription": "Build FastAPI / Django services for web and mobile.",
        "typicalEntryRoute": "CS degree or bootcamp + portfolio.",
        "salaryRange": "£40,000 - £85,000"
      },
      {
        "title": "Data Engineering",
        "shortDescription": "ETL pipelines with Airflow, dbt and Snowflake.",
        "typicalEntryRoute": "Python + SQL.",
        "salaryRange": "£50,000 - £100,000"
      },
      {
        "title": "ML / AI Engineering",
        "shortDescription": "Build and deploy ML and LLM-backed services.",
        "typicalEntryRoute": "Python + ML experience.",
        "salaryRange": "£60,000 - £120,000"
      },
      {
        "title": "Automation and Scripting",
        "shortDescription": "Internal tools, scrapers and ops automation.",
        "typicalEntryRoute": "Python + ops background.",
        "salaryRange": "£35,000 - £70,000"
      }
    ],
    "skillsNeeded": [
      "Python",
      "APIs",
      "SQL",
      "Testing",
      "Git"
    ],
    "selfEmployable": true
  },
  {
    "name": "RPA Developer",
    "category": "AI, Data and Automation",
    "shortDescription": "Build software robots (UiPath, Blue Prism) that automate clicking, copying and form-filling work.",
    "whyItFits": "You enjoy a structured, deliverable-driven role where automation savings are obvious.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£50,000 - £70,000",
    "seniorSalary": "£80,000 - £110,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "creativity": 0,
      "people": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "UiPath",
        "shortDescription": "Automate desktop and enterprise workflows in UiPath.",
        "typicalEntryRoute": "UiPath cert + IT background.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Blue Prism",
        "shortDescription": "Build automations in finance and insurance environments.",
        "typicalEntryRoute": "Blue Prism cert.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Power Automate",
        "shortDescription": "Build Microsoft 365 and Dynamics automations.",
        "typicalEntryRoute": "Power Platform background.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Intelligent Automation",
        "shortDescription": "Combine RPA with LLMs and computer vision.",
        "typicalEntryRoute": "RPA + AI tooling.",
        "salaryRange": "£55,000 - £100,000"
      }
    ],
    "skillsNeeded": [
      "UiPath / Blue Prism",
      "Process mapping",
      "VBA / Python",
      "Testing",
      "Documentation"
    ]
  },
  {
    "name": "No-Code Automation Specialist",
    "category": "AI, Data and Automation",
    "shortDescription": "Stitch together Zapier, Make, Airtable and AI tools into systems that replace manual work.",
    "whyItFits": "You're product-minded, non-technical-friendly, and like shipping fast solutions without a dev team.",
    "startingSalary": "£30,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£85,000 - £140,000",
    "timeToEntry": "0 - 12 months",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "freedom": 3,
      "creativity": 2,
      "income": 2,
      "structure": 1,
      "people": 1
    },
    "specialisations": [
      {
        "title": "Zapier",
        "shortDescription": "Multi-step automations across SaaS tools.",
        "typicalEntryRoute": "Self-taught + portfolio.",
        "salaryRange": "£300 - £800 / day"
      },
      {
        "title": "Make.com",
        "shortDescription": "Visual scenarios with branching, loops and APIs.",
        "typicalEntryRoute": "Self-taught + portfolio.",
        "salaryRange": "£350 - £900 / day"
      },
      {
        "title": "n8n / Self-hosted",
        "shortDescription": "Open-source automation for data-sensitive teams.",
        "typicalEntryRoute": "DevOps-lite + automation skills.",
        "salaryRange": "£400 - £1,000 / day"
      },
      {
        "title": "Airtable / Notion Systems",
        "shortDescription": "Bespoke internal tools and ops systems.",
        "typicalEntryRoute": "No-code generalist.",
        "salaryRange": "£350 - £900 / day"
      }
    ],
    "skillsNeeded": [
      "Zapier/Make/n8n",
      "Airtable",
      "APIs basics",
      "Process design",
      "Client comms"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£200 - £800 (subscriptions, basic site, accounting software)",
      "timeToFirstClient": "2 - 6 weeks via outbound + portfolio builds",
      "monthOneIncome": "£0 - £2,500",
      "yearOneIncome": "£30,000 - £90,000 full-time",
      "scaledIncome": "£100,000 - £200,000 with productised builds + monthly retainers",
      "commonPitfalls": [
        "Charging hourly instead of by outcome",
        "No documentation - clients can't maintain the build",
        "Building one-offs instead of templates you reuse",
        "Scope creep - free 'small tweaks' that take a day"
      ],
      "firstFiveClients": [
        "Past employer - automate one workflow free, charge for v2",
        "Post a free Loom audit on LinkedIn weekly",
        "Build templates and list on Make/Zapier directories",
        "Niche down - 'automation for accountants/agencies/coaches'",
        "Partner with an agency that resells your builds"
      ],
      "toolsAndCosts": [
        "Zapier £20-£60/mo",
        "Make £10-£30/mo",
        "Airtable £20/mo",
        "Loom £12/mo",
        "Notion £8/mo"
      ],
      "legalBasics": [
        "Sole trader UTR or Ltd",
        "Professional indemnity £15-£30/mo",
        "Simple contract + scope template",
        "Quarterly tax savings account"
      ]
    }
  },
  {
    "name": "Data Privacy Analyst",
    "category": "AI, Data and Automation",
    "shortDescription": "Make sure customer data is collected, stored and used in line with GDPR and modern privacy law.",
    "whyItFits": "You're principled, detail-oriented, and like work where being right matters more than being fast.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£85,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 1,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "GDPR Compliance",
        "shortDescription": "DPIA, ROPA and DSAR work for UK / EU clients.",
        "typicalEntryRoute": "Privacy or legal background.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Privacy Engineering",
        "shortDescription": "Implement privacy-by-design in product and data systems.",
        "typicalEntryRoute": "Engineering + privacy training.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "AI Privacy",
        "shortDescription": "Privacy reviews for LLM and AI use cases.",
        "typicalEntryRoute": "Privacy + AI literacy.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "International Transfers",
        "shortDescription": "Manage SCCs, IDTAs and transfer impact assessments.",
        "typicalEntryRoute": "Privacy + legal background.",
        "salaryRange": "£50,000 - £90,000"
      }
    ],
    "skillsNeeded": [
      "GDPR",
      "Data mapping",
      "Risk assessments",
      "Policy writing",
      "Stakeholder management"
    ]
  },
  {
    "name": "Data Quality Analyst",
    "category": "AI, Data and Automation",
    "shortDescription": "Find, fix and prevent dirty data so analytics and AI built on top of it can be trusted.",
    "whyItFits": "You hate sloppy data and enjoy the detective work of finding where it broke.",
    "startingSalary": "£28,000 - £38,000",
    "midSalary": "£42,000 - £60,000",
    "seniorSalary": "£70,000 - £95,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 1,
      "creativity": 0,
      "people": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Data Profiling",
        "shortDescription": "Profile and document data quality across systems.",
        "typicalEntryRoute": "SQL + analytics background.",
        "salaryRange": "£35,000 - £65,000"
      },
      {
        "title": "Data Cleansing",
        "shortDescription": "Build cleansing pipelines and master data fixes.",
        "typicalEntryRoute": "Data engineering background.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Data Quality Frameworks",
        "shortDescription": "Implement DAMA / DQ scorecards and metrics.",
        "typicalEntryRoute": "Data governance background.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "MDM Quality",
        "shortDescription": "Quality work inside master data management programmes.",
        "typicalEntryRoute": "MDM tooling background.",
        "salaryRange": "£50,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "SQL",
      "Profiling tools",
      "Excel",
      "Documentation",
      "Communication"
    ]
  },
  {
    "name": "Analytics Engineer",
    "category": "AI, Data and Automation",
    "shortDescription": "Sit between data engineers and analysts - model raw data into clean, trusted tables teams can use.",
    "whyItFits": "You're half engineer half analyst and like clean SQL and well-documented data models.",
    "startingSalary": "£40,000 - £55,000",
    "midSalary": "£65,000 - £90,000",
    "seniorSalary": "£100,000 - £140,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "creativity": 1,
      "people": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "dbt Modelling",
        "shortDescription": "Build modular SQL models and tests in dbt.",
        "typicalEntryRoute": "SQL + analytics background.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "Data Warehousing",
        "shortDescription": "Design star / data vault schemas in Snowflake / BigQuery.",
        "typicalEntryRoute": "Data engineering + analytics.",
        "salaryRange": "£55,000 - £105,000"
      },
      {
        "title": "Metrics Layer",
        "shortDescription": "Define and govern company-wide metrics.",
        "typicalEntryRoute": "Analytics engineering background.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Reverse ETL",
        "shortDescription": "Sync warehouse data back to SaaS tools.",
        "typicalEntryRoute": "Data + ops background.",
        "salaryRange": "£50,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "dbt",
      "SQL",
      "Git",
      "Data modelling",
      "Documentation"
    ]
  },
  {
    "name": "Database Administrator",
    "category": "AI, Data and Automation",
    "shortDescription": "Keep mission-critical databases fast, backed up and secure - the unglamorous role that everything depends on.",
    "whyItFits": "You're calm, methodical, and take quiet pride in things never breaking on your watch.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£85,000 - £120,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 1,
      "people": -1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "SQL Server DBA",
        "shortDescription": "Manage Microsoft SQL Server estates.",
        "typicalEntryRoute": "IT background + Microsoft certs.",
        "salaryRange": "£40,000 - £80,000"
      },
      {
        "title": "Postgres DBA",
        "shortDescription": "Manage Postgres clusters in cloud and on-prem.",
        "typicalEntryRoute": "Linux + Postgres experience.",
        "salaryRange": "£45,000 - £90,000"
      },
      {
        "title": "Cloud Database Engineer",
        "shortDescription": "Manage RDS, Aurora, Cloud SQL and BigQuery.",
        "typicalEntryRoute": "Cloud engineering background.",
        "salaryRange": "£55,000 - £105,000"
      },
      {
        "title": "Performance Tuning",
        "shortDescription": "Specialise in query and index optimisation.",
        "typicalEntryRoute": "Senior DBA background.",
        "salaryRange": "£60,000 - £110,000"
      }
    ],
    "skillsNeeded": [
      "SQL Server / Postgres / Oracle",
      "Backup & recovery",
      "Performance tuning",
      "Scripting",
      "Security"
    ]
  },
  {
    "name": "Data Visualisation Specialist",
    "category": "AI, Data and Automation",
    "shortDescription": "Turn complex data into clear, beautiful dashboards and charts that decision-makers actually use.",
    "whyItFits": "You combine design instincts with data fluency and care about how numbers are told as a story.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£80,000 - £120,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "creativity": 3,
      "structure": 2,
      "income": 1,
      "people": 1,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "Tableau",
        "shortDescription": "Enterprise Tableau dashboards and governance.",
        "typicalEntryRoute": "BI background + Tableau cert.",
        "salaryRange": "£40,000 - £80,000"
      },
      {
        "title": "Power BI",
        "shortDescription": "Power BI semantic models and dashboards.",
        "typicalEntryRoute": "BI background + Microsoft stack.",
        "salaryRange": "£40,000 - £80,000"
      },
      {
        "title": "Looker / LookML",
        "shortDescription": "Looker semantic modelling and embedded analytics.",
        "typicalEntryRoute": "Analytics engineering background.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "D3 / Custom Viz",
        "shortDescription": "Bespoke visualisations for journalism and product.",
        "typicalEntryRoute": "Front-end + data background.",
        "salaryRange": "£45,000 - £90,000"
      }
    ],
    "skillsNeeded": [
      "Tableau / Power BI",
      "Figma",
      "Storytelling with data",
      "SQL",
      "Colour theory"
    ],
    "selfEmployable": true
  },
  {
    "name": "CRM Manager",
    "category": "AI, Data and Automation",
    "shortDescription": "Own the customer database and the email/lifecycle marketing that turns leads into long-term customers.",
    "whyItFits": "You like blending marketing, data and process and enjoy seeing revenue tied directly to your work.",
    "startingSalary": "£35,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£85,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "income": 2,
      "creativity": 1,
      "people": 1,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "HubSpot",
        "shortDescription": "Run lifecycle and revenue ops in HubSpot.",
        "typicalEntryRoute": "Marketing ops background.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Salesforce",
        "shortDescription": "Salesforce admin and lifecycle automation.",
        "typicalEntryRoute": "Salesforce admin cert.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "Lifecycle Email",
        "shortDescription": "Onboarding, retention and win-back programmes.",
        "typicalEntryRoute": "Lifecycle marketing background.",
        "salaryRange": "£40,000 - £80,000"
      },
      {
        "title": "Loyalty Programmes",
        "shortDescription": "Design and run loyalty schemes.",
        "typicalEntryRoute": "CRM + retail background.",
        "salaryRange": "£50,000 - £90,000"
      }
    ],
    "skillsNeeded": [
      "HubSpot/Salesforce",
      "Email",
      "Segmentation",
      "Analytics",
      "Copywriting"
    ]
  },
  {
    "name": "Revenue Analyst",
    "category": "AI, Data and Automation",
    "shortDescription": "Forecast, track and explain a company's revenue - the analyst the CFO and sales leaders both lean on.",
    "whyItFits": "You're commercial, comfortable with spreadsheets, and like translating numbers into business decisions.",
    "startingSalary": "£35,000 - £45,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "people": 0,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "SaaS Metrics",
        "shortDescription": "MRR, NRR, churn and cohort analytics.",
        "typicalEntryRoute": "Finance or analytics background.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "Pricing Analytics",
        "shortDescription": "Pricing experiments and willingness-to-pay studies.",
        "typicalEntryRoute": "Analytics + commercial background.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "Revenue Forecasting",
        "shortDescription": "Build bottoms-up revenue forecasts and pipeline models.",
        "typicalEntryRoute": "FP&A or RevOps background.",
        "salaryRange": "£50,000 - £90,000"
      },
      {
        "title": "Sales Compensation Analytics",
        "shortDescription": "Model and govern sales comp plans.",
        "typicalEntryRoute": "RevOps background.",
        "salaryRange": "£45,000 - £85,000"
      }
    ],
    "skillsNeeded": [
      "Excel modelling",
      "SQL",
      "Forecasting",
      "Reporting",
      "Communication"
    ]
  },
  {
    "name": "Sales Operations Analyst",
    "category": "AI, Data and Automation",
    "shortDescription": "Make sales teams more effective with better processes, tools, dashboards and territory design.",
    "whyItFits": "You like the structure side of sales without needing to be on the phone all day.",
    "startingSalary": "£32,000 - £42,000",
    "midSalary": "£50,000 - £70,000",
    "seniorSalary": "£80,000 - £120,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Pipeline Hygiene",
        "shortDescription": "Own data quality and forecasting accuracy in CRM.",
        "typicalEntryRoute": "RevOps background.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Territory and Quota",
        "shortDescription": "Design territories and quotas with the GTM team.",
        "typicalEntryRoute": "RevOps background.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "Sales Tech Stack",
        "shortDescription": "Manage Salesforce, Gong, Outreach and integrations.",
        "typicalEntryRoute": "RevOps + admin certs.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "Sales Analytics",
        "shortDescription": "Win-rate, conversion and rep performance analytics.",
        "typicalEntryRoute": "Analytics + sales background.",
        "salaryRange": "£45,000 - £85,000"
      }
    ],
    "skillsNeeded": [
      "Salesforce / HubSpot",
      "Excel",
      "SQL",
      "Process design",
      "Reporting"
    ]
  },
  {
    "name": "Marketing Automation Manager",
    "category": "AI, Data and Automation",
    "shortDescription": "Design the lifecycle journeys, triggers and nurture sequences that scale marketing without manual work.",
    "whyItFits": "You like the intersection of copy, data and tooling and enjoy building once and shipping forever.",
    "startingSalary": "£35,000 - £48,000",
    "midSalary": "£52,000 - £78,000",
    "seniorSalary": "£85,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "creativity": 2,
      "income": 2,
      "people": 1,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "HubSpot Automation",
        "shortDescription": "Workflows, lead scoring and lifecycle in HubSpot.",
        "typicalEntryRoute": "Marketing ops background.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Marketo",
        "shortDescription": "Enterprise nurture, scoring and ABM in Marketo.",
        "typicalEntryRoute": "Marketo cert.",
        "salaryRange": "£50,000 - £90,000"
      },
      {
        "title": "Customer.io / Iterable",
        "shortDescription": "Lifecycle automation for consumer apps.",
        "typicalEntryRoute": "Lifecycle marketing background.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "ABM Automation",
        "shortDescription": "Account-based campaigns with intent and personalisation.",
        "typicalEntryRoute": "B2B marketing background.",
        "salaryRange": "£55,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "HubSpot/Marketo/Klaviyo",
      "Email",
      "Segmentation",
      "Analytics",
      "Copywriting"
    ]
  },
  {
    "name": "SOC Analyst",
    "category": "AI, Data and Automation",
    "shortDescription": "Monitor live security alerts and respond to attacks in real time from a security operations centre.",
    "whyItFits": "You stay calm under pressure and enjoy clear escalation paths and structured incident playbooks.",
    "startingSalary": "£28,000 - £38,000",
    "midSalary": "£42,000 - £62,000",
    "seniorSalary": "£75,000 - £110,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "SOC, often shift work",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 1,
      "people": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Tier 1 Triage",
        "shortDescription": "Front-line alert triage in a 24/7 SOC.",
        "typicalEntryRoute": "Cyber cert (Sec+, BTL1) + IT background.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Tier 2 Investigation",
        "shortDescription": "Deep investigation of escalated incidents.",
        "typicalEntryRoute": "SOC experience + GCFA / CySA+.",
        "salaryRange": "£42,000 - £65,000"
      },
      {
        "title": "Threat Detection Engineering",
        "shortDescription": "Write and tune detection rules and content.",
        "typicalEntryRoute": "SOC + scripting background.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "Cloud SOC",
        "shortDescription": "Detection and response across AWS / Azure / GCP.",
        "typicalEntryRoute": "Cloud + cyber background.",
        "salaryRange": "£60,000 - £105,000"
      }
    ],
    "skillsNeeded": [
      "SIEM tools",
      "Networking",
      "Triage",
      "Documentation",
      "Incident response"
    ]
  },
  {
    "name": "Cloud Security Engineer",
    "category": "AI, Data and Automation",
    "shortDescription": "Lock down cloud environments (AWS, Azure, GCP) so they can't be exploited or misconfigured.",
    "whyItFits": "You're an experienced engineer who wants to specialise in the highest-leverage corner of security.",
    "startingSalary": "£50,000 - £70,000",
    "midSalary": "£85,000 - £120,000",
    "seniorSalary": "£140,000 - £200,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 3,
      "people": -1,
      "creativity": 1,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "AWS Security",
        "shortDescription": "Harden AWS estates, IAM and guardrails.",
        "typicalEntryRoute": "AWS Security Specialty cert.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Azure Security",
        "shortDescription": "Defender for Cloud, Entra and Azure Policy.",
        "typicalEntryRoute": "Azure security certs.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Kubernetes Security",
        "shortDescription": "Cluster, workload and supply-chain security.",
        "typicalEntryRoute": "DevOps + security background.",
        "salaryRange": "£70,000 - £130,000"
      },
      {
        "title": "Cloud SIEM and SOAR",
        "shortDescription": "Build and tune cloud-native SOC tooling.",
        "typicalEntryRoute": "Detection engineering background.",
        "salaryRange": "£65,000 - £120,000"
      }
    ],
    "skillsNeeded": [
      "AWS/Azure/GCP",
      "IAM",
      "Networking",
      "Terraform",
      "Threat modelling"
    ]
  },
  {
    "name": "Application Security Engineer",
    "category": "AI, Data and Automation",
    "shortDescription": "Embed security into the software development lifecycle - design reviews, code audits, vuln triage.",
    "whyItFits": "You're a developer who likes thinking like an attacker and helping other devs ship safer code.",
    "startingSalary": "£50,000 - £70,000",
    "midSalary": "£80,000 - £120,000",
    "seniorSalary": "£140,000 - £200,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 3,
      "creativity": 1,
      "people": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "SAST / DAST",
        "shortDescription": "Run code and runtime scanning at scale.",
        "typicalEntryRoute": "Developer + security background.",
        "salaryRange": "£60,000 - £115,000"
      },
      {
        "title": "Threat Modelling",
        "shortDescription": "Embed threat modelling into product teams.",
        "typicalEntryRoute": "Senior dev + security background.",
        "salaryRange": "£70,000 - £125,000"
      },
      {
        "title": "Secure SDLC",
        "shortDescription": "Run security champions and shift-left programmes.",
        "typicalEntryRoute": "Senior AppSec background.",
        "salaryRange": "£70,000 - £130,000"
      },
      {
        "title": "API Security",
        "shortDescription": "Specialise in REST / GraphQL / mTLS security.",
        "typicalEntryRoute": "AppSec + backend background.",
        "salaryRange": "£70,000 - £130,000"
      }
    ],
    "skillsNeeded": [
      "Secure coding",
      "OWASP",
      "Threat modelling",
      "SAST/DAST",
      "Developer empathy"
    ]
  },
  {
    "name": "Cyber Incident Responder",
    "category": "AI, Data and Automation",
    "shortDescription": "Lead the response when a serious cyber attack hits - contain, investigate, recover and report.",
    "whyItFits": "You stay calm in crises, like investigative work and don't mind being on call when it counts.",
    "startingSalary": "£45,000 - £60,000",
    "midSalary": "£70,000 - £100,000",
    "seniorSalary": "£120,000 - £180,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "people": 1,
      "creativity": 1,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Digital Forensics",
        "shortDescription": "Acquire and analyse evidence from endpoints and cloud.",
        "typicalEntryRoute": "GCFA / GCFE certs.",
        "salaryRange": "£55,000 - £110,000"
      },
      {
        "title": "Ransomware Response",
        "shortDescription": "Lead containment and recovery for ransomware events.",
        "typicalEntryRoute": "Senior IR background.",
        "salaryRange": "£70,000 - £140,000"
      },
      {
        "title": "Cloud IR",
        "shortDescription": "Investigate incidents across AWS / Azure / GCP estates.",
        "typicalEntryRoute": "Cloud + IR background.",
        "salaryRange": "£65,000 - £125,000"
      },
      {
        "title": "OT / ICS IR",
        "shortDescription": "Incident response in industrial control environments.",
        "typicalEntryRoute": "OT security background.",
        "salaryRange": "£70,000 - £140,000"
      }
    ],
    "skillsNeeded": [
      "Forensics",
      "Malware analysis",
      "Logging tools",
      "Crisis comms",
      "Networking"
    ]
  },
  {
    "name": "Threat Hunter",
    "category": "AI, Data and Automation",
    "shortDescription": "Proactively search for attackers already inside the network - assume breach and prove it.",
    "whyItFits": "You're a curious investigator who enjoys building hypotheses and finding what others missed.",
    "startingSalary": "£45,000 - £60,000",
    "midSalary": "£70,000 - £100,000",
    "seniorSalary": "£110,000 - £170,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "income": 2,
      "creativity": 2,
      "people": -1,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "Endpoint Hunting",
        "shortDescription": "Hunt across EDR telemetry for unknown threats.",
        "typicalEntryRoute": "Detection engineering + scripting.",
        "salaryRange": "£60,000 - £115,000"
      },
      {
        "title": "Network Hunting",
        "shortDescription": "Hunt in NetFlow, PCAP and Zeek logs.",
        "typicalEntryRoute": "Network + cyber background.",
        "salaryRange": "£60,000 - £115,000"
      },
      {
        "title": "Cloud Hunting",
        "shortDescription": "Hunt in CloudTrail, Defender and GCP audit logs.",
        "typicalEntryRoute": "Cloud + detection background.",
        "salaryRange": "£65,000 - £120,000"
      },
      {
        "title": "Threat Intelligence-led Hunting",
        "shortDescription": "Hunt based on adversary TTP intelligence.",
        "typicalEntryRoute": "CTI + detection background.",
        "salaryRange": "£65,000 - £125,000"
      }
    ],
    "skillsNeeded": [
      "TTPs",
      "EDR tools",
      "Scripting",
      "Threat intel",
      "Curiosity"
    ]
  },
  {
    "name": "GRC Analyst",
    "category": "AI, Data and Automation",
    "shortDescription": "Run governance, risk and compliance programmes - frameworks like ISO 27001, SOC 2 and NIST.",
    "whyItFits": "You like clear frameworks, evidence-gathering, and bridging tech and audit worlds.",
    "startingSalary": "£35,000 - £48,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 3,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "ISO 27001",
        "shortDescription": "Run and audit ISO 27001 ISMS programmes.",
        "typicalEntryRoute": "Internal audit + ISO Lead Auditor cert.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "SOC 2",
        "shortDescription": "Manage SOC 2 readiness and audits for SaaS firms.",
        "typicalEntryRoute": "GRC background + auditor relationships.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "NIST CSF",
        "shortDescription": "Map and manage controls against NIST CSF.",
        "typicalEntryRoute": "Cyber + GRC background.",
        "salaryRange": "£50,000 - £90,000"
      },
      {
        "title": "Third-party Risk",
        "shortDescription": "Run vendor risk assessments at scale.",
        "typicalEntryRoute": "Risk background.",
        "salaryRange": "£40,000 - £80,000"
      }
    ],
    "skillsNeeded": [
      "ISO 27001 / SOC 2",
      "Risk assessment",
      "Policy writing",
      "Audit prep",
      "GRC tools"
    ]
  },
  {
    "name": "Cyber Risk Consultant",
    "category": "AI, Data and Automation",
    "shortDescription": "Advise boards and execs on their cyber risk posture, controls maturity and where to invest next.",
    "whyItFits": "You're senior enough to talk to leadership and translate technical risk into business language.",
    "startingSalary": "£50,000 - £70,000",
    "midSalary": "£85,000 - £130,000",
    "seniorSalary": "£150,000 - £250,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Helpful, not always required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote, desk-based",
    "stability": "Strong growth as AI adoption scales",
    "weights": {
      "structure": 2,
      "income": 3,
      "people": 2,
      "creativity": 1,
      "freedom": 1
    },
    "specialisations": [
      {
        "title": "Cyber Risk Quantification",
        "shortDescription": "Model cyber risk in £ using FAIR.",
        "typicalEntryRoute": "Risk + cyber background.",
        "salaryRange": "£60,000 - £120,000"
      },
      {
        "title": "Board Reporting",
        "shortDescription": "Translate cyber posture for execs and boards.",
        "typicalEntryRoute": "Senior cyber background.",
        "salaryRange": "£70,000 - £140,000"
      },
      {
        "title": "M&A Cyber Due Diligence",
        "shortDescription": "Cyber risk reviews during acquisitions.",
        "typicalEntryRoute": "Consulting + cyber background.",
        "salaryRange": "£70,000 - £140,000"
      },
      {
        "title": "Insurance and Cyber",
        "shortDescription": "Work with brokers and insurers on cover and claims.",
        "typicalEntryRoute": "Cyber + insurance background.",
        "salaryRange": "£65,000 - £130,000"
      }
    ],
    "skillsNeeded": [
      "Risk frameworks",
      "Boardroom comms",
      "Maturity models",
      "Audit",
      "Sales-adjacent skills"
    ],
    "selfEmployable": true
  },
  {
    "name": "Lift Engineer",
    "category": "Skilled Trades",
    "shortDescription": "Install, service and repair lifts and escalators in commercial buildings.",
    "whyItFits": "You like skilled mechanical/electrical work with clear safety stakes and good pay for a trade.",
    "startingSalary": "£24,000 - £32,000 (apprentice)",
    "midSalary": "£38,000 - £55,000",
    "seniorSalary": "£60,000 - £85,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Passenger Lifts",
        "shortDescription": "Install and service passenger lifts in residential and commercial buildings.",
        "typicalEntryRoute": "Apprenticeship + NVQ Level 3.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Escalators",
        "shortDescription": "Service moving walkways and escalators in transport and retail.",
        "typicalEntryRoute": "Lift engineer + escalator training.",
        "salaryRange": "£38,000 - £58,000"
      },
      {
        "title": "Service and Maintenance",
        "shortDescription": "Planned maintenance contracts on a lift portfolio.",
        "typicalEntryRoute": "NVQ Level 3 + experience.",
        "salaryRange": "£35,000 - £52,000"
      },
      {
        "title": "Lift Modernisation",
        "shortDescription": "Refurbish and upgrade ageing lifts to current standards.",
        "typicalEntryRoute": "Senior lift engineer.",
        "salaryRange": "£42,000 - £65,000"
      },
      {
        "title": "Emergency Breakdown",
        "shortDescription": "On-call response to lift entrapments and faults.",
        "typicalEntryRoute": "NVQ Level 3 + on-call rota.",
        "salaryRange": "£40,000 - £62,000 + on-call uplift"
      }
    ],
    "skillsNeeded": [
      "Electrical fundamentals",
      "Mechanical aptitude",
      "Working at height",
      "Fault finding",
      "Customer comms"
    ]
  },
  {
    "name": "Rail Engineering Technician",
    "category": "Skilled Trades",
    "shortDescription": "Maintain track, signalling, overhead lines and rolling stock that keep UK railways running.",
    "whyItFits": "You like outdoor, safety-critical work with strong union pay and unsocial-hours premiums.",
    "startingSalary": "£25,000 - £33,000",
    "midSalary": "£38,000 - £55,000",
    "seniorSalary": "£60,000 - £80,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "Trackside, often nights and weekends",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Overhead Line Equipment",
        "shortDescription": "Install and maintain OLE on electrified routes.",
        "typicalEntryRoute": "Network Rail apprenticeship.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Signalling",
        "shortDescription": "Install, test and maintain signalling systems.",
        "typicalEntryRoute": "Signalling apprenticeship + IRSE licensing.",
        "salaryRange": "£35,000 - £65,000"
      },
      {
        "title": "Track Maintenance",
        "shortDescription": "Maintain ballast, sleepers and rails to NR standards.",
        "typicalEntryRoute": "PTS + track apprenticeship.",
        "salaryRange": "£30,000 - £50,000"
      },
      {
        "title": "Rolling Stock",
        "shortDescription": "Maintain and overhaul trains in depots.",
        "typicalEntryRoute": "Engineering apprenticeship + manufacturer training.",
        "salaryRange": "£32,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Mechanical/electrical skills",
      "Safety discipline",
      "Teamwork",
      "Problem solving",
      "Physical fitness"
    ]
  },
  {
    "name": "Fire Alarm Engineer",
    "category": "Skilled Trades",
    "shortDescription": "Install, test and service fire detection systems in commercial and residential buildings.",
    "whyItFits": "You like electrical work with clear regulations and steady demand from compliance requirements.",
    "startingSalary": "£23,000 - £30,000",
    "midSalary": "£35,000 - £50,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Commercial Installation",
        "shortDescription": "Install fire detection systems in offices and retail.",
        "typicalEntryRoute": "Electrical apprenticeship + BS 5839.",
        "salaryRange": "£32,000 - £48,000"
      },
      {
        "title": "Service and Maintenance",
        "shortDescription": "Annual servicing and fault-finding contracts.",
        "typicalEntryRoute": "BS 5839 + experience.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "Healthcare and High-risk",
        "shortDescription": "Install systems in hospitals and care settings.",
        "typicalEntryRoute": "Senior fire engineer.",
        "salaryRange": "£38,000 - £55,000"
      },
      {
        "title": "Aspirating Smoke Detection",
        "shortDescription": "Specialist VESDA and similar systems.",
        "typicalEntryRoute": "Manufacturer certification.",
        "salaryRange": "£40,000 - £60,000"
      }
    ],
    "skillsNeeded": [
      "BS5839 standards",
      "Wiring",
      "Testing equipment",
      "Customer comms",
      "Driving licence"
    ]
  },
  {
    "name": "Security Systems Installer",
    "category": "Skilled Trades",
    "shortDescription": "Fit alarms, CCTV and access control for homes and businesses.",
    "whyItFits": "You like practical electrical work that mixes site visits with technical problem solving.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £65,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "CCTV",
        "shortDescription": "Install IP CCTV in retail, schools and homes.",
        "typicalEntryRoute": "Apprenticeship + manufacturer training.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Intruder Alarms",
        "shortDescription": "Install graded intruder alarm systems.",
        "typicalEntryRoute": "NSI / SSAIB approved engineer.",
        "salaryRange": "£30,000 - £48,000"
      },
      {
        "title": "Access Control",
        "shortDescription": "Card, fob and biometric access systems.",
        "typicalEntryRoute": "Manufacturer training + experience.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Integrated Systems",
        "shortDescription": "Combined CCTV, access and intruder solutions.",
        "typicalEntryRoute": "Senior installer + commissioning skills.",
        "salaryRange": "£38,000 - £60,000"
      }
    ],
    "skillsNeeded": [
      "Wiring",
      "Networking basics",
      "CCTV systems",
      "Customer comms",
      "Driving licence"
    ],
    "selfEmployable": true
  },
  {
    "name": "Smart Meter Engineer",
    "category": "Skilled Trades",
    "shortDescription": "Install smart electricity and gas meters in homes across the UK.",
    "whyItFits": "You want a trade with short training, high demand and good day-rate pay.",
    "startingSalary": "£26,000 - £32,000",
    "midSalary": "£35,000 - £45,000",
    "seniorSalary": "£48,000 - £60,000",
    "timeToEntry": "3 - 6 months training",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Domestic Electricity",
        "shortDescription": "Install SMETS2 electricity meters in homes.",
        "typicalEntryRoute": "Energy & Utility Skills cert (MOCOPA).",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Domestic Gas",
        "shortDescription": "Install gas smart meters with Gas Safe.",
        "typicalEntryRoute": "Gas Safe registration + MOCOPA.",
        "salaryRange": "£32,000 - £48,000"
      },
      {
        "title": "Dual Fuel",
        "shortDescription": "Install both electricity and gas smart meters.",
        "typicalEntryRoute": "Both certifications + experience.",
        "salaryRange": "£35,000 - £52,000"
      },
      {
        "title": "Commercial / I&C",
        "shortDescription": "Install meters at SME and industrial sites.",
        "typicalEntryRoute": "Senior meter engineer + CT metering.",
        "salaryRange": "£40,000 - £62,000"
      }
    ],
    "skillsNeeded": [
      "Gas/electric safe",
      "Customer service",
      "Time management",
      "Driving licence",
      "Manual dexterity"
    ]
  },
  {
    "name": "Refrigeration Engineer",
    "category": "Skilled Trades",
    "shortDescription": "Install and maintain commercial fridges, freezers and cold rooms in food, pharma and retail.",
    "whyItFits": "You want a niche, well-paid trade with steady commercial demand year-round.",
    "startingSalary": "£25,000 - £35,000",
    "midSalary": "£40,000 - £55,000",
    "seniorSalary": "£60,000 - £80,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Commercial Refrigeration",
        "shortDescription": "Service supermarket and food retail systems.",
        "typicalEntryRoute": "NVQ Level 3 + F-Gas.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "Industrial Refrigeration",
        "shortDescription": "Ammonia and CO2 systems in cold storage and food production.",
        "typicalEntryRoute": "Senior engineer + ammonia training.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Heat Pumps",
        "shortDescription": "Install and service air-source and ground-source pumps.",
        "typicalEntryRoute": "F-Gas + MCS heat pump training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Air Conditioning",
        "shortDescription": "Split, VRF and chiller systems for commercial buildings.",
        "typicalEntryRoute": "F-Gas Cat 1.",
        "salaryRange": "£32,000 - £50,000"
      }
    ],
    "skillsNeeded": [
      "F-Gas certification",
      "Refrigeration cycles",
      "Electrical basics",
      "Fault diagnosis",
      "Driving licence"
    ],
    "selfEmployable": true
  },
  {
    "name": "CNC Machinist",
    "category": "Skilled Trades",
    "shortDescription": "Operate computer-controlled machines to cut precision parts from metal and plastic.",
    "whyItFits": "You enjoy precision engineering, programming and seeing finished parts come off the machine.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £65,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "Workshop / factory",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "CNC Milling",
        "shortDescription": "Run multi-axis mills for aerospace and motorsport parts.",
        "typicalEntryRoute": "Engineering apprenticeship.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "CNC Turning",
        "shortDescription": "Lathe work for high-precision components.",
        "typicalEntryRoute": "Engineering apprenticeship.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "5-Axis Machining",
        "shortDescription": "Complex parts on 5-axis machines.",
        "typicalEntryRoute": "Senior machinist + CAM software.",
        "salaryRange": "£38,000 - £58,000"
      },
      {
        "title": "CNC Programming",
        "shortDescription": "Write CAM programmes for production.",
        "typicalEntryRoute": "CAM training + senior machinist.",
        "salaryRange": "£40,000 - £60,000"
      }
    ],
    "skillsNeeded": [
      "G-code",
      "Reading drawings",
      "Metrology",
      "CAD/CAM basics",
      "Attention to detail"
    ]
  },
  {
    "name": "Toolmaker",
    "category": "Skilled Trades",
    "shortDescription": "Hand-build the precision tools, jigs and moulds that production machines use.",
    "whyItFits": "You're a craftsperson at heart and like working to thousandths of a millimetre.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£36,000 - £48,000",
    "seniorSalary": "£52,000 - £68,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Injection Mould Tooling",
        "shortDescription": "Build and repair plastic injection moulds.",
        "typicalEntryRoute": "Toolmaking apprenticeship.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "Press Tooling",
        "shortDescription": "Build progression and stamping tools.",
        "typicalEntryRoute": "Toolmaking apprenticeship.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "Jigs and Fixtures",
        "shortDescription": "Build production jigs for aerospace and auto.",
        "typicalEntryRoute": "Senior toolmaker.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "EDM Specialist",
        "shortDescription": "Wire and spark erosion specialist work.",
        "typicalEntryRoute": "Toolmaker + EDM training.",
        "salaryRange": "£38,000 - £58,000"
      }
    ],
    "skillsNeeded": [
      "Precision measurement",
      "Lathe / mill",
      "Reading drawings",
      "Hand finishing",
      "Patience"
    ]
  },
  {
    "name": "Plant Operator",
    "category": "Skilled Trades",
    "shortDescription": "Drive and operate heavy construction plant - excavators, dumpers, telehandlers.",
    "whyItFits": "You like outdoor site work and being trusted with serious machinery.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£35,000 - £48,000",
    "seniorSalary": "£50,000 - £65,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "360 Excavator",
        "shortDescription": "Operate excavators on civils and groundworks.",
        "typicalEntryRoute": "CPCS / NPORS ticket.",
        "salaryRange": "£32,000 - £48,000"
      },
      {
        "title": "Crane Operator",
        "shortDescription": "Mobile and tower crane operation.",
        "typicalEntryRoute": "CPCS Blue + experience.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Dumper / Roller",
        "shortDescription": "Operate site dumpers and rollers.",
        "typicalEntryRoute": "CPCS tickets.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Piling Rig",
        "shortDescription": "Operate piling rigs on foundation work.",
        "typicalEntryRoute": "CPCS Piling + specialist training.",
        "salaryRange": "£42,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "CPCS / NPORS cards",
      "Spatial awareness",
      "Safety discipline",
      "Maintenance basics",
      "Site etiquette"
    ]
  },
  {
    "name": "Plant Mechanic",
    "category": "Skilled Trades",
    "shortDescription": "Service and repair heavy construction and agricultural plant on site or in the workshop.",
    "whyItFits": "You like big machines and complex hydraulic, electrical and engine fault-finding.",
    "startingSalary": "£25,000 - £34,000",
    "midSalary": "£38,000 - £52,000",
    "seniorSalary": "£55,000 - £72,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Field Service",
        "shortDescription": "Mobile service of plant on customer sites.",
        "typicalEntryRoute": "Plant mechanic apprenticeship.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Workshop Mechanic",
        "shortDescription": "Workshop overhauls and rebuilds.",
        "typicalEntryRoute": "Plant mechanic apprenticeship.",
        "salaryRange": "£30,000 - £48,000"
      },
      {
        "title": "Crane Specialist",
        "shortDescription": "Service of mobile and tower cranes.",
        "typicalEntryRoute": "Senior plant mechanic.",
        "salaryRange": "£40,000 - £62,000"
      },
      {
        "title": "Hydraulics Specialist",
        "shortDescription": "Specialist hydraulic system diagnostics.",
        "typicalEntryRoute": "Hydraulics training + experience.",
        "salaryRange": "£38,000 - £58,000"
      }
    ],
    "skillsNeeded": [
      "Hydraulics",
      "Diesel engines",
      "Auto-electrics",
      "Welding basics",
      "Mobile working"
    ]
  },
  {
    "name": "Groundworker",
    "category": "Skilled Trades",
    "shortDescription": "Do the foundation, drainage and concrete work that every building starts with.",
    "whyItFits": "You're physical, like outdoor work and want to learn the trade construction depends on.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£32,000 - £42,000",
    "seniorSalary": "£45,000 - £60,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Foundations and Concrete",
        "shortDescription": "Pour and form foundations for new builds.",
        "typicalEntryRoute": "CSCS + on-site experience.",
        "salaryRange": "£30,000 - £45,000"
      },
      {
        "title": "Drainage",
        "shortDescription": "Install storm and foul drainage on sites.",
        "typicalEntryRoute": "NVQ Level 2 + ticket training.",
        "salaryRange": "£30,000 - £45,000"
      },
      {
        "title": "Kerbing and Paving",
        "shortDescription": "Hard-landscape elements on civils projects.",
        "typicalEntryRoute": "CSCS + experience.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Reinforced Concrete",
        "shortDescription": "Steel-fix and pour reinforced structures.",
        "typicalEntryRoute": "Steelfixer training + experience.",
        "salaryRange": "£35,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Concreting",
      "Drainage",
      "Setting out",
      "Plant tickets",
      "Physical fitness"
    ]
  },
  {
    "name": "Road Worker",
    "category": "Skilled Trades",
    "shortDescription": "Lay, repair and resurface roads, kerbs and pavements across the UK.",
    "whyItFits": "You like outdoor team work and don't mind nights and weather for steady pay.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £42,000",
    "seniorSalary": "£45,000 - £55,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "Roadside, often nights/weekends",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Surfacing",
        "shortDescription": "Lay asphalt on motorways and trunk roads.",
        "typicalEntryRoute": "Streetworks ticket.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Line Marking",
        "shortDescription": "Apply road markings and high-friction surfaces.",
        "typicalEntryRoute": "Streetworks + training.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Highways Repair",
        "shortDescription": "Pothole and reactive highways repair.",
        "typicalEntryRoute": "Streetworks ticket.",
        "salaryRange": "£26,000 - £38,000"
      },
      {
        "title": "Traffic Management",
        "shortDescription": "Set up TM on motorway and urban works.",
        "typicalEntryRoute": "12D / Lantra TM tickets.",
        "salaryRange": "£28,000 - £42,000"
      }
    ],
    "skillsNeeded": [
      "Tarmac and concrete",
      "Plant operation",
      "NRSWA",
      "Teamwork",
      "Physical fitness"
    ]
  },
  {
    "name": "Highways Maintenance Operative",
    "category": "Skilled Trades",
    "shortDescription": "Keep motorways, A-roads and local highways safe - gritting, repairs, signs and barriers.",
    "whyItFits": "You want stable shift work with a council or contractor and don't mind nights.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£28,000 - £38,000",
    "seniorSalary": "£40,000 - £50,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Reactive Maintenance",
        "shortDescription": "Fix potholes, signs and drains on call-outs.",
        "typicalEntryRoute": "Streetworks + driving licence.",
        "salaryRange": "£26,000 - £38,000"
      },
      {
        "title": "Winter Service",
        "shortDescription": "Gritting and snow clearance rotas.",
        "typicalEntryRoute": "LGV + winter training.",
        "salaryRange": "£28,000 - £40,000 + standby"
      },
      {
        "title": "Drainage and Gullies",
        "shortDescription": "Maintain highway drainage networks.",
        "typicalEntryRoute": "Streetworks + jetting tickets.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Vegetation Management",
        "shortDescription": "Verge cutting and tree work on highways.",
        "typicalEntryRoute": "PA1/PA6 + chainsaw tickets.",
        "salaryRange": "£26,000 - £40,000"
      }
    ],
    "skillsNeeded": [
      "NRSWA",
      "Plant tickets",
      "Traffic management",
      "Manual handling",
      "Driving licence"
    ]
  },
  {
    "name": "Tiler",
    "category": "Skilled Trades",
    "shortDescription": "Cut and fit tiles to walls and floors in bathrooms, kitchens and commercial spaces.",
    "whyItFits": "You're craft-focused, neat and like jobs you can finish and step back from.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £70,000",
    "timeToEntry": "1 - 2 years",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Wall and Floor",
        "shortDescription": "Domestic kitchen and bathroom tiling.",
        "typicalEntryRoute": "NVQ Level 2 + experience.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Large-format Tiling",
        "shortDescription": "Large slab porcelain and natural stone.",
        "typicalEntryRoute": "Senior tiler + manufacturer training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Wet Rooms",
        "shortDescription": "Tank and tile wet rooms and shower areas.",
        "typicalEntryRoute": "Tiler + waterproofing training.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Commercial Tiling",
        "shortDescription": "Retail, hotel and public-realm tiling.",
        "typicalEntryRoute": "Senior tiler + CSCS.",
        "salaryRange": "£35,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Setting out",
      "Cutting tiles",
      "Adhesives & grout",
      "Waterproofing",
      "Customer comms"
    ],
    "selfEmployable": true
  },
  {
    "name": "Roofer",
    "category": "Skilled Trades",
    "shortDescription": "Install and repair pitched and flat roofs - tiles, slate, felt, lead and modern membranes.",
    "whyItFits": "You're confident with heights, physically fit and want a trade that's always in demand.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £70,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On rooftops, outdoors, often at height",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Pitched Tiling and Slating",
        "shortDescription": "Traditional pitched roofing.",
        "typicalEntryRoute": "NVQ Level 2 roofing.",
        "salaryRange": "£28,000 - £48,000"
      },
      {
        "title": "Flat Roofing",
        "shortDescription": "Single-ply, felt and EPDM systems.",
        "typicalEntryRoute": "Manufacturer training.",
        "salaryRange": "£30,000 - £50,000"
      },
      {
        "title": "Lead Work",
        "shortDescription": "Heritage and complex lead roofing.",
        "typicalEntryRoute": "Senior roofer + lead training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Industrial / Sheeting",
        "shortDescription": "Profile metal sheeting on industrial buildings.",
        "typicalEntryRoute": "Roofer + IPAF / PASMA.",
        "salaryRange": "£32,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Working at height",
      "Slating/tiling",
      "Lead work",
      "Health & safety",
      "Physical fitness"
    ],
    "selfEmployable": true
  },
  {
    "name": "Plasterer",
    "category": "Skilled Trades",
    "shortDescription": "Apply skim, render and plasterboard finishes to walls and ceilings in homes and commercial buildings.",
    "whyItFits": "You take pride in a smooth finish and enjoy a clear, well-paid trade you can run solo.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £70,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Skim Finishing",
        "shortDescription": "Two-coat skim finishes on plasterboard.",
        "typicalEntryRoute": "NVQ Level 2 + experience.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Rendering",
        "shortDescription": "External rendering and silicone systems.",
        "typicalEntryRoute": "Manufacturer training.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Drywall and Partitions",
        "shortDescription": "Metal stud and drywall installation.",
        "typicalEntryRoute": "Drywall training + CSCS.",
        "salaryRange": "£30,000 - £48,000"
      },
      {
        "title": "Heritage Plastering",
        "shortDescription": "Lime plaster and traditional finishes.",
        "typicalEntryRoute": "Heritage skills training.",
        "salaryRange": "£35,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Skim and float",
      "Rendering",
      "Drywall",
      "Quoting",
      "Physical stamina"
    ],
    "selfEmployable": true
  },
  {
    "name": "Locksmith",
    "category": "Skilled Trades",
    "shortDescription": "Fit, repair and open locks for homes, businesses and vehicles - emergency and planned work.",
    "whyItFits": "You like solving practical puzzles, helping people in a fix and running your own van.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £45,000",
    "seniorSalary": "£50,000 - £70,000",
    "timeToEntry": "3 - 12 months",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Domestic Locksmith",
        "shortDescription": "Lock changes and emergency entry for homes.",
        "typicalEntryRoute": "MLA training + ID checks.",
        "salaryRange": "£25,000 - £45,000"
      },
      {
        "title": "Commercial Locksmith",
        "shortDescription": "Master-key systems and access for businesses.",
        "typicalEntryRoute": "Senior locksmith + MLA.",
        "salaryRange": "£30,000 - £55,000"
      },
      {
        "title": "Auto Locksmith",
        "shortDescription": "Vehicle key cutting, programming and entry.",
        "typicalEntryRoute": "Auto locksmith training.",
        "salaryRange": "£30,000 - £55,000"
      },
      {
        "title": "Safe Engineer",
        "shortDescription": "Service, open and install safes and vaults.",
        "typicalEntryRoute": "Specialist safe training.",
        "salaryRange": "£35,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "Lock mechanisms",
      "Non-destructive entry",
      "Customer comms",
      "Driving licence",
      "On-call availability"
    ],
    "selfEmployable": true
  },
  {
    "name": "Window Fitter",
    "category": "Skilled Trades",
    "shortDescription": "Install replacement windows and doors in homes - uPVC, aluminium and timber.",
    "whyItFits": "You're practical, like clean installation work and want a trade with strong domestic demand.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £42,000",
    "seniorSalary": "£48,000 - £65,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "uPVC Windows",
        "shortDescription": "Replace residential uPVC windows and doors.",
        "typicalEntryRoute": "FENSA registration + experience.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Aluminium Systems",
        "shortDescription": "Fit aluminium windows and bi-folds.",
        "typicalEntryRoute": "Manufacturer training.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Commercial Glazing",
        "shortDescription": "Curtain walling on commercial buildings.",
        "typicalEntryRoute": "CSCS + glazing apprenticeship.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Sash Window Restoration",
        "shortDescription": "Restore traditional timber sashes.",
        "typicalEntryRoute": "Heritage training + experience.",
        "salaryRange": "£32,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Measuring",
      "Sealing/weather-proofing",
      "Hand and power tools",
      "Customer comms",
      "Heavy lifting"
    ],
    "selfEmployable": true
  },
  {
    "name": "Kitchen Fitter",
    "category": "Skilled Trades",
    "shortDescription": "Install fitted kitchens - units, worktops, appliances and connecting trades.",
    "whyItFits": "You're meticulous, like end-to-end ownership of a project and enjoy seeing finished spaces.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£35,000 - £48,000",
    "seniorSalary": "£52,000 - £75,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Domestic Kitchens",
        "shortDescription": "Fit kitchens in private homes.",
        "typicalEntryRoute": "Joinery background + experience.",
        "salaryRange": "£28,000 - £48,000"
      },
      {
        "title": "Bespoke Kitchens",
        "shortDescription": "Fit high-end bespoke and handmade kitchens.",
        "typicalEntryRoute": "Senior fitter + furniture-making background.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Multi-trade Fit-out",
        "shortDescription": "Full kitchen refit including plumbing and electrics.",
        "typicalEntryRoute": "Multi-trade + supervision skills.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "Commercial Kitchens",
        "shortDescription": "Fit catering and hospitality kitchens.",
        "typicalEntryRoute": "Commercial fitter + Gas Safe ideal.",
        "salaryRange": "£35,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Carpentry",
      "Plumbing basics",
      "Electrical basics",
      "Quoting",
      "Customer comms"
    ],
    "selfEmployable": true
  },
  {
    "name": "Bathroom Fitter",
    "category": "Skilled Trades",
    "shortDescription": "Strip and re-fit bathrooms - tiling, plumbing, electrics and finishing.",
    "whyItFits": "You like multi-trade jobs and the satisfaction of transforming a room in a week.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£35,000 - £48,000",
    "seniorSalary": "£55,000 - £75,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Domestic Bathrooms",
        "shortDescription": "Full bathroom installs in private homes.",
        "typicalEntryRoute": "Multi-trade background.",
        "salaryRange": "£28,000 - £48,000"
      },
      {
        "title": "Wet Rooms",
        "shortDescription": "Tank, tile and fit wet rooms.",
        "typicalEntryRoute": "Senior fitter + waterproofing training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Disability Adaptations",
        "shortDescription": "Level-access showers and adapted bathrooms.",
        "typicalEntryRoute": "Fitter + OT-led adaptations training.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Luxury Bathrooms",
        "shortDescription": "High-end stone, brassware and bespoke installs.",
        "typicalEntryRoute": "Senior fitter + manufacturer training.",
        "salaryRange": "£38,000 - £60,000"
      }
    ],
    "skillsNeeded": [
      "Plumbing",
      "Tiling",
      "Electrical basics",
      "Project sequencing",
      "Customer comms"
    ],
    "selfEmployable": true
  },
  {
    "name": "Flooring Installer",
    "category": "Skilled Trades",
    "shortDescription": "Fit wood, vinyl, laminate and carpet flooring in residential and commercial properties.",
    "whyItFits": "You like precise, satisfying work where the finished result speaks for itself.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £42,000",
    "seniorSalary": "£45,000 - £60,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "LVT and Vinyl",
        "shortDescription": "Glue-down LVT and sheet vinyl for retail and homes.",
        "typicalEntryRoute": "Manufacturer training + experience.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Carpet Fitting",
        "shortDescription": "Residential and commercial carpet installation.",
        "typicalEntryRoute": "Carpet apprenticeship.",
        "salaryRange": "£26,000 - £42,000"
      },
      {
        "title": "Hardwood and Engineered",
        "shortDescription": "Lay solid and engineered timber floors.",
        "typicalEntryRoute": "Senior fitter + manufacturer training.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Sports and Resin Flooring",
        "shortDescription": "Gym, court and resin floor specialist work.",
        "typicalEntryRoute": "Manufacturer certification.",
        "salaryRange": "£35,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Subfloor prep",
      "Cutting and laying",
      "Adhesives",
      "Measuring",
      "Customer comms"
    ],
    "selfEmployable": true
  },
  {
    "name": "Pest Control Technician",
    "category": "Skilled Trades",
    "shortDescription": "Identify and remove pests from homes, businesses and food premises across a local patch.",
    "whyItFits": "You like a varied job out of the office and don't mind unusual problems.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £40,000",
    "seniorSalary": "£45,000 - £55,000",
    "timeToEntry": "3 - 12 months",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Domestic Pest Control",
        "shortDescription": "Rodent, insect and wildlife work in homes.",
        "typicalEntryRoute": "RSPH Level 2.",
        "salaryRange": "£25,000 - £38,000"
      },
      {
        "title": "Commercial Pest Control",
        "shortDescription": "Contract pest control in food and retail premises.",
        "typicalEntryRoute": "RSPH Level 2 + BPCA membership.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Bird Control",
        "shortDescription": "Netting, spiking and proofing for buildings.",
        "typicalEntryRoute": "Working at height + bird training.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Fumigation Specialist",
        "shortDescription": "Container and structural fumigation.",
        "typicalEntryRoute": "Senior tech + specialist licensing.",
        "salaryRange": "£35,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "BPCA training",
      "Customer comms",
      "Reporting",
      "Driving licence",
      "Practical problem solving"
    ],
    "selfEmployable": true
  },
  {
    "name": "Drainage Engineer",
    "category": "Skilled Trades",
    "shortDescription": "Clear, repair and reline domestic and commercial drainage - often emergency callouts.",
    "whyItFits": "You like physical, on-call work with strong day rates and obvious results.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £75,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "CCTV Surveys",
        "shortDescription": "Inspect and report on drains and sewers.",
        "typicalEntryRoute": "Drainage apprenticeship + WinCan training.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Jetting and Clearing",
        "shortDescription": "High-pressure jetting and blockage clearance.",
        "typicalEntryRoute": "Drainage training + driving licence.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "No-dig Repairs",
        "shortDescription": "Patch, lining and CIPP repair work.",
        "typicalEntryRoute": "Specialist no-dig training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Sewer Adoption Works",
        "shortDescription": "Build adoptable drainage on new developments.",
        "typicalEntryRoute": "Senior drainage engineer.",
        "salaryRange": "£35,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Jetting equipment",
      "CCTV surveys",
      "Excavation basics",
      "Driving licence",
      "Customer comms"
    ],
    "selfEmployable": true
  },
  {
    "name": "Water Treatment Technician",
    "category": "Skilled Trades",
    "shortDescription": "Service and maintain water systems in industry, healthcare and commercial buildings.",
    "whyItFits": "You like technical, regulated work with a mix of site visits and chemistry.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£35,000 - £48,000",
    "seniorSalary": "£52,000 - £68,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Cooling Towers",
        "shortDescription": "Service and dose cooling tower systems.",
        "typicalEntryRoute": "L8 + Legionella training.",
        "salaryRange": "£32,000 - £48,000"
      },
      {
        "title": "Boiler Water",
        "shortDescription": "Treat boiler and steam systems in industry.",
        "typicalEntryRoute": "Water treatment + chemistry training.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "Closed Heating Systems",
        "shortDescription": "Treat closed heating systems in buildings.",
        "typicalEntryRoute": "Water treatment training.",
        "salaryRange": "£30,000 - £45,000"
      },
      {
        "title": "Legionella Risk Assessment",
        "shortDescription": "Carry out L8 risk assessments.",
        "typicalEntryRoute": "L8 + risk assessment qualification.",
        "salaryRange": "£32,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Water hygiene (L8)",
      "Chemistry basics",
      "Sampling",
      "Reporting",
      "Driving licence"
    ]
  },
  {
    "name": "Telecoms Engineer",
    "category": "Skilled Trades",
    "shortDescription": "Install and repair telephone, broadband and business network infrastructure.",
    "whyItFits": "You enjoy mixing customer work with cabling, kit and a bit of networking.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £65,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Copper and Broadband",
        "shortDescription": "Install and fault-find on copper and FTTC.",
        "typicalEntryRoute": "Openreach training or apprenticeship.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Mobile Network",
        "shortDescription": "Install, commission and maintain mobile sites.",
        "typicalEntryRoute": "Telecoms apprenticeship + RF training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Business Voice and Data",
        "shortDescription": "Install VoIP and managed services for SMEs.",
        "typicalEntryRoute": "Telecoms + IT background.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Network Engineer",
        "shortDescription": "Configure routers, switches and SD-WAN.",
        "typicalEntryRoute": "CCNA + IT background.",
        "salaryRange": "£35,000 - £60,000"
      }
    ],
    "skillsNeeded": [
      "Copper and fibre",
      "Networking basics",
      "Climbing/poles",
      "Customer comms",
      "Driving licence"
    ]
  },
  {
    "name": "Fibre Optic Technician",
    "category": "Skilled Trades",
    "shortDescription": "Install and splice fibre cabling for the UK's full-fibre broadband rollout.",
    "whyItFits": "You like a niche, well-paid trade tied to one of the UK's biggest infrastructure projects.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£36,000 - £50,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "FTTP Installation",
        "shortDescription": "Install fibre-to-the-premises in homes.",
        "typicalEntryRoute": "Fibre installer training.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Splicing and Termination",
        "shortDescription": "Fusion splicing and OTDR testing.",
        "typicalEntryRoute": "Splicing training + experience.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "Cabling Infrastructure",
        "shortDescription": "Pull and dress fibre in data centres and buildings.",
        "typicalEntryRoute": "Structured cabling training.",
        "salaryRange": "£30,000 - £48,000"
      },
      {
        "title": "Network Commissioning",
        "shortDescription": "Test and commission high-capacity fibre links.",
        "typicalEntryRoute": "Senior fibre engineer.",
        "salaryRange": "£40,000 - £60,000"
      }
    ],
    "skillsNeeded": [
      "Fusion splicing",
      "OTDR testing",
      "Working at height",
      "Civils basics",
      "Driving licence"
    ]
  },
  {
    "name": "Glazier",
    "category": "Skilled Trades",
    "shortDescription": "Cut, fit and replace glass in windows, shopfronts and structural installations.",
    "whyItFits": "You're careful, methodical and like a trade where precision matters every cut.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £42,000",
    "seniorSalary": "£48,000 - £62,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Glass Installation",
        "shortDescription": "Cut, fit and replace glazing in homes and offices.",
        "typicalEntryRoute": "Glazing apprenticeship.",
        "salaryRange": "£26,000 - £42,000"
      },
      {
        "title": "Commercial Curtain Walling",
        "shortDescription": "Install structural glazing on commercial builds.",
        "typicalEntryRoute": "CSCS + glazing apprenticeship.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Bespoke and Decorative",
        "shortDescription": "Stained, leaded and bespoke decorative glass.",
        "typicalEntryRoute": "Senior glazier + craft training.",
        "salaryRange": "£30,000 - £48,000"
      },
      {
        "title": "Emergency Glazing",
        "shortDescription": "24/7 break-in and storm-damage response.",
        "typicalEntryRoute": "Glazier + driving licence.",
        "salaryRange": "£28,000 - £45,000"
      }
    ],
    "skillsNeeded": [
      "Glass handling",
      "Measuring",
      "Sealing",
      "Working at height",
      "Heavy lifting"
    ],
    "selfEmployable": true
  },
  {
    "name": "Stonemason",
    "category": "Skilled Trades",
    "shortDescription": "Cut, carve and fix stone for heritage restoration, new builds and memorials.",
    "whyItFits": "You're a craft-driven person who values traditional skills and beautiful materials.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£32,000 - £42,000",
    "seniorSalary": "£45,000 - £60,000",
    "timeToEntry": "3 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Banker Masonry",
        "shortDescription": "Work stone at the bench to drawings.",
        "typicalEntryRoute": "Stonemasonry apprenticeship.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Fixer Mason",
        "shortDescription": "Fix dressed stone on buildings.",
        "typicalEntryRoute": "Stonemasonry + CSCS.",
        "salaryRange": "£30,000 - £48,000"
      },
      {
        "title": "Heritage Restoration",
        "shortDescription": "Conservation work on listed buildings.",
        "typicalEntryRoute": "Senior mason + heritage training.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Memorial Masonry",
        "shortDescription": "Carve and letter memorial stones.",
        "typicalEntryRoute": "Apprenticeship + lettering training.",
        "salaryRange": "£28,000 - £45,000"
      }
    ],
    "skillsNeeded": [
      "Hand tools",
      "Banker masonry",
      "Fixing",
      "Drawing reading",
      "Patience"
    ]
  },
  {
    "name": "Insulation Installer",
    "category": "Skilled Trades",
    "shortDescription": "Install loft, cavity wall and external insulation in homes - core trade for net-zero retrofits.",
    "whyItFits": "You want a trade tied to the long-running UK retrofit programme with steady demand.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £42,000",
    "seniorSalary": "£45,000 - £58,000",
    "timeToEntry": "2 - 4 years (apprenticeship)",
    "degreeRequired": "Not required - apprenticeship route",
    "difficulty": "Medium",
    "competitionLevel": "Low",
    "workEnvironment": "On-site, hands-on, often outdoors",
    "stability": "Steady - shortage of skilled tradespeople in the UK",
    "weights": {
      "structure": 2,
      "income": 1,
      "freedom": 1,
      "people": 0,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Cavity Wall Insulation",
        "shortDescription": "Install cavity insulation under ECO and PAS 2035.",
        "typicalEntryRoute": "PAS 2030 + manufacturer training.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "External Wall Insulation",
        "shortDescription": "Fit EWI systems on solid-wall homes.",
        "typicalEntryRoute": "PAS 2030 + scaffolding tickets.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "Loft Insulation",
        "shortDescription": "Install and top up loft insulation at scale.",
        "typicalEntryRoute": "PAS 2030.",
        "salaryRange": "£26,000 - £38,000"
      },
      {
        "title": "Industrial Insulation",
        "shortDescription": "Lag pipework and plant in industrial settings.",
        "typicalEntryRoute": "Industrial insulation apprenticeship.",
        "salaryRange": "£32,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Insulation materials",
      "Working at height",
      "PAS 2030/2035",
      "Manual handling",
      "Customer comms"
    ]
  },
  {
    "name": "Midwife",
    "category": "Healthcare and Care",
    "shortDescription": "Care for women through pregnancy, birth and the early days with a new baby.",
    "whyItFits": "You want a deeply human, high-trust clinical role where your work changes families' lives.",
    "startingSalary": "£28,407 (NHS Band 5)",
    "midSalary": "£35,000 - £43,000",
    "seniorSalary": "£50,000 - £75,000",
    "timeToEntry": "3 years (BSc Midwifery)",
    "degreeRequired": "Required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, birthing unit or community",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Community Midwifery",
        "shortDescription": "Antenatal and postnatal care in community clinics and homes.",
        "typicalEntryRoute": "BSc Midwifery + NMC registration.",
        "salaryRange": "£29,000 - £46,000"
      },
      {
        "title": "Hospital Labour Ward",
        "shortDescription": "Intrapartum care in obstetric units.",
        "typicalEntryRoute": "BSc Midwifery + NMC + labour ward experience.",
        "salaryRange": "£29,000 - £46,000"
      },
      {
        "title": "Antenatal Care",
        "shortDescription": "Specialist clinics, screening and education.",
        "typicalEntryRoute": "Midwife + antenatal training.",
        "salaryRange": "£32,000 - £48,000"
      },
      {
        "title": "Postnatal and Infant Feeding",
        "shortDescription": "Postnatal support including IBCLC pathways.",
        "typicalEntryRoute": "Midwife + lactation training.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "High-risk Pregnancy",
        "shortDescription": "Caseload midwifery for complex and high-risk pregnancies.",
        "typicalEntryRoute": "Senior midwife + specialist training.",
        "salaryRange": "£37,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Clinical skills",
      "Empathy",
      "Calm under pressure",
      "Communication",
      "Stamina"
    ]
  },
  {
    "name": "Dental Hygienist",
    "category": "Healthcare and Care",
    "shortDescription": "Clean teeth, treat gum disease and teach patients how to keep their mouths healthy.",
    "whyItFits": "You want a regulated healthcare role with great work-life balance and clear career routes.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £50,000",
    "seniorSalary": "£55,000 - £75,000",
    "timeToEntry": "2 - 3 years (diploma)",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Dental practice",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "General Practice Hygiene",
        "shortDescription": "Routine scale-and-polish and perio care.",
        "typicalEntryRoute": "Diploma in Dental Hygiene + GDC.",
        "salaryRange": "£30,000 - £50,000"
      },
      {
        "title": "Periodontal Specialist",
        "shortDescription": "Advanced perio work in specialist practices.",
        "typicalEntryRoute": "Hygienist + perio training.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Direct Access Practice",
        "shortDescription": "Run own clinic offering hygiene direct to patients.",
        "typicalEntryRoute": "Direct access training + GDC.",
        "salaryRange": "£45,000 - £75,000"
      },
      {
        "title": "Paediatric Hygiene",
        "shortDescription": "Children's preventative dental care.",
        "typicalEntryRoute": "Hygienist + paediatric training.",
        "salaryRange": "£32,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Manual dexterity",
      "Patient comms",
      "Clinical attention",
      "Stamina",
      "Teamwork"
    ],
    "selfEmployable": true
  },
  {
    "name": "Dental Therapist",
    "category": "Healthcare and Care",
    "shortDescription": "Do fillings, extractions of baby teeth and preventive treatment - more clinical scope than a hygienist.",
    "whyItFits": "You want broader clinical work than hygiene but a shorter route than full dentistry.",
    "startingSalary": "£32,000 - £40,000",
    "midSalary": "£45,000 - £60,000",
    "seniorSalary": "£65,000 - £85,000",
    "timeToEntry": "3 years (degree)",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "NHS Therapy",
        "shortDescription": "Restorative and preventative work in NHS practice.",
        "typicalEntryRoute": "BSc Dental Therapy + GDC.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Private Therapy",
        "shortDescription": "Cosmetic and restorative work in private practice.",
        "typicalEntryRoute": "Senior therapist + GDC.",
        "salaryRange": "£45,000 - £75,000"
      },
      {
        "title": "Paediatric Therapy",
        "shortDescription": "Therapy for children, often community-based.",
        "typicalEntryRoute": "Therapist + paediatric experience.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Special Care Dentistry",
        "shortDescription": "Therapy for patients with complex needs.",
        "typicalEntryRoute": "Therapist + special care training.",
        "salaryRange": "£38,000 - £58,000"
      }
    ],
    "skillsNeeded": [
      "Clinical procedures",
      "Patient comms",
      "Manual dexterity",
      "Calm under pressure",
      "Teamwork"
    ]
  },
  {
    "name": "Operating Department Practitioner",
    "category": "Healthcare and Care",
    "shortDescription": "Support surgical teams before, during and after operations - the team's third clinical pillar.",
    "whyItFits": "You want high-pressure clinical work in theatres without the doctor training pathway.",
    "startingSalary": "£28,407 (NHS Band 5)",
    "midSalary": "£35,000 - £43,000",
    "seniorSalary": "£50,000 - £65,000",
    "timeToEntry": "3 years (BSc)",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Operating theatre",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Anaesthetics",
        "shortDescription": "Assist the anaesthetist during surgery.",
        "typicalEntryRoute": "BSc ODP + HCPC registration.",
        "salaryRange": "£29,000 - £40,000"
      },
      {
        "title": "Surgical Practice",
        "shortDescription": "Scrub role in theatre supporting the surgeon.",
        "typicalEntryRoute": "BSc ODP + HCPC + theatre experience.",
        "salaryRange": "£29,000 - £40,000"
      },
      {
        "title": "Recovery",
        "shortDescription": "Look after patients waking from anaesthetic.",
        "typicalEntryRoute": "ODP + recovery training.",
        "salaryRange": "£32,000 - £45,000"
      },
      {
        "title": "Trauma and Emergency",
        "shortDescription": "Theatre work in trauma and emergency settings.",
        "typicalEntryRoute": "Senior ODP + trauma rotation.",
        "salaryRange": "£37,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Clinical skills",
      "Calm under pressure",
      "Sterile technique",
      "Teamwork",
      "Stamina"
    ]
  },
  {
    "name": "Audiologist",
    "category": "Healthcare and Care",
    "shortDescription": "Diagnose hearing and balance disorders and fit hearing aids for patients of all ages.",
    "whyItFits": "You enjoy a technical clinical role with real, measurable impact on quality of life.",
    "startingSalary": "£28,407 (NHS Band 5)",
    "midSalary": "£35,000 - £48,000",
    "seniorSalary": "£55,000 - £75,000",
    "timeToEntry": "3 - 4 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Adult Audiology",
        "shortDescription": "Hearing assessment and aids for adults.",
        "typicalEntryRoute": "BSc Healthcare Science (Audiology) + RCCP/HCPC.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Paediatric Audiology",
        "shortDescription": "Hearing screening and management for children.",
        "typicalEntryRoute": "Audiologist + paediatric training.",
        "salaryRange": "£32,000 - £50,000"
      },
      {
        "title": "Tinnitus Specialist",
        "shortDescription": "Assessment and management of tinnitus patients.",
        "typicalEntryRoute": "Audiologist + tinnitus training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Cochlear Implants",
        "shortDescription": "Programming and rehab for implant patients.",
        "typicalEntryRoute": "Senior audiologist + specialist training.",
        "salaryRange": "£40,000 - £62,000"
      }
    ],
    "skillsNeeded": [
      "Clinical assessment",
      "Hearing aid tech",
      "Patient comms",
      "Patience",
      "Reporting"
    ]
  },
  {
    "name": "Orthoptist",
    "category": "Healthcare and Care",
    "shortDescription": "Diagnose and treat eye movement disorders, squints and binocular vision problems.",
    "whyItFits": "You're interested in a niche, regulated eye health role with strong long-term demand.",
    "startingSalary": "£28,407 (NHS Band 5)",
    "midSalary": "£35,000 - £45,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "3 years (BSc)",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Paediatric Eye Care",
        "shortDescription": "Diagnose and treat squint and lazy eye in children.",
        "typicalEntryRoute": "BSc Orthoptics + HCPC.",
        "salaryRange": "£29,000 - £40,000"
      },
      {
        "title": "Adult Neuro-orthoptics",
        "shortDescription": "Eye movement disorders after stroke or brain injury.",
        "typicalEntryRoute": "Senior orthoptist + neuro training.",
        "salaryRange": "£37,000 - £52,000"
      },
      {
        "title": "Glaucoma Clinics",
        "shortDescription": "Orthoptists running shared-care glaucoma clinics.",
        "typicalEntryRoute": "Orthoptist + glaucoma training.",
        "salaryRange": "£37,000 - £55,000"
      },
      {
        "title": "Low Vision",
        "shortDescription": "Assessment and support for partially sighted patients.",
        "typicalEntryRoute": "Orthoptist + low vision training.",
        "salaryRange": "£35,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Clinical assessment",
      "Working with children",
      "Reporting",
      "Teamwork",
      "Patience"
    ]
  },
  {
    "name": "Podiatrist",
    "category": "Healthcare and Care",
    "shortDescription": "Diagnose and treat conditions affecting the foot, ankle and lower limb.",
    "whyItFits": "You want a clinical role with both NHS and private-practice routes and a strong solo option.",
    "startingSalary": "£28,407 (NHS Band 5)",
    "midSalary": "£35,000 - £50,000",
    "seniorSalary": "£55,000 - £80,000",
    "timeToEntry": "3 years (BSc)",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "NHS Podiatry",
        "shortDescription": "Routine and high-risk foot care in the NHS.",
        "typicalEntryRoute": "BSc Podiatry + HCPC.",
        "salaryRange": "£29,000 - £45,000"
      },
      {
        "title": "Diabetic Foot",
        "shortDescription": "Specialist diabetic foot clinics.",
        "typicalEntryRoute": "Podiatrist + diabetic training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Sports Podiatry",
        "shortDescription": "Biomechanics and gait for athletes.",
        "typicalEntryRoute": "Podiatrist + sports training.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "Podiatric Surgery",
        "shortDescription": "Surgical podiatry in specialist centres.",
        "typicalEntryRoute": "Podiatrist + surgical fellowship.",
        "salaryRange": "£60,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "Clinical assessment",
      "Manual dexterity",
      "Patient comms",
      "Biomechanics",
      "Business basics"
    ],
    "selfEmployable": true
  },
  {
    "name": "Dietitian",
    "category": "Healthcare and Care",
    "shortDescription": "Use nutrition science to treat medical conditions - in hospitals, clinics or private practice.",
    "whyItFits": "You like science-based, patient-centred work with strong NHS and private options.",
    "startingSalary": "£28,407 (NHS Band 5)",
    "midSalary": "£35,000 - £48,000",
    "seniorSalary": "£55,000 - £75,000",
    "timeToEntry": "3 - 4 years (BSc)",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Acute Dietetics",
        "shortDescription": "Hospital wards including ICU and surgery.",
        "typicalEntryRoute": "BSc Dietetics + HCPC.",
        "salaryRange": "£29,000 - £45,000"
      },
      {
        "title": "Diabetes and Endocrine",
        "shortDescription": "Specialist diabetes clinics.",
        "typicalEntryRoute": "Dietitian + specialist training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Paediatric Dietetics",
        "shortDescription": "Children's dietetics in hospital and community.",
        "typicalEntryRoute": "Dietitian + paediatric training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Public Health and Industry",
        "shortDescription": "Industry, public-health and food policy roles.",
        "typicalEntryRoute": "Dietitian + public-health background.",
        "salaryRange": "£35,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "Nutrition science",
      "Patient comms",
      "Behaviour change",
      "Reporting",
      "Empathy"
    ],
    "selfEmployable": true
  },
  {
    "name": "Paramedic Practitioner",
    "category": "Healthcare and Care",
    "shortDescription": "Experienced paramedic working in advanced roles - urgent care, GP surgeries, hospitals.",
    "whyItFits": "You want autonomy and clinical authority beyond standard ambulance work.",
    "startingSalary": "£35,392 (NHS Band 6)",
    "midSalary": "£43,742 - £50,056",
    "seniorSalary": "£55,000 - £75,000",
    "timeToEntry": "5 - 7 years post-qualification",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Urgent Care",
        "shortDescription": "Same-day urgent care in GP and community settings.",
        "typicalEntryRoute": "BSc Paramedic Science + Advanced Practice MSc.",
        "salaryRange": "£43,000 - £58,000"
      },
      {
        "title": "Primary Care",
        "shortDescription": "Embedded in GP surgeries running same-day clinics.",
        "typicalEntryRoute": "ACP MSc + prescribing.",
        "salaryRange": "£45,000 - £62,000"
      },
      {
        "title": "Critical Care Paramedic",
        "shortDescription": "Pre-hospital critical care with HEMS / air ambulance.",
        "typicalEntryRoute": "Senior paramedic + critical care PG.",
        "salaryRange": "£45,000 - £65,000"
      },
      {
        "title": "Mental Health Response",
        "shortDescription": "Crisis response with mental health teams.",
        "typicalEntryRoute": "Paramedic + mental health PG.",
        "salaryRange": "£42,000 - £58,000"
      }
    ],
    "skillsNeeded": [
      "Advanced assessment",
      "Prescribing",
      "Decision making",
      "Calm under pressure",
      "Comms"
    ]
  },
  {
    "name": "Clinical Coder",
    "category": "Healthcare and Care",
    "shortDescription": "Translate medical records into the codes used for NHS funding, audits and research.",
    "whyItFits": "You're detail-driven, like clear standards and want a back-office role inside healthcare.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£32,000 - £42,000",
    "seniorSalary": "£45,000 - £60,000",
    "timeToEntry": "6 - 18 months training",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office or remote",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Inpatient Coding",
        "shortDescription": "Code finished consultant episodes for tariffs.",
        "typicalEntryRoute": "ACC qualification.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Outpatient Coding",
        "shortDescription": "Coding for outpatient activity and PbR.",
        "typicalEntryRoute": "ACC + outpatient training.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Cancer / Specialty Coding",
        "shortDescription": "Specialist coding in cancer registries and tertiary centres.",
        "typicalEntryRoute": "Senior coder + specialty training.",
        "salaryRange": "£35,000 - £50,000"
      },
      {
        "title": "Coding Auditor",
        "shortDescription": "Audit and train other coders.",
        "typicalEntryRoute": "Senior coder + audit training.",
        "salaryRange": "£38,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "ICD-10 / OPCS",
      "Medical terminology",
      "Attention to detail",
      "Concentration",
      "Reporting"
    ]
  },
  {
    "name": "Medical Secretary",
    "category": "Healthcare and Care",
    "shortDescription": "Support consultants and clinical teams with admin, letters, scheduling and patient comms.",
    "whyItFits": "You're organised, professional and want a stable healthcare admin role with clear hours.",
    "startingSalary": "£22,000 - £27,000",
    "midSalary": "£28,000 - £35,000",
    "seniorSalary": "£38,000 - £45,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital or clinic office",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Hospital Specialty Secretary",
        "shortDescription": "Support consultants in NHS specialties.",
        "typicalEntryRoute": "AMSPAR + secretarial experience.",
        "salaryRange": "£24,000 - £32,000"
      },
      {
        "title": "Private Practice Secretary",
        "shortDescription": "Run a consultant's private practice end to end.",
        "typicalEntryRoute": "Senior med sec + private invoicing.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Medico-legal Secretary",
        "shortDescription": "Support medico-legal report writing.",
        "typicalEntryRoute": "Secretarial + legal background.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "GP Practice Secretary",
        "shortDescription": "Referrals, results and admin in primary care.",
        "typicalEntryRoute": "AMSPAR or NHS experience.",
        "salaryRange": "£23,000 - £32,000"
      }
    ],
    "skillsNeeded": [
      "Audio typing",
      "Medical terminology",
      "Scheduling",
      "Discretion",
      "Patient comms"
    ]
  },
  {
    "name": "Pharmacy Technician",
    "category": "Healthcare and Care",
    "shortDescription": "Prepare and dispense medicines in community, hospital or production pharmacy settings.",
    "whyItFits": "You want a regulated healthcare role with a 2-year apprenticeship route and steady demand.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£30,000 - £38,000",
    "seniorSalary": "£40,000 - £55,000",
    "timeToEntry": "2 years (apprenticeship)",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Hospital Pharmacy",
        "shortDescription": "Dispensing, wards and aseptic services.",
        "typicalEntryRoute": "BTEC Level 3 + GPhC.",
        "salaryRange": "£25,000 - £37,000"
      },
      {
        "title": "Community Pharmacy",
        "shortDescription": "Dispensing and patient services in high-street pharmacy.",
        "typicalEntryRoute": "BTEC Level 3 + GPhC.",
        "salaryRange": "£22,000 - £32,000"
      },
      {
        "title": "Aseptic / Production",
        "shortDescription": "Compounding in aseptic units and chemo production.",
        "typicalEntryRoute": "Senior tech + aseptic training.",
        "salaryRange": "£30,000 - £45,000"
      },
      {
        "title": "Medicines Management",
        "shortDescription": "Ward-based medicines optimisation.",
        "typicalEntryRoute": "Senior tech + clinical training.",
        "salaryRange": "£32,000 - £45,000"
      }
    ],
    "skillsNeeded": [
      "Dispensing accuracy",
      "Patient comms",
      "Stock control",
      "Numeracy",
      "Calm pace"
    ]
  },
  {
    "name": "Biomedical Scientist",
    "category": "Healthcare and Care",
    "shortDescription": "Analyse blood, tissue and other samples in NHS and private labs to support diagnosis.",
    "whyItFits": "You like science and lab work and want a regulated route into a clinical-adjacent career.",
    "startingSalary": "£28,407 (NHS Band 5)",
    "midSalary": "£35,000 - £45,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "3 - 4 years (BSc)",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital or private lab",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Haematology",
        "shortDescription": "Blood disorders and transfusion lab work.",
        "typicalEntryRoute": "BSc IBMS + HCPC.",
        "salaryRange": "£29,000 - £45,000"
      },
      {
        "title": "Microbiology",
        "shortDescription": "Identify pathogens and antibiotic susceptibility.",
        "typicalEntryRoute": "BSc IBMS + HCPC.",
        "salaryRange": "£29,000 - £45,000"
      },
      {
        "title": "Histopathology",
        "shortDescription": "Tissue processing and reporting for cancer diagnosis.",
        "typicalEntryRoute": "BSc IBMS + histopathology training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Clinical Biochemistry",
        "shortDescription": "Analyse body fluids for disease markers.",
        "typicalEntryRoute": "BSc IBMS + HCPC.",
        "salaryRange": "£29,000 - £45,000"
      }
    ],
    "skillsNeeded": [
      "Lab techniques",
      "Microscopy",
      "QC",
      "Reporting",
      "Attention to detail"
    ]
  },
  {
    "name": "Genetic Counsellor",
    "category": "Healthcare and Care",
    "shortDescription": "Help patients and families understand inherited conditions and the implications of genetic testing.",
    "whyItFits": "You combine scientific literacy with deep empathy and like working through complex personal decisions.",
    "startingSalary": "£35,000 - £45,000",
    "midSalary": "£45,000 - £55,000",
    "seniorSalary": "£60,000 - £75,000",
    "timeToEntry": "3 - 5 years (MSc + training)",
    "degreeRequired": "Required (MSc)",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Cancer Genetics",
        "shortDescription": "Counselling families on hereditary cancer risk.",
        "typicalEntryRoute": "MSc Genetic Counselling + GCRB registration.",
        "salaryRange": "£40,000 - £58,000"
      },
      {
        "title": "Prenatal Genetics",
        "shortDescription": "Counselling on prenatal screening and diagnosis.",
        "typicalEntryRoute": "GCRB + prenatal experience.",
        "salaryRange": "£42,000 - £60,000"
      },
      {
        "title": "Paediatric Genetics",
        "shortDescription": "Counselling families with paediatric genetic conditions.",
        "typicalEntryRoute": "GCRB + paediatric experience.",
        "salaryRange": "£42,000 - £60,000"
      },
      {
        "title": "Cardiac Genetics",
        "shortDescription": "Inherited cardiac conditions counselling.",
        "typicalEntryRoute": "GCRB + cardiac genetics training.",
        "salaryRange": "£42,000 - £62,000"
      }
    ],
    "skillsNeeded": [
      "Genetics",
      "Counselling skills",
      "Empathy",
      "Reporting",
      "Family dynamics"
    ]
  },
  {
    "name": "Play Therapist",
    "category": "Healthcare and Care",
    "shortDescription": "Use play as the language of therapy with children working through trauma, loss or emotional issues.",
    "whyItFits": "You're patient with children and want a deeply meaningful therapeutic specialism.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £50,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "2 - 4 years (post-graduate)",
    "degreeRequired": "Required + post-grad",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "School-based Play Therapy",
        "shortDescription": "Therapy for children in primary and secondary schools.",
        "typicalEntryRoute": "PG Dip Play Therapy + BAPT.",
        "salaryRange": "£32,000 - £48,000"
      },
      {
        "title": "NHS / CAMHS Play Therapy",
        "shortDescription": "Therapy in CAMHS and paediatric services.",
        "typicalEntryRoute": "BAPT + NHS experience.",
        "salaryRange": "£32,000 - £48,000"
      },
      {
        "title": "Trauma-focused Play Therapy",
        "shortDescription": "Therapy for children with trauma backgrounds.",
        "typicalEntryRoute": "Play therapist + trauma training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Private Practice",
        "shortDescription": "Independent practice for self-referred families.",
        "typicalEntryRoute": "BAPT + private practice setup.",
        "salaryRange": "£40,000 - £75,000"
      }
    ],
    "skillsNeeded": [
      "Child development",
      "Therapeutic skills",
      "Patience",
      "Reflection",
      "Boundary holding"
    ],
    "selfEmployable": true
  },
  {
    "name": "Counselling Psychologist",
    "category": "Healthcare and Care",
    "shortDescription": "Use psychological theory to help adults with mental health and life challenges.",
    "whyItFits": "You want a doctoral-level psychology route with strong private-practice options.",
    "startingSalary": "£40,000 - £50,000",
    "midSalary": "£55,000 - £70,000",
    "seniorSalary": "£80,000 - £110,000",
    "timeToEntry": "6 - 8 years (DPsych)",
    "degreeRequired": "Required (DPsych)",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "NHS Counselling Psychology",
        "shortDescription": "Therapy in adult mental health teams.",
        "typicalEntryRoute": "DPsych Counselling Psychology + HCPC.",
        "salaryRange": "£43,000 - £58,000"
      },
      {
        "title": "Private Practice",
        "shortDescription": "Independent therapy practice for self-referred clients.",
        "typicalEntryRoute": "HCPC + practice setup.",
        "salaryRange": "£60,000 - £120,000"
      },
      {
        "title": "Forensic Settings",
        "shortDescription": "Counselling psychology in prisons and forensic units.",
        "typicalEntryRoute": "HCPC + forensic experience.",
        "salaryRange": "£45,000 - £60,000"
      },
      {
        "title": "Workplace Psychology",
        "shortDescription": "EAP and workplace mental health.",
        "typicalEntryRoute": "HCPC + corporate experience.",
        "salaryRange": "£50,000 - £85,000"
      }
    ],
    "skillsNeeded": [
      "Therapeutic models",
      "Empathy",
      "Reflective practice",
      "Reporting",
      "Boundary holding"
    ],
    "selfEmployable": true
  },
  {
    "name": "Educational Psychologist",
    "category": "Healthcare and Care",
    "shortDescription": "Assess and support children and young people with learning, behavioural or emotional needs.",
    "whyItFits": "You want a psychology specialism inside schools and local authorities with clear pathways.",
    "startingSalary": "£42,000 - £50,000",
    "midSalary": "£55,000 - £70,000",
    "seniorSalary": "£75,000 - £95,000",
    "timeToEntry": "6 - 7 years (DEdPsy)",
    "degreeRequired": "Required (DEdPsy)",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Local Authority EP",
        "shortDescription": "Statutory assessment work for LA SEND teams.",
        "typicalEntryRoute": "DEdPsy + HCPC.",
        "salaryRange": "£45,000 - £62,000"
      },
      {
        "title": "Independent EP",
        "shortDescription": "Private assessments for tribunal and parent referrals.",
        "typicalEntryRoute": "HCPC + private practice.",
        "salaryRange": "£500 - £1,200 per assessment"
      },
      {
        "title": "Early Years Specialist",
        "shortDescription": "EP work focused on under-5s.",
        "typicalEntryRoute": "DEdPsy + early years training.",
        "salaryRange": "£45,000 - £62,000"
      },
      {
        "title": "Specialist Schools EP",
        "shortDescription": "EP work embedded in special schools.",
        "typicalEntryRoute": "DEdPsy + SEND experience.",
        "salaryRange": "£45,000 - £62,000"
      }
    ],
    "skillsNeeded": [
      "Child development",
      "Assessment",
      "Report writing",
      "Empathy",
      "Multi-agency working"
    ]
  },
  {
    "name": "Clinical Psychologist",
    "category": "Healthcare and Care",
    "shortDescription": "Use psychology to assess and treat people with mental health conditions in NHS and private settings.",
    "whyItFits": "You want the highest-level psychology training route into NHS and private practice.",
    "startingSalary": "£43,742 (NHS Band 7)",
    "midSalary": "£55,000 - £75,000",
    "seniorSalary": "£85,000 - £115,000",
    "timeToEntry": "6 - 8 years (DClinPsy)",
    "degreeRequired": "Required (DClinPsy)",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Adult Mental Health",
        "shortDescription": "Therapy and assessment in adult mental health teams.",
        "typicalEntryRoute": "DClinPsy + HCPC.",
        "salaryRange": "£50,000 - £75,000"
      },
      {
        "title": "CAMHS",
        "shortDescription": "Therapy with children and adolescents.",
        "typicalEntryRoute": "DClinPsy + CAMHS training.",
        "salaryRange": "£50,000 - £75,000"
      },
      {
        "title": "Neuropsychology",
        "shortDescription": "Cognitive assessment after brain injury or dementia.",
        "typicalEntryRoute": "DClinPsy + BPS neuropsychology training.",
        "salaryRange": "£55,000 - £85,000"
      },
      {
        "title": "Health Psychology in Hospitals",
        "shortDescription": "Therapy for patients with long-term conditions.",
        "typicalEntryRoute": "DClinPsy + health psychology training.",
        "salaryRange": "£50,000 - £80,000"
      }
    ],
    "skillsNeeded": [
      "Therapy models",
      "Assessment",
      "Empathy",
      "Reflective practice",
      "Supervision"
    ],
    "selfEmployable": true
  },
  {
    "name": "Mental Health Support Worker",
    "category": "Healthcare and Care",
    "shortDescription": "Support people with mental health conditions day-to-day in hospitals, communities or supported housing.",
    "whyItFits": "You want a hands-on entry route into mental health work without years of training.",
    "startingSalary": "£22,000 - £26,000",
    "midSalary": "£26,000 - £32,000",
    "seniorSalary": "£32,000 - £38,000",
    "timeToEntry": "0 - 6 months",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Inpatient Support",
        "shortDescription": "Support patients on acute wards.",
        "typicalEntryRoute": "Care experience + provider training.",
        "salaryRange": "£22,000 - £28,000"
      },
      {
        "title": "Community Mental Health",
        "shortDescription": "Outreach with community mental health teams.",
        "typicalEntryRoute": "Care experience + training.",
        "salaryRange": "£22,000 - £30,000"
      },
      {
        "title": "Crisis Team Support",
        "shortDescription": "Work alongside crisis and home treatment teams.",
        "typicalEntryRoute": "Senior support worker.",
        "salaryRange": "£24,000 - £32,000"
      },
      {
        "title": "CAMHS Support",
        "shortDescription": "Support children and young people in services.",
        "typicalEntryRoute": "Care + young people experience.",
        "salaryRange": "£23,000 - £30,000"
      }
    ],
    "skillsNeeded": [
      "Empathy",
      "Listening",
      "De-escalation",
      "Recording",
      "Resilience"
    ]
  },
  {
    "name": "Substance Misuse Worker",
    "category": "Healthcare and Care",
    "shortDescription": "Support people through drug and alcohol recovery in NHS, charity or community services.",
    "whyItFits": "You want practical, relational work with people at hard points in their lives.",
    "startingSalary": "£23,000 - £28,000",
    "midSalary": "£28,000 - £35,000",
    "seniorSalary": "£38,000 - £48,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Community Drug and Alcohol",
        "shortDescription": "Keyworking adults in community treatment.",
        "typicalEntryRoute": "Care experience + NCFE diploma.",
        "salaryRange": "£24,000 - £32,000"
      },
      {
        "title": "Criminal Justice",
        "shortDescription": "Substance misuse work in prisons and probation.",
        "typicalEntryRoute": "Substance misuse + CJ experience.",
        "salaryRange": "£26,000 - £35,000"
      },
      {
        "title": "Young People's Services",
        "shortDescription": "Substance misuse work with under-25s.",
        "typicalEntryRoute": "Substance misuse + YP training.",
        "salaryRange": "£25,000 - £33,000"
      },
      {
        "title": "Recovery Worker",
        "shortDescription": "Peer-led and recovery-focused programmes.",
        "typicalEntryRoute": "Lived experience + training.",
        "salaryRange": "£23,000 - £30,000"
      }
    ],
    "skillsNeeded": [
      "Motivational interviewing",
      "Risk assessment",
      "Empathy",
      "Boundaries",
      "Reporting"
    ]
  },
  {
    "name": "Care Coordinator",
    "category": "Healthcare and Care",
    "shortDescription": "Organise care plans and rotas so vulnerable people get the right support at the right time.",
    "whyItFits": "You're organised, calm and want a meaningful behind-the-scenes role in the care sector.",
    "startingSalary": "£23,000 - £28,000",
    "midSalary": "£28,000 - £35,000",
    "seniorSalary": "£38,000 - £48,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Care office, hybrid",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Domiciliary Care",
        "shortDescription": "Plan home care visits and rotas.",
        "typicalEntryRoute": "Care background + Level 3.",
        "salaryRange": "£24,000 - £32,000"
      },
      {
        "title": "Hospital Discharge",
        "shortDescription": "Coordinate care for discharged patients.",
        "typicalEntryRoute": "Care + NHS background.",
        "salaryRange": "£26,000 - £35,000"
      },
      {
        "title": "Complex Care Coordinator",
        "shortDescription": "Coordinate care packages for complex clients.",
        "typicalEntryRoute": "Senior coordinator + Level 4.",
        "salaryRange": "£28,000 - £38,000"
      },
      {
        "title": "Primary Care Network Coordinator",
        "shortDescription": "Coordinate long-term conditions in GP networks.",
        "typicalEntryRoute": "Care + NHS experience.",
        "salaryRange": "£27,000 - £35,000"
      }
    ],
    "skillsNeeded": [
      "Scheduling",
      "Care planning",
      "Communication",
      "Safeguarding",
      "IT systems"
    ]
  },
  {
    "name": "Residential Support Worker",
    "category": "Healthcare and Care",
    "shortDescription": "Support children or adults in residential homes - daily living, learning, emotional needs.",
    "whyItFits": "You're patient, resilient and want frontline work with a long-term residential community.",
    "startingSalary": "£22,000 - £26,000",
    "midSalary": "£26,000 - £32,000",
    "seniorSalary": "£32,000 - £40,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Residential home, shift work",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Children's Homes",
        "shortDescription": "Support children in residential care homes.",
        "typicalEntryRoute": "Level 3 Children & Young People.",
        "salaryRange": "£22,000 - £30,000"
      },
      {
        "title": "Learning Disability Homes",
        "shortDescription": "Support adults with learning disabilities.",
        "typicalEntryRoute": "Level 2/3 + PBS training.",
        "salaryRange": "£22,000 - £28,000"
      },
      {
        "title": "Mental Health Recovery",
        "shortDescription": "Support adults in recovery housing.",
        "typicalEntryRoute": "Mental health + housing training.",
        "salaryRange": "£22,000 - £30,000"
      },
      {
        "title": "Therapeutic Communities",
        "shortDescription": "Work in therapeutic residential settings.",
        "typicalEntryRoute": "Senior support worker + therapeutic training.",
        "salaryRange": "£25,000 - £34,000"
      }
    ],
    "skillsNeeded": [
      "Empathy",
      "Boundaries",
      "Safeguarding",
      "Routine building",
      "Resilience"
    ]
  },
  {
    "name": "Health Visitor",
    "category": "Healthcare and Care",
    "shortDescription": "Specialist nurse working with families with babies and young children in the community.",
    "whyItFits": "You're an experienced nurse or midwife who wants community-based preventive work.",
    "startingSalary": "£35,392 (NHS Band 6)",
    "midSalary": "£43,742 - £50,056",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "1 year post-qualification (specialist training)",
    "degreeRequired": "Nursing/midwifery + SCPHN",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Universal Service",
        "shortDescription": "Routine reviews for under-5s.",
        "typicalEntryRoute": "BSc Nursing + PG Dip SCPHN.",
        "salaryRange": "£37,000 - £48,000"
      },
      {
        "title": "Targeted Family Support",
        "shortDescription": "Intensive work with vulnerable families.",
        "typicalEntryRoute": "Health visitor + safeguarding training.",
        "salaryRange": "£37,000 - £52,000"
      },
      {
        "title": "Infant Feeding Lead",
        "shortDescription": "Lead infant feeding strategy across teams.",
        "typicalEntryRoute": "Health visitor + IBCLC.",
        "salaryRange": "£40,000 - £55,000"
      },
      {
        "title": "Perinatal Mental Health",
        "shortDescription": "Specialist support for mums with PMH issues.",
        "typicalEntryRoute": "Health visitor + PMH training.",
        "salaryRange": "£40,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Child development",
      "Family assessment",
      "Safeguarding",
      "Comms",
      "Lone working"
    ]
  },
  {
    "name": "Speech Therapy Assistant",
    "category": "Healthcare and Care",
    "shortDescription": "Support speech and language therapists running sessions with children and adults.",
    "whyItFits": "You want a way into speech and language work without a full degree route yet.",
    "startingSalary": "£22,000 - £26,000",
    "midSalary": "£26,000 - £30,000",
    "seniorSalary": "£30,000 - £36,000",
    "timeToEntry": "0 - 12 months",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Paediatric SLT Support",
        "shortDescription": "Support SLTs working with children in clinics and schools.",
        "typicalEntryRoute": "Care or assistant background + provider training.",
        "salaryRange": "£22,000 - £28,000"
      },
      {
        "title": "Adult SLT Support",
        "shortDescription": "Support adults after stroke or with degenerative conditions.",
        "typicalEntryRoute": "Care + training.",
        "salaryRange": "£22,000 - £28,000"
      },
      {
        "title": "Specialist School Support",
        "shortDescription": "Embedded support in special schools.",
        "typicalEntryRoute": "TA + SLT support training.",
        "salaryRange": "£22,000 - £28,000"
      },
      {
        "title": "Bilingual SLT Assistant",
        "shortDescription": "Support SLT services in additional languages.",
        "typicalEntryRoute": "Bilingual + assistant training.",
        "salaryRange": "£23,000 - £30,000"
      }
    ],
    "skillsNeeded": [
      "Communication",
      "Patience",
      "Working with children",
      "Recording",
      "Teamwork"
    ]
  },
  {
    "name": "Occupational Therapy Assistant",
    "category": "Healthcare and Care",
    "shortDescription": "Support OTs helping people regain skills for daily life after illness, injury or disability.",
    "whyItFits": "You want a hands-on care role with a clear path into OT degrees later.",
    "startingSalary": "£22,000 - £26,000",
    "midSalary": "£26,000 - £30,000",
    "seniorSalary": "£30,000 - £36,000",
    "timeToEntry": "0 - 12 months",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "Moderate",
    "workEnvironment": "Hospital, clinic or community setting",
    "stability": "Very strong - long-term NHS and private demand",
    "weights": {
      "people": 3,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [
      {
        "title": "Hospital OT Support",
        "shortDescription": "Support OTs with assessments and rehab on wards.",
        "typicalEntryRoute": "Care background + provider training.",
        "salaryRange": "£22,000 - £28,000"
      },
      {
        "title": "Community OT Support",
        "shortDescription": "Home visits and adaptations alongside OTs.",
        "typicalEntryRoute": "Care + driving licence.",
        "salaryRange": "£22,000 - £28,000"
      },
      {
        "title": "Mental Health OT Support",
        "shortDescription": "Support OTs in mental health teams.",
        "typicalEntryRoute": "Care + MH experience.",
        "salaryRange": "£22,000 - £29,000"
      },
      {
        "title": "Paediatric OT Support",
        "shortDescription": "Support paediatric OTs in clinics and schools.",
        "typicalEntryRoute": "Care + paediatric experience.",
        "salaryRange": "£22,000 - £29,000"
      }
    ],
    "skillsNeeded": [
      "Patience",
      "Practical skills",
      "Recording",
      "Communication",
      "Empathy"
    ]
  },
  {
    "name": "Environmental Health Officer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Inspect food, housing and workplaces to make sure they meet health and safety law.",
    "whyItFits": "You're principled, observant and like a regulated role with clear authority.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £50,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "3 - 4 years (BSc + training)",
    "degreeRequired": "Required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed - office, site visits",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Food Safety",
        "shortDescription": "Inspect and rate food businesses.",
        "typicalEntryRoute": "BSc Environmental Health + CIEH.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Housing Standards",
        "shortDescription": "Inspect private rented housing under HHSRS.",
        "typicalEntryRoute": "EHO + housing training.",
        "salaryRange": "£30,000 - £48,000"
      },
      {
        "title": "Health and Safety",
        "shortDescription": "Workplace inspections and accident investigation.",
        "typicalEntryRoute": "EHO + H&S training.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Public Health Protection",
        "shortDescription": "Communicable disease and outbreak response.",
        "typicalEntryRoute": "EHO + public health training.",
        "salaryRange": "£35,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Regulations",
      "Report writing",
      "Inspection",
      "Communication",
      "Evidence handling"
    ]
  },
  {
    "name": "Ecologist",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Survey habitats and species to inform planning, conservation and development decisions.",
    "whyItFits": "You love wildlife, the outdoors and using science to protect natural spaces.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £70,000",
    "timeToEntry": "3 - 4 years (BSc)",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Outdoors in season, office out of season",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Protected Species Surveys",
        "shortDescription": "Bat, newt and dormouse surveys for planning.",
        "typicalEntryRoute": "BSc Ecology + CIEEM + licences.",
        "salaryRange": "£26,000 - £42,000"
      },
      {
        "title": "Habitat Assessment",
        "shortDescription": "UKHab and Phase 1 habitat surveys.",
        "typicalEntryRoute": "Ecologist + CIEEM.",
        "salaryRange": "£26,000 - £42,000"
      },
      {
        "title": "Biodiversity Net Gain",
        "shortDescription": "Calculate and design BNG strategies under new planning rules.",
        "typicalEntryRoute": "Ecologist + BNG training.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Marine Ecology",
        "shortDescription": "Subtidal and intertidal surveys for offshore projects.",
        "typicalEntryRoute": "Marine biology background.",
        "salaryRange": "£30,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Species ID",
      "Survey methods",
      "GIS",
      "Report writing",
      "Driving licence"
    ]
  },
  {
    "name": "Arboriculturist",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Manage trees - inspection, surveys, planting and tree-related planning advice.",
    "whyItFits": "You like outdoor specialist work that mixes science, planning and practical skills.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £42,000",
    "seniorSalary": "£45,000 - £60,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Outdoors, all weathers",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Tree Surveys for Development",
        "shortDescription": "BS5837 surveys for planning applications.",
        "typicalEntryRoute": "ABC Level 4 + survey experience.",
        "salaryRange": "£28,000 - £48,000"
      },
      {
        "title": "Tree Risk Management",
        "shortDescription": "Inspect trees for risk on highways and estates.",
        "typicalEntryRoute": "QTRA / VALID training.",
        "salaryRange": "£30,000 - £50,000"
      },
      {
        "title": "Local Authority Arb Officer",
        "shortDescription": "Tree officer role in council planning.",
        "typicalEntryRoute": "ABC Level 4 + planning experience.",
        "salaryRange": "£32,000 - £48,000"
      },
      {
        "title": "Consultancy and Expert Witness",
        "shortDescription": "Independent reports and expert evidence.",
        "typicalEntryRoute": "Senior arb + expert witness training.",
        "salaryRange": "£45,000 - £80,000"
      }
    ],
    "skillsNeeded": [
      "Tree biology",
      "Surveying",
      "Risk assessment",
      "Reporting",
      "Driving licence"
    ],
    "selfEmployable": true
  },
  {
    "name": "Recycling Manager",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Run recycling and waste reduction programmes for councils, contractors or large businesses.",
    "whyItFits": "You're operational, planet-minded and like work where impact is measurable.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £55,000",
    "seniorSalary": "£60,000 - £80,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Local Authority Recycling",
        "shortDescription": "Manage kerbside collections and HWRC sites.",
        "typicalEntryRoute": "Waste management background.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "MRF Operations",
        "shortDescription": "Run a materials recovery facility.",
        "typicalEntryRoute": "Operations + WAMITAB CoTC.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "Commercial Recycling",
        "shortDescription": "Manage business waste contracts for a waste co.",
        "typicalEntryRoute": "Commercial + waste background.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Producer Compliance",
        "shortDescription": "EPR and packaging waste compliance for retailers.",
        "typicalEntryRoute": "Compliance + waste background.",
        "salaryRange": "£40,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "Operations",
      "Compliance",
      "Reporting",
      "Stakeholder mgmt",
      "Continuous improvement"
    ]
  },
  {
    "name": "Waste Management Officer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Plan and oversee waste collection, recycling and disposal for a local area or business.",
    "whyItFits": "You like operational work with environmental purpose and steady council-style hours.",
    "startingSalary": "£25,000 - £32,000",
    "midSalary": "£35,000 - £48,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Local Authority Officer",
        "shortDescription": "Manage council waste services and contracts.",
        "typicalEntryRoute": "Waste background + Level 4 cert.",
        "salaryRange": "£30,000 - £48,000"
      },
      {
        "title": "Hazardous Waste",
        "shortDescription": "Permitting and movement of hazardous waste.",
        "typicalEntryRoute": "Hazardous waste training + COTC.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Construction Waste",
        "shortDescription": "Site waste plans on major projects.",
        "typicalEntryRoute": "Waste + construction background.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Healthcare Waste",
        "shortDescription": "Clinical waste management for NHS estates.",
        "typicalEntryRoute": "Waste + healthcare background.",
        "salaryRange": "£32,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Regulations",
      "Operations",
      "Reporting",
      "Stakeholder mgmt",
      "Data"
    ]
  },
  {
    "name": "Water Quality Scientist",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Test and monitor water quality across rivers, treatment works and industrial sites.",
    "whyItFits": "You like applied science with strong public-health and environmental relevance.",
    "startingSalary": "£26,000 - £32,000",
    "midSalary": "£34,000 - £48,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "3 - 4 years (BSc)",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Lab + field",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Drinking Water",
        "shortDescription": "Sample and test drinking water for water companies.",
        "typicalEntryRoute": "BSc + lab experience.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "Wastewater",
        "shortDescription": "Monitor wastewater treatment plant compliance.",
        "typicalEntryRoute": "BSc + wastewater experience.",
        "salaryRange": "£28,000 - £42,000"
      },
      {
        "title": "River and Catchment",
        "shortDescription": "Monitor river quality for EA and water companies.",
        "typicalEntryRoute": "Environmental science background.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Bathing Waters",
        "shortDescription": "Coastal and bathing water monitoring.",
        "typicalEntryRoute": "Environmental science + sampling.",
        "salaryRange": "£28,000 - £42,000"
      }
    ],
    "skillsNeeded": [
      "Sampling",
      "Lab analysis",
      "Reporting",
      "Regulations",
      "Driving licence"
    ]
  },
  {
    "name": "Hydrologist",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Study water flow, flooding and water resources to support engineering and environmental decisions.",
    "whyItFits": "You like maths, modelling and applied environmental science.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £52,000",
    "seniorSalary": "£58,000 - £80,000",
    "timeToEntry": "3 - 5 years (BSc/MSc)",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Flood Risk Modelling",
        "shortDescription": "1D/2D hydraulic models for flood risk assessment.",
        "typicalEntryRoute": "MSc Hydrology + modelling software.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Water Resources",
        "shortDescription": "Catchment yield and abstraction studies.",
        "typicalEntryRoute": "MSc + water industry experience.",
        "salaryRange": "£35,000 - £58,000"
      },
      {
        "title": "Urban Drainage",
        "shortDescription": "SuDS and urban drainage modelling.",
        "typicalEntryRoute": "Hydrology + civil engineering background.",
        "salaryRange": "£35,000 - £58,000"
      },
      {
        "title": "River Restoration",
        "shortDescription": "Design river restoration schemes.",
        "typicalEntryRoute": "Hydrology + ecology background.",
        "salaryRange": "£32,000 - £52,000"
      }
    ],
    "skillsNeeded": [
      "Modelling",
      "GIS",
      "Stats",
      "Reporting",
      "Field work"
    ]
  },
  {
    "name": "Flood Risk Consultant",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Assess flood risk for planning applications and design schemes to manage it.",
    "whyItFits": "You enjoy applied environmental engineering with rising demand from climate change.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£40,000 - £55,000",
    "seniorSalary": "£60,000 - £85,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Flood Risk Assessment",
        "shortDescription": "FRAs for planning applications.",
        "typicalEntryRoute": "MSc + EA experience or training.",
        "salaryRange": "£32,000 - £52,000"
      },
      {
        "title": "Flood Modelling",
        "shortDescription": "Detailed hydraulic modelling for major schemes.",
        "typicalEntryRoute": "Modelling software + senior experience.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Coastal Flood Risk",
        "shortDescription": "Specialist coastal and tidal flood work.",
        "typicalEntryRoute": "Coastal engineering background.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Surface Water Strategies",
        "shortDescription": "LLFA surface water management plans.",
        "typicalEntryRoute": "Drainage + planning background.",
        "salaryRange": "£35,000 - £58,000"
      }
    ],
    "skillsNeeded": [
      "Hydraulic modelling",
      "GIS",
      "Planning policy",
      "Report writing",
      "Client comms"
    ]
  },
  {
    "name": "Environmental Planner",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Make sure development projects meet environmental policy and protect natural and built environments.",
    "whyItFits": "You like work where policy, ecology and planning intersect.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £55,000",
    "seniorSalary": "£60,000 - £85,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "EIA Coordination",
        "shortDescription": "Coordinate Environmental Impact Assessments.",
        "typicalEntryRoute": "MSc + RTPI / IEMA membership.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Renewables Planning",
        "shortDescription": "Planning consents for wind and solar farms.",
        "typicalEntryRoute": "Planner + renewables experience.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "Infrastructure DCO",
        "shortDescription": "NSIP DCO submissions for major infrastructure.",
        "typicalEntryRoute": "Senior planner + DCO experience.",
        "salaryRange": "£45,000 - £80,000"
      },
      {
        "title": "Local Authority Planning",
        "shortDescription": "Environmental planning in council teams.",
        "typicalEntryRoute": "RTPI + LA experience.",
        "salaryRange": "£32,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Planning law",
      "EIA",
      "Stakeholder mgmt",
      "Report writing",
      "Negotiation"
    ]
  },
  {
    "name": "Town Planner",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Shape how towns and cities grow - balancing development, infrastructure and community needs.",
    "whyItFits": "You like long-term work where decisions affect places for decades.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£40,000 - £55,000",
    "seniorSalary": "£60,000 - £90,000",
    "timeToEntry": "3 - 5 years (RTPI-accredited route)",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Development Management",
        "shortDescription": "Process planning applications in a council.",
        "typicalEntryRoute": "RTPI accredited degree + experience.",
        "salaryRange": "£30,000 - £55,000"
      },
      {
        "title": "Planning Policy",
        "shortDescription": "Write local plans and supplementary guidance.",
        "typicalEntryRoute": "RTPI + policy experience.",
        "salaryRange": "£32,000 - £58,000"
      },
      {
        "title": "Private Planning Consultancy",
        "shortDescription": "Run applications and appeals for developers.",
        "typicalEntryRoute": "RTPI + consultancy experience.",
        "salaryRange": "£35,000 - £75,000"
      },
      {
        "title": "Heritage Planning",
        "shortDescription": "Listed-building and conservation-area work.",
        "typicalEntryRoute": "Planner + heritage training.",
        "salaryRange": "£35,000 - £60,000"
      }
    ],
    "skillsNeeded": [
      "Planning policy",
      "Report writing",
      "Community engagement",
      "Negotiation",
      "GIS"
    ]
  },
  {
    "name": "Transport Sustainability Planner",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Plan low-carbon transport networks - active travel, EV infrastructure, public transport.",
    "whyItFits": "You want a planning role tied to climate goals and changing how we move.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£40,000 - £55,000",
    "seniorSalary": "£60,000 - £85,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Active Travel Schemes",
        "shortDescription": "Design walking and cycling schemes.",
        "typicalEntryRoute": "Transport planning + LTN 1/20 training.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Decarbonisation Strategy",
        "shortDescription": "Local transport decarbonisation plans.",
        "typicalEntryRoute": "Transport planning + sustainability background.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Bus and Rapid Transit",
        "shortDescription": "Bus priority and BRT scheme planning.",
        "typicalEntryRoute": "Transport planning + modelling.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Travel Demand Management",
        "shortDescription": "Modal shift programmes for employers and cities.",
        "typicalEntryRoute": "Transport planning + behaviour change background.",
        "salaryRange": "£32,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Transport modelling",
      "Policy",
      "Community engagement",
      "GIS",
      "Report writing"
    ]
  },
  {
    "name": "EV Charger Installer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Install home and commercial electric vehicle charging points across the UK.",
    "whyItFits": "You want a fast-growing electrical specialism tied to the EV transition.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £55,000",
    "seniorSalary": "£60,000 - £80,000",
    "timeToEntry": "6 - 18 months for qualified sparks",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Client homes and businesses",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Home EV Chargers",
        "shortDescription": "Install 7kW chargers in homes under OZEV/EVHS.",
        "typicalEntryRoute": "Electrician + OZEV-approved training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Commercial Charging",
        "shortDescription": "Install workplace and destination chargers.",
        "typicalEntryRoute": "Electrician + commercial EV training.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Smart Charging and V2G",
        "shortDescription": "Install smart load-managed and V2G systems.",
        "typicalEntryRoute": "Senior installer + manufacturer training.",
        "salaryRange": "£45,000 - £70,000"
      },
      {
        "title": "Maintenance and Fault-finding",
        "shortDescription": "Service charging estates under SLAs.",
        "typicalEntryRoute": "Senior electrician + EV fault training.",
        "salaryRange": "£40,000 - £62,000"
      },
      {
        "title": "OZEV-Compliant Installations",
        "shortDescription": "OZEV-grant compliant installs with DNO notification.",
        "typicalEntryRoute": "OZEV-approved installer.",
        "salaryRange": "£35,000 - £60,000"
      }
    ],
    "skillsNeeded": [
      "Electrical installation",
      "OZEV scheme",
      "Customer comms",
      "Driving licence",
      "Health & safety"
    ],
    "selfEmployable": true
  },
  {
    "name": "Battery Engineer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Design, test and integrate batteries for EVs, grid storage and industrial use.",
    "whyItFits": "You're an engineer who wants to work on a defining technology of the decade.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£85,000 - £140,000",
    "timeToEntry": "3 - 5 years (degree + experience)",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Cell Engineering",
        "shortDescription": "Design and test Li-ion and next-gen cells.",
        "typicalEntryRoute": "MSc/PhD + cell test experience.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Battery Pack Design",
        "shortDescription": "Design EV and storage packs and BMS.",
        "typicalEntryRoute": "Electrical / mechanical engineering background.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "Grid-scale BESS",
        "shortDescription": "Engineer container-scale battery storage systems.",
        "typicalEntryRoute": "Power engineering + BESS experience.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "Battery Safety and Compliance",
        "shortDescription": "Safety, abuse testing and regulatory compliance.",
        "typicalEntryRoute": "Engineering + safety background.",
        "salaryRange": "£45,000 - £85,000"
      }
    ],
    "skillsNeeded": [
      "Electrochemistry",
      "Testing",
      "CAD",
      "Safety",
      "Data analysis"
    ]
  },
  {
    "name": "Hydrogen Engineer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Design and build hydrogen production, storage and end-use systems for industry and transport.",
    "whyItFits": "You want exposure to a clean-energy pillar still being built from scratch in the UK.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £140,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Electrolyser Engineering",
        "shortDescription": "Design and commission electrolyser systems.",
        "typicalEntryRoute": "MEng + hydrogen training.",
        "salaryRange": "£55,000 - £90,000"
      },
      {
        "title": "Hydrogen Storage",
        "shortDescription": "Compression, liquefaction and storage design.",
        "typicalEntryRoute": "Chemical / mechanical engineering background.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "Hydrogen for Heat",
        "shortDescription": "Hydrogen-ready boilers and district trials.",
        "typicalEntryRoute": "Gas engineering + hydrogen training.",
        "salaryRange": "£45,000 - £80,000"
      },
      {
        "title": "Industrial Hydrogen",
        "shortDescription": "Decarbonise industrial processes with hydrogen.",
        "typicalEntryRoute": "Process engineering + hydrogen background.",
        "salaryRange": "£60,000 - £100,000"
      }
    ],
    "skillsNeeded": [
      "Process engineering",
      "Safety case",
      "Materials",
      "Modelling",
      "Project work"
    ]
  },
  {
    "name": "Grid Connections Engineer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Manage how new energy projects (solar, wind, battery) connect to the electricity grid.",
    "whyItFits": "You like engineering with high commercial stakes and big project timelines.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £130,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Distribution Connections",
        "shortDescription": "Manage DNO connections for renewables and BESS.",
        "typicalEntryRoute": "Power engineering background.",
        "salaryRange": "£45,000 - £80,000"
      },
      {
        "title": "Transmission Connections",
        "shortDescription": "NGESO transmission connection applications.",
        "typicalEntryRoute": "Senior power engineer.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Grid Code Compliance",
        "shortDescription": "Compliance testing and energisation.",
        "typicalEntryRoute": "Power systems engineering.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Connection Strategy",
        "shortDescription": "Optimise sites and contestable works.",
        "typicalEntryRoute": "Senior connections + commercial background.",
        "salaryRange": "£60,000 - £110,000"
      }
    ],
    "skillsNeeded": [
      "Power systems",
      "DNO process",
      "Contracts",
      "Project management",
      "Stakeholder mgmt"
    ]
  },
  {
    "name": "Energy Storage Specialist",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Design and operate battery storage projects that balance the grid as renewables grow.",
    "whyItFits": "You want a specialism right at the centre of the UK's net-zero transition.",
    "startingSalary": "£35,000 - £50,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £140,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Co-located Solar + Storage",
        "shortDescription": "Design co-located solar and battery sites.",
        "typicalEntryRoute": "Renewables + BESS background.",
        "salaryRange": "£50,000 - £90,000"
      },
      {
        "title": "Standalone BESS",
        "shortDescription": "Develop standalone grid-connected battery sites.",
        "typicalEntryRoute": "Renewables development background.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "Behind-the-meter Storage",
        "shortDescription": "Industrial and commercial storage projects.",
        "typicalEntryRoute": "Power engineering + commercial background.",
        "salaryRange": "£50,000 - £90,000"
      },
      {
        "title": "Storage Trading and Optimisation",
        "shortDescription": "Trade and optimise BESS revenue stacks.",
        "typicalEntryRoute": "Energy trading + storage knowledge.",
        "salaryRange": "£60,000 - £130,000+"
      }
    ],
    "skillsNeeded": [
      "Power markets",
      "Battery tech",
      "Modelling",
      "Project work",
      "Commercial sense"
    ]
  },
  {
    "name": "Smart Grid Engineer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Build the digital, sensor-driven grid that handles distributed renewables, EVs and storage.",
    "whyItFits": "You're an electrical engineer who loves software and data as much as hardware.",
    "startingSalary": "£35,000 - £48,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £140,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "ADMS / DERMS",
        "shortDescription": "Implement distribution management systems.",
        "typicalEntryRoute": "Power systems engineering.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Substation Automation",
        "shortDescription": "Modernise substations with IEC 61850.",
        "typicalEntryRoute": "Protection & control engineering.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Flexibility Services",
        "shortDescription": "Engineer DSO flexibility platforms and tenders.",
        "typicalEntryRoute": "Smart grid + market knowledge.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "LV Network Visibility",
        "shortDescription": "Smart meter and LV monitoring solutions.",
        "typicalEntryRoute": "Power engineering + data background.",
        "salaryRange": "£50,000 - £90,000"
      }
    ],
    "skillsNeeded": [
      "Power systems",
      "Comms protocols",
      "Data",
      "Cyber basics",
      "Modelling"
    ]
  },
  {
    "name": "Low Carbon Heating Engineer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Install heat pumps and low-carbon heating systems in homes and small commercial sites.",
    "whyItFits": "You're a heating engineer who wants in on the long retrofit programme ahead.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £55,000",
    "seniorSalary": "£60,000 - £85,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Customer homes, on-site",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Air Source Heat Pumps",
        "shortDescription": "Design and install ASHP under MCS.",
        "typicalEntryRoute": "Gas Safe + MCS heat pump training.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Ground Source Heat Pumps",
        "shortDescription": "GSHP design including loops and boreholes.",
        "typicalEntryRoute": "Senior heat pump engineer.",
        "salaryRange": "£40,000 - £65,000"
      },
      {
        "title": "Hybrid Systems",
        "shortDescription": "Hybrid heat pump + boiler systems.",
        "typicalEntryRoute": "Gas Safe + heat pump training.",
        "salaryRange": "£38,000 - £60,000"
      },
      {
        "title": "Heat Networks",
        "shortDescription": "Communal and district heat network installs.",
        "typicalEntryRoute": "Heating engineer + CIBSE training.",
        "salaryRange": "£42,000 - £68,000"
      }
    ],
    "skillsNeeded": [
      "MCS standards",
      "Heat loss calcs",
      "Plumbing",
      "Customer comms",
      "Driving licence"
    ],
    "selfEmployable": true
  },
  {
    "name": "Building Services Engineer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Design the heating, lighting, water and ventilation systems that make buildings work.",
    "whyItFits": "You like engineering with a tangible everyday output you can point at.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£42,000 - £58,000",
    "seniorSalary": "£65,000 - £95,000",
    "timeToEntry": "3 - 5 years",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Mechanical Services",
        "shortDescription": "Heating, cooling and ventilation design.",
        "typicalEntryRoute": "BEng + CIBSE/IMechE route.",
        "salaryRange": "£32,000 - £58,000"
      },
      {
        "title": "Electrical Services",
        "shortDescription": "Power, lighting and small power design.",
        "typicalEntryRoute": "BEng + IET route.",
        "salaryRange": "£32,000 - £58,000"
      },
      {
        "title": "Public Health",
        "shortDescription": "Water and drainage services in buildings.",
        "typicalEntryRoute": "BEng + CIBSE training.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "Sustainability and Net Zero",
        "shortDescription": "Net zero strategies for new and existing buildings.",
        "typicalEntryRoute": "Building services + sustainability training.",
        "salaryRange": "£40,000 - £70,000"
      }
    ],
    "skillsNeeded": [
      "CAD",
      "Energy modelling",
      "Regulations",
      "Coordination",
      "Communication"
    ]
  },
  {
    "name": "BREEAM Assessor",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Assess and certify the environmental performance of buildings against the BREEAM standard.",
    "whyItFits": "You want a sustainability specialism with clear demand from new-build construction.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£38,000 - £52,000",
    "seniorSalary": "£55,000 - £75,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "New Construction Assessor",
        "shortDescription": "Assess new builds against BREEAM NC.",
        "typicalEntryRoute": "BRE-licensed assessor course.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Refurbishment and Fit-out",
        "shortDescription": "Assess RFO projects.",
        "typicalEntryRoute": "BREEAM RFO licence.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "In-Use Assessor",
        "shortDescription": "Assess operational performance of existing buildings.",
        "typicalEntryRoute": "BREEAM In-Use licence.",
        "salaryRange": "£35,000 - £55,000"
      },
      {
        "title": "Communities Assessor",
        "shortDescription": "Master-planning scale BREEAM assessments.",
        "typicalEntryRoute": "Senior assessor + planning background.",
        "salaryRange": "£42,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "BREEAM methodology",
      "Building services",
      "Reporting",
      "Coordination",
      "Attention to detail"
    ],
    "selfEmployable": true
  },
  {
    "name": "Circular Economy Consultant",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Help businesses redesign products, materials and supply chains around reuse and recycling.",
    "whyItFits": "You're systems-minded and want sustainability work tied to product and operations.",
    "startingSalary": "£32,000 - £42,000",
    "midSalary": "£50,000 - £70,000",
    "seniorSalary": "£80,000 - £120,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Product Circularity",
        "shortDescription": "Help manufacturers redesign products for circularity.",
        "typicalEntryRoute": "Sustainability background + LCA tools.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "Construction Circularity",
        "shortDescription": "Material passports and reuse strategies for buildings.",
        "typicalEntryRoute": "Sustainability + construction background.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "EPR and Take-back Schemes",
        "shortDescription": "Design extended producer responsibility schemes.",
        "typicalEntryRoute": "Compliance + sustainability background.",
        "salaryRange": "£42,000 - £75,000"
      },
      {
        "title": "Circular Strategy",
        "shortDescription": "Corporate circular economy strategy advisory.",
        "typicalEntryRoute": "Senior sustainability consulting.",
        "salaryRange": "£60,000 - £110,000"
      }
    ],
    "skillsNeeded": [
      "Lifecycle assessment",
      "Materials",
      "Strategy",
      "Stakeholder mgmt",
      "Reporting"
    ],
    "selfEmployable": true
  },
  {
    "name": "Sustainable Packaging Specialist",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Redesign packaging to cut plastic and carbon while meeting cost and regulation requirements.",
    "whyItFits": "You like applied sustainability work with clear, visible product outcomes.",
    "startingSalary": "£30,000 - £42,000",
    "midSalary": "£45,000 - £65,000",
    "seniorSalary": "£75,000 - £105,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Packaging Redesign",
        "shortDescription": "Redesign packs for recyclability and weight.",
        "typicalEntryRoute": "Packaging tech background.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "EPR Compliance",
        "shortDescription": "Packaging EPR data and reporting.",
        "typicalEntryRoute": "Compliance background.",
        "salaryRange": "£35,000 - £58,000"
      },
      {
        "title": "LCA for Packaging",
        "shortDescription": "Lifecycle analysis of packaging options.",
        "typicalEntryRoute": "Sustainability + LCA tools.",
        "salaryRange": "£38,000 - £62,000"
      },
      {
        "title": "Reusable Systems",
        "shortDescription": "Design reuse and refill systems for retailers.",
        "typicalEntryRoute": "Packaging + circular economy background.",
        "salaryRange": "£38,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "Materials science",
      "Regulations",
      "Supplier mgmt",
      "Lifecycle thinking",
      "Design sense"
    ]
  },
  {
    "name": "Air Quality Consultant",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Model and assess air pollution impacts for planning, transport and industrial projects.",
    "whyItFits": "You like science-led environmental consulting with growing regulatory demand.",
    "startingSalary": "£26,000 - £34,000",
    "midSalary": "£36,000 - £52,000",
    "seniorSalary": "£60,000 - £85,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Planning Air Quality",
        "shortDescription": "Air quality assessments for planning applications.",
        "typicalEntryRoute": "Environmental science + ADMS training.",
        "salaryRange": "£28,000 - £48,000"
      },
      {
        "title": "Construction Dust",
        "shortDescription": "Construction dust assessments and monitoring.",
        "typicalEntryRoute": "Air quality + IAQM training.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Indoor Air Quality",
        "shortDescription": "IAQ assessments and remediation for buildings.",
        "typicalEntryRoute": "Air quality + buildings background.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Industrial Permitting",
        "shortDescription": "Air emissions modelling for IED permits.",
        "typicalEntryRoute": "Senior air quality consultant.",
        "salaryRange": "£40,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "Dispersion modelling",
      "GIS",
      "Regulations",
      "Field measurement",
      "Reporting"
    ]
  },
  {
    "name": "Noise Consultant",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Measure and model noise from buildings, infrastructure and industry for planning approvals.",
    "whyItFits": "You like quantitative environmental work with site visits and clear deliverables.",
    "startingSalary": "£26,000 - £34,000",
    "midSalary": "£35,000 - £50,000",
    "seniorSalary": "£58,000 - £80,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Planning Noise",
        "shortDescription": "Noise assessments for residential planning.",
        "typicalEntryRoute": "Acoustics MSc + IOA Diploma.",
        "salaryRange": "£28,000 - £48,000"
      },
      {
        "title": "Building Acoustics",
        "shortDescription": "Pre-completion testing under Part E.",
        "typicalEntryRoute": "IOA + testing experience.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Industrial Noise",
        "shortDescription": "BS4142 assessments for industry.",
        "typicalEntryRoute": "IOA + senior experience.",
        "salaryRange": "£35,000 - £58,000"
      },
      {
        "title": "Environmental Noise Mapping",
        "shortDescription": "Strategic noise mapping for cities and roads.",
        "typicalEntryRoute": "IOA + modelling software.",
        "salaryRange": "£35,000 - £58,000"
      }
    ],
    "skillsNeeded": [
      "Acoustics",
      "Modelling software",
      "Regulations",
      "Field measurement",
      "Reporting"
    ]
  },
  {
    "name": "Land Surveyor",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Use GPS, drones and total stations to measure land for construction and infrastructure projects.",
    "whyItFits": "You like precise outdoor work and the mix of fieldwork, tech and CAD.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£36,000 - £52,000",
    "seniorSalary": "£60,000 - £85,000",
    "timeToEntry": "1 - 4 years",
    "degreeRequired": "Often helpful",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Field + office",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Topographic Survey",
        "shortDescription": "Detailed topo surveys for design teams.",
        "typicalEntryRoute": "BSc Surveying or apprenticeship.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Setting Out",
        "shortDescription": "Setting out on construction sites.",
        "typicalEntryRoute": "Surveyor + site experience.",
        "salaryRange": "£32,000 - £55,000"
      },
      {
        "title": "Laser Scanning",
        "shortDescription": "3D laser scan and BIM deliverables.",
        "typicalEntryRoute": "Surveyor + Leica/Faro training.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "Measured Building Survey",
        "shortDescription": "Detailed surveys of existing buildings.",
        "typicalEntryRoute": "Surveyor + heritage / arch background.",
        "salaryRange": "£32,000 - £55,000"
      }
    ],
    "skillsNeeded": [
      "Total station / GPS",
      "CAD",
      "Drone surveying",
      "Reporting",
      "Driving licence"
    ],
    "selfEmployable": true
  },
  {
    "name": "Geotechnical Engineer",
    "category": "Green Energy and Sustainability",
    "shortDescription": "Investigate ground conditions and design foundations, slopes and retaining structures.",
    "whyItFits": "You like applied engineering with a strong mix of field, lab and design work.",
    "startingSalary": "£28,000 - £36,000",
    "midSalary": "£42,000 - £58,000",
    "seniorSalary": "£65,000 - £95,000",
    "timeToEntry": "3 - 5 years (BSc/MSc)",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Mixed site / office / field work",
    "stability": "Growing long-term demand from net-zero transition",
    "weights": {
      "structure": 2,
      "income": 1,
      "creativity": 1,
      "freedom": 0,
      "people": 0
    },
    "specialisations": [
      {
        "title": "Ground Investigation",
        "shortDescription": "Manage SI campaigns and AGS data.",
        "typicalEntryRoute": "BEng + ground investigation experience.",
        "salaryRange": "£30,000 - £52,000"
      },
      {
        "title": "Foundation Design",
        "shortDescription": "Design pile, raft and shallow foundations.",
        "typicalEntryRoute": "BEng + ICE Chartership route.",
        "salaryRange": "£35,000 - £65,000"
      },
      {
        "title": "Slope Stability",
        "shortDescription": "Stability assessment for highways and rail.",
        "typicalEntryRoute": "Geotechnical engineer + senior experience.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "Contaminated Land",
        "shortDescription": "Combined geotechnical / contaminated land role.",
        "typicalEntryRoute": "Geotech + environmental training.",
        "salaryRange": "£35,000 - £62,000"
      }
    ],
    "skillsNeeded": [
      "Soil mechanics",
      "Site investigation",
      "CAD",
      "Reporting",
      "Driving licence"
    ]
  },
  {
    "name": "Content Strategist",
    "category": "Marketing and Media",
    "shortDescription": "Plan what content a brand should publish, why and how it ties to business goals.",
    "whyItFits": "You like the editorial side of marketing but want it tied tightly to commercial outcomes.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£85,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "structure": 2,
      "income": 1,
      "freedom": 2,
      "people": 1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Editorial judgement",
      "SEO",
      "Brand voice",
      "Analytics",
      "Stakeholder mgmt"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£300 - £1,000 (site, subscriptions, basic kit)",
      "timeToFirstClient": "4 - 12 weeks",
      "monthOneIncome": "£0 - £2,500",
      "yearOneIncome": "£35,000 - £85,000 full-time",
      "scaledIncome": "£100,000 - £180,000 with retainers and small team",
      "commonPitfalls": [
        "Selling 'content' instead of a business outcome",
        "Underpricing one-off projects",
        "No measurement built into the work",
        "Taking any client - dilutes your portfolio"
      ],
      "firstFiveClients": [
        "Old employer or marketing contacts",
        "LinkedIn outbound to founders/heads of marketing in one niche",
        "Audit a niche brand free + share publicly",
        "Partner with a design or SEO agency",
        "Speak at one industry meetup with a case study"
      ],
      "toolsAndCosts": [
        "Ahrefs/Semrush £80-£200/mo",
        "Notion £8/mo",
        "Loom £12/mo",
        "Google Workspace £6/mo",
        "Figma £12/mo"
      ],
      "legalBasics": [
        "Sole trader UTR or Ltd",
        "Professional indemnity insurance",
        "Master services agreement template",
        "Quarterly tax savings"
      ]
    }
  },
  {
    "name": "Social Media Producer",
    "category": "Marketing and Media",
    "shortDescription": "Plan, shoot and edit social-first content as a hands-on producer, not just a strategist.",
    "whyItFits": "You're scrappy, creative and like owning the full creative pipeline.",
    "startingSalary": "£25,000 - £35,000",
    "midSalary": "£40,000 - £60,000",
    "seniorSalary": "£65,000 - £95,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Shooting and editing",
      "Platform trends",
      "Copywriting",
      "Project mgmt",
      "Talent direction"
    ],
    "selfEmployable": true
  },
  {
    "name": "YouTube Strategist",
    "category": "Marketing and Media",
    "shortDescription": "Help creators and brands grow channels - thumbnails, titles, packaging, content strategy.",
    "whyItFits": "You're obsessed with how YouTube works and like the mix of art and analytics.",
    "startingSalary": "£30,000 - £45,000",
    "midSalary": "£55,000 - £85,000",
    "seniorSalary": "£90,000 - £180,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Packaging",
      "Analytics",
      "Audience research",
      "Editing eye",
      "Communication"
    ],
    "selfEmployable": true
  },
  {
    "name": "Short-Form Video Editor",
    "category": "Marketing and Media",
    "shortDescription": "Cut Reels, TikToks and Shorts that hook viewers in the first second and keep them watching.",
    "whyItFits": "You're fast, on-trend and like editing as its own creative discipline.",
    "startingSalary": "£25,000 - £35,000",
    "midSalary": "£40,000 - £60,000",
    "seniorSalary": "£65,000 - £100,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "CapCut / Premiere",
      "Pacing",
      "Captions",
      "Sound design",
      "Trend literacy"
    ],
    "selfEmployable": true
  },
  {
    "name": "Creative Producer",
    "category": "Marketing and Media",
    "shortDescription": "Run creative projects end-to-end - budgets, talent, timelines and final delivery.",
    "whyItFits": "You're organised and creative and like being the person who makes things happen.",
    "startingSalary": "£28,000 - £40,000",
    "midSalary": "£48,000 - £70,000",
    "seniorSalary": "£80,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Project mgmt",
      "Budgeting",
      "Crew booking",
      "Client comms",
      "Problem solving"
    ]
  },
  {
    "name": "Creative Strategist",
    "category": "Marketing and Media",
    "shortDescription": "Turn brand and audience insight into the big idea that creative teams then execute.",
    "whyItFits": "You think conceptually and enjoy sitting between strategy and creative execution.",
    "startingSalary": "£35,000 - £50,000",
    "midSalary": "£55,000 - £85,000",
    "seniorSalary": "£95,000 - £150,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Insight",
      "Workshops",
      "Concepting",
      "Storytelling",
      "Stakeholder mgmt"
    ]
  },
  {
    "name": "Digital Designer",
    "category": "Marketing and Media",
    "shortDescription": "Design across websites, ads, apps and social - the modern in-house design generalist.",
    "whyItFits": "You're a strong visual designer who likes variety more than one specialism.",
    "startingSalary": "£26,000 - £36,000",
    "midSalary": "£40,000 - £58,000",
    "seniorSalary": "£65,000 - £95,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Figma",
      "Brand systems",
      "Layout",
      "Typography",
      "Motion basics"
    ],
    "selfEmployable": true
  },
  {
    "name": "Web Designer",
    "category": "Marketing and Media",
    "shortDescription": "Design websites that look great, convert visitors and reflect a brand properly.",
    "whyItFits": "You like the mix of visual design, UX thinking and shipping real, public work.",
    "startingSalary": "£26,000 - £38,000",
    "midSalary": "£42,000 - £65,000",
    "seniorSalary": "£70,000 - £110,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Figma",
      "Webflow / Framer / WordPress",
      "UX basics",
      "Copy sense",
      "SEO basics"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£200 - £1,000 (Figma, Webflow, domain, simple LLC)",
      "timeToFirstClient": "2 - 8 weeks",
      "monthOneIncome": "£0 - £3,000",
      "yearOneIncome": "£30,000 - £80,000 full-time",
      "scaledIncome": "£90,000 - £180,000 with productised packages + small team",
      "commonPitfalls": [
        "Hourly pricing instead of fixed-price packages",
        "No portfolio of real client sites",
        "Scope creep on unlimited revisions",
        "Building bespoke when a Webflow template would do"
      ],
      "firstFiveClients": [
        "Redesign 3 local businesses you'd genuinely buy from - for portfolio",
        "DM small businesses with a 60-second Loom audit",
        "Partner with a copywriter or SEO freelancer",
        "List on Webflow / Framer expert directories",
        "Niche down to one industry"
      ],
      "toolsAndCosts": [
        "Figma £12/mo",
        "Webflow £25-£40/mo per site",
        "Framer £20/mo",
        "Loom £12/mo",
        "Notion £8/mo"
      ],
      "legalBasics": [
        "Sole trader UTR or Ltd",
        "Simple website contract template",
        "Professional indemnity insurance",
        "50% deposit terms"
      ]
    }
  },
  {
    "name": "Email Designer",
    "category": "Marketing and Media",
    "shortDescription": "Design and code branded marketing and lifecycle emails that render well in every inbox.",
    "whyItFits": "You like a niche where design, copy and a bit of HTML all come together.",
    "startingSalary": "£26,000 - £36,000",
    "midSalary": "£40,000 - £58,000",
    "seniorSalary": "£65,000 - £95,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Figma",
      "HTML email",
      "Klaviyo / HubSpot",
      "Brand systems",
      "Copy sense"
    ],
    "selfEmployable": true
  },
  {
    "name": "Packaging Designer",
    "category": "Marketing and Media",
    "shortDescription": "Design product packaging that wins on shelf and survives manufacturing and shipping reality.",
    "whyItFits": "You're a visual designer who likes physical, tangible work and consumer brands.",
    "startingSalary": "£26,000 - £36,000",
    "midSalary": "£40,000 - £58,000",
    "seniorSalary": "£65,000 - £90,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Adobe suite",
      "Print production",
      "Material knowledge",
      "Brand systems",
      "Client comms"
    ]
  },
  {
    "name": "Exhibition Designer",
    "category": "Marketing and Media",
    "shortDescription": "Design exhibition stands, museum installations and experiential brand activations.",
    "whyItFits": "You like 3D, spatial design and short, intense project cycles.",
    "startingSalary": "£26,000 - £36,000",
    "midSalary": "£40,000 - £58,000",
    "seniorSalary": "£65,000 - £95,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "3D design",
      "Spatial thinking",
      "Materials",
      "Client comms",
      "Project mgmt"
    ]
  },
  {
    "name": "Game Artist",
    "category": "Marketing and Media",
    "shortDescription": "Create characters, environments and assets for video games across genres and platforms.",
    "whyItFits": "You're a strong illustrator/3D artist who wants to work on games full-time.",
    "startingSalary": "£26,000 - £38,000",
    "midSalary": "£42,000 - £65,000",
    "seniorSalary": "£70,000 - £100,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Maya / Blender",
      "Substance",
      "Anatomy",
      "Lighting",
      "Iteration"
    ]
  },
  {
    "name": "VFX Artist",
    "category": "Marketing and Media",
    "shortDescription": "Build visual effects for film, TV and games - simulations, compositing, environments.",
    "whyItFits": "You combine artistic skill with technical confidence and love high-end production.",
    "startingSalary": "£28,000 - £40,000",
    "midSalary": "£45,000 - £70,000",
    "seniorSalary": "£80,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Houdini / Nuke",
      "Compositing",
      "Lighting",
      "Pipeline awareness",
      "Iteration"
    ]
  },
  {
    "name": "Concept Artist",
    "category": "Marketing and Media",
    "shortDescription": "Visualise characters, worlds and props for film, games and animation pre-production.",
    "whyItFits": "You're an exceptional illustrator who can design as well as render.",
    "startingSalary": "£28,000 - £40,000",
    "midSalary": "£45,000 - £70,000",
    "seniorSalary": "£80,000 - £130,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Drawing",
      "Photobashing",
      "Design",
      "Anatomy",
      "Iteration"
    ],
    "selfEmployable": true
  },
  {
    "name": "Talent Agent",
    "category": "Marketing and Media",
    "shortDescription": "Represent actors, creators or sportspeople - find work, negotiate deals, manage careers.",
    "whyItFits": "You're commercially sharp, people-driven and like high-stakes negotiation.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£40,000 - £70,000",
    "seniorSalary": "£90,000 - £250,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "people": 3,
      "income": 3,
      "freedom": 2,
      "creativity": 1,
      "structure": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "Negotiation",
      "Relationships",
      "Sales",
      "Discretion",
      "Industry knowledge"
    ]
  },
  {
    "name": "Music Manager",
    "category": "Marketing and Media",
    "shortDescription": "Manage musicians' careers - releases, tours, deals, brand work.",
    "whyItFits": "You live and breathe the music industry and like long-term artist partnerships.",
    "startingSalary": "£20,000 - £30,000 (commission led)",
    "midSalary": "£35,000 - £70,000",
    "seniorSalary": "£80,000 - £250,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "people": 3,
      "freedom": 3,
      "income": 2,
      "creativity": 1,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Industry network",
      "Negotiation",
      "Marketing",
      "Touring ops",
      "Loyalty"
    ],
    "selfEmployable": true
  },
  {
    "name": "Live Events Producer",
    "category": "Marketing and Media",
    "shortDescription": "Produce live events from conferences to festivals - logistics, talent, suppliers and budgets.",
    "whyItFits": "You like high-energy, deadline-driven project work with visible results.",
    "startingSalary": "£26,000 - £36,000",
    "midSalary": "£42,000 - £65,000",
    "seniorSalary": "£75,000 - £120,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office + venues, long event-week hours",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Project mgmt",
      "Supplier mgmt",
      "Budgeting",
      "Crisis solving",
      "Talent handling"
    ]
  },
  {
    "name": "Brand Partnerships Manager",
    "category": "Marketing and Media",
    "shortDescription": "Land and run brand sponsorship deals - for creators, media or events.",
    "whyItFits": "You're a commercial relationship-builder who can sell and deliver.",
    "startingSalary": "£30,000 - £42,000",
    "midSalary": "£48,000 - £75,000",
    "seniorSalary": "£85,000 - £140,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "people": 3,
      "income": 3,
      "freedom": 2,
      "creativity": 1,
      "structure": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "Sales",
      "Account mgmt",
      "Pitching",
      "Negotiation",
      "Reporting"
    ]
  },
  {
    "name": "Podcast Editor",
    "category": "Marketing and Media",
    "shortDescription": "Edit podcasts - cut tape, mix sound, add music and ship polished episodes to schedule.",
    "whyItFits": "You have a great ear, like quiet focused work and the rise of audio media.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£36,000 - £55,000",
    "seniorSalary": "£60,000 - £90,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Office, studio or remote",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Logic / Pro Tools / Adobe",
      "Sound design",
      "Pacing",
      "Show structure",
      "Reliability"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£300 - £1,200 (DAW, plugins, decent headphones, basic site)",
      "timeToFirstClient": "2 - 6 weeks",
      "monthOneIncome": "£0 - £1,800",
      "yearOneIncome": "£25,000 - £60,000 full-time",
      "scaledIncome": "£70,000 - £130,000 with retainers + production add-ons",
      "commonPitfalls": [
        "Per-hour pricing instead of per-episode",
        "Taking on shows with chaotic hosts and no schedule",
        "No turnaround SLA",
        "Doing creative direction free"
      ],
      "firstFiveClients": [
        "Edit 3 small podcasts for free in exchange for testimonials",
        "Cold email business podcasters with a 60-second sample edit",
        "Join podcast Slack/Facebook groups",
        "Partner with a podcast launch consultant",
        "List on production agency rosters"
      ],
      "toolsAndCosts": [
        "Logic / Pro Tools £200-£600 one-off",
        "iZotope RX £200-£800",
        "Decent headphones £150-£400",
        "Cloud storage £10/mo",
        "Hindenburg £12/mo"
      ],
      "legalBasics": [
        "Sole trader UTR or Ltd",
        "Simple production agreement",
        "Per-episode pricing",
        "Late-fee terms for delayed audio"
      ]
    }
  },
  {
    "name": "Sign Maker",
    "category": "Marketing and Media",
    "shortDescription": "Design, build and install signage for shops, vehicles, exhibitions and buildings.",
    "whyItFits": "You like a practical craft trade with creative and design elements.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £42,000",
    "seniorSalary": "£48,000 - £65,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Workshop + on-site fitting",
    "stability": "Cyclical - strong for skilled specialists",
    "weights": {
      "creativity": 3,
      "freedom": 2,
      "people": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Vinyl cutting",
      "Print production",
      "Vehicle livery",
      "Installation",
      "Customer comms"
    ],
    "selfEmployable": true
  },
  {
    "name": "Commercial Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Own the commercial terms of contracts, supplier deals and major customer accounts.",
    "whyItFits": "You're a deal-maker who likes the contractual and financial side of business.",
    "startingSalary": "£35,000 - £48,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£95,000 - £150,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Construction Commercial Mgmt",
        "shortDescription": "Run commercial side of construction projects.",
        "typicalEntryRoute": "QS or commercial background + RICS route.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "IT Services Commercial",
        "shortDescription": "Manage commercial terms on outsourced IT contracts.",
        "typicalEntryRoute": "Commercial + IT background.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Outsourcing Commercial",
        "shortDescription": "Run commercial side of large outsourcing deals.",
        "typicalEntryRoute": "Senior commercial background.",
        "salaryRange": "£65,000 - £130,000"
      },
      {
        "title": "Public Sector Commercial",
        "shortDescription": "Public sector commercial and procurement leadership.",
        "typicalEntryRoute": "Commercial + Crown Commercial training.",
        "salaryRange": "£55,000 - £100,000"
      }
    ],
    "skillsNeeded": [
      "Contracts",
      "Negotiation",
      "Pricing",
      "Stakeholder mgmt",
      "Commercial modelling"
    ]
  },
  {
    "name": "Strategy Analyst",
    "category": "Business, Finance and Legal",
    "shortDescription": "Research markets, competitors and opportunities to help leadership decide where to play.",
    "whyItFits": "You like big-picture thinking, structured analysis and exec-level audiences.",
    "startingSalary": "£38,000 - £55,000",
    "midSalary": "£65,000 - £95,000",
    "seniorSalary": "£110,000 - £180,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Corporate Strategy",
        "shortDescription": "In-house strategy team work for FTSE/large corporates.",
        "typicalEntryRoute": "Top consultancy or finance background.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "Strategy Consulting",
        "shortDescription": "Big-3 / strategy boutique consultant role.",
        "typicalEntryRoute": "Strong degree + analytical background.",
        "salaryRange": "£55,000 - £110,000"
      },
      {
        "title": "Commercial Strategy",
        "shortDescription": "Pricing, portfolio and route-to-market strategy.",
        "typicalEntryRoute": "Commercial + analytics background.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "PE Strategy",
        "shortDescription": "Strategy and value creation for PE portfolio firms.",
        "typicalEntryRoute": "Consulting + finance background.",
        "salaryRange": "£70,000 - £140,000"
      }
    ],
    "skillsNeeded": [
      "Frameworks",
      "Research",
      "Modelling",
      "Storytelling",
      "Slide craft"
    ]
  },
  {
    "name": "Corporate Finance Analyst",
    "category": "Business, Finance and Legal",
    "shortDescription": "Model deals, mergers, fundraises and strategic investments for big companies.",
    "whyItFits": "You're numbers-strong, ambitious and want a hard-grad-job route into finance.",
    "startingSalary": "£45,000 - £65,000",
    "midSalary": "£75,000 - £110,000",
    "seniorSalary": "£130,000 - £250,000+",
    "timeToEntry": "3 - 5 years",
    "degreeRequired": "Usually required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "M&A Advisory",
        "shortDescription": "Sell-side and buy-side M&A advisory.",
        "typicalEntryRoute": "Top finance background + analytical degree.",
        "salaryRange": "£55,000 - £120,000"
      },
      {
        "title": "Debt Advisory",
        "shortDescription": "Advise on leverage, refinancing and bonds.",
        "typicalEntryRoute": "Finance background + ACA/CFA.",
        "salaryRange": "£55,000 - £110,000"
      },
      {
        "title": "Restructuring",
        "shortDescription": "Distressed M&A and restructuring advice.",
        "typicalEntryRoute": "Finance + restructuring background.",
        "salaryRange": "£55,000 - £110,000"
      },
      {
        "title": "Mid-market CF",
        "shortDescription": "Mid-market deals at regional CF boutiques.",
        "typicalEntryRoute": "ACA + finance background.",
        "salaryRange": "£45,000 - £90,000"
      }
    ],
    "skillsNeeded": [
      "Financial modelling",
      "Valuation",
      "PowerPoint",
      "Accounting",
      "Stamina"
    ]
  },
  {
    "name": "Credit Analyst",
    "category": "Business, Finance and Legal",
    "shortDescription": "Assess whether to lend money to companies or individuals and at what risk-adjusted terms.",
    "whyItFits": "You're analytical, cautious by nature and like quantitative decision-making.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£50,000 - £75,000",
    "seniorSalary": "£85,000 - £130,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Corporate Credit",
        "shortDescription": "Analyse corporate borrowers for banks.",
        "typicalEntryRoute": "Finance / accounting background + ACA/CFA.",
        "salaryRange": "£35,000 - £70,000"
      },
      {
        "title": "Leveraged Finance",
        "shortDescription": "Analyse PE-backed borrowers and LevFin deals.",
        "typicalEntryRoute": "Strong finance background + CFA.",
        "salaryRange": "£55,000 - £110,000"
      },
      {
        "title": "SME Credit",
        "shortDescription": "Underwrite SME lending decisions.",
        "typicalEntryRoute": "Banking + credit training.",
        "salaryRange": "£30,000 - £55,000"
      },
      {
        "title": "Credit Risk Modelling",
        "shortDescription": "Build PD/LGD models under IRB and IFRS9.",
        "typicalEntryRoute": "Quantitative background + risk training.",
        "salaryRange": "£55,000 - £100,000"
      }
    ],
    "skillsNeeded": [
      "Financial statements",
      "Risk scoring",
      "Modelling",
      "Industry research",
      "Reporting"
    ]
  },
  {
    "name": "Actuary",
    "category": "Business, Finance and Legal",
    "shortDescription": "Use stats and finance to price risk for insurers, pension funds and big employers.",
    "whyItFits": "You're maths-strong and want a long, exam-driven path with high pay at the end.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£60,000 - £90,000",
    "seniorSalary": "£110,000 - £200,000+",
    "timeToEntry": "5 - 8 years (IFoA exams)",
    "degreeRequired": "Usually required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Pensions",
        "shortDescription": "DB / DC pension scheme advisory work.",
        "typicalEntryRoute": "IFoA exams + actuarial trainee programme.",
        "salaryRange": "£35,000 - £100,000+"
      },
      {
        "title": "General Insurance",
        "shortDescription": "Pricing and reserving in GI insurers.",
        "typicalEntryRoute": "IFoA + GI trainee programme.",
        "salaryRange": "£35,000 - £110,000+"
      },
      {
        "title": "Life Insurance / Life Assurance",
        "shortDescription": "Life office reserving and capital work.",
        "typicalEntryRoute": "IFoA + life trainee programme.",
        "salaryRange": "£35,000 - £110,000+"
      },
      {
        "title": "Investment Risk",
        "shortDescription": "Investment consulting and risk advisory.",
        "typicalEntryRoute": "IFoA + investment background.",
        "salaryRange": "£40,000 - £130,000+"
      },
      {
        "title": "Enterprise Risk",
        "shortDescription": "ERM and capital modelling at insurers.",
        "typicalEntryRoute": "Qualified actuary + ERM training.",
        "salaryRange": "£70,000 - £180,000+"
      }
    ],
    "skillsNeeded": [
      "Maths",
      "Stats",
      "Modelling",
      "Exam stamina",
      "Communication"
    ]
  },
  {
    "name": "Underwriter",
    "category": "Business, Finance and Legal",
    "shortDescription": "Decide what risks an insurer takes on and at what price - from cars to corporate fleets.",
    "whyItFits": "You like applied judgement, risk thinking and a clear career inside insurance.",
    "startingSalary": "£28,000 - £38,000",
    "midSalary": "£42,000 - £65,000",
    "seniorSalary": "£75,000 - £120,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Property Underwriter",
        "shortDescription": "Underwrite commercial property insurance.",
        "typicalEntryRoute": "Insurance background + CII.",
        "salaryRange": "£30,000 - £65,000"
      },
      {
        "title": "Casualty Underwriter",
        "shortDescription": "Liability and casualty lines underwriting.",
        "typicalEntryRoute": "Insurance background + CII.",
        "salaryRange": "£32,000 - £68,000"
      },
      {
        "title": "Specialty Underwriter",
        "shortDescription": "Marine, aviation or cyber specialty lines.",
        "typicalEntryRoute": "Senior underwriter + specialty training.",
        "salaryRange": "£45,000 - £95,000"
      },
      {
        "title": "Reinsurance Underwriter",
        "shortDescription": "Treaty and facultative reinsurance.",
        "typicalEntryRoute": "Senior underwriter + reinsurance background.",
        "salaryRange": "£60,000 - £130,000"
      }
    ],
    "skillsNeeded": [
      "Risk assessment",
      "Underwriting rules",
      "Negotiation",
      "Numeracy",
      "Communication"
    ]
  },
  {
    "name": "Claims Handler",
    "category": "Business, Finance and Legal",
    "shortDescription": "Manage insurance claims end to end - assess, investigate, decide and pay or decline.",
    "whyItFits": "You're empathetic but firm, organised and want a stable entry into the insurance world.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£30,000 - £40,000",
    "seniorSalary": "£45,000 - £60,000",
    "timeToEntry": "0 - 6 months",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Motor Claims",
        "shortDescription": "Handle motor insurance claims end-to-end.",
        "typicalEntryRoute": "Insurance experience + provider training.",
        "salaryRange": "£22,000 - £35,000"
      },
      {
        "title": "Property Claims",
        "shortDescription": "Domestic and commercial property claims.",
        "typicalEntryRoute": "Insurance experience + CILA route.",
        "salaryRange": "£25,000 - £40,000"
      },
      {
        "title": "Liability Claims",
        "shortDescription": "EL/PL and third-party liability claims.",
        "typicalEntryRoute": "Insurance experience + CILA.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Major Loss Claims",
        "shortDescription": "Complex major loss claims handling.",
        "typicalEntryRoute": "Senior handler + CILA / loss adjuster background.",
        "salaryRange": "£40,000 - £75,000"
      }
    ],
    "skillsNeeded": [
      "Communication",
      "Empathy",
      "Process discipline",
      "Decision making",
      "IT systems"
    ]
  },
  {
    "name": "Payroll Specialist",
    "category": "Business, Finance and Legal",
    "shortDescription": "Run accurate, on-time payroll for businesses - tax, pensions, benefits, deductions.",
    "whyItFits": "You're meticulous, calm under deadlines and like critical back-office work.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£48,000 - £65,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "In-house Payroll",
        "shortDescription": "Run monthly payroll for one large employer.",
        "typicalEntryRoute": "CIPP qualification + experience.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Bureau Payroll",
        "shortDescription": "Manage multiple client payrolls in a bureau.",
        "typicalEntryRoute": "CIPP + bureau experience.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Global / Expat Payroll",
        "shortDescription": "International payroll and modified PAYE.",
        "typicalEntryRoute": "Senior payroll + global experience.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "Payroll Systems Specialist",
        "shortDescription": "Implement and manage payroll systems.",
        "typicalEntryRoute": "Payroll + systems background.",
        "salaryRange": "£45,000 - £75,000"
      }
    ],
    "skillsNeeded": [
      "Payroll software",
      "PAYE / NI / Pensions",
      "Reconciliation",
      "Discretion",
      "Communication"
    ]
  },
  {
    "name": "Bookkeeper",
    "category": "Business, Finance and Legal",
    "shortDescription": "Keep small businesses' financial records accurate and ready for the accountant or HMRC.",
    "whyItFits": "You're detail-driven and want a flexible, in-demand role you can run solo.",
    "startingSalary": "£20,000 - £28,000",
    "midSalary": "£28,000 - £40,000",
    "seniorSalary": "£45,000 - £65,000",
    "timeToEntry": "6 - 18 months (AAT route)",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Small Business Bookkeeper",
        "shortDescription": "Day-to-day books for SMEs.",
        "typicalEntryRoute": "AAT Level 2/3.",
        "salaryRange": "£22,000 - £35,000"
      },
      {
        "title": "Cloud Accounting (Xero/QuickBooks)",
        "shortDescription": "Run client books in cloud accounting tools.",
        "typicalEntryRoute": "AAT + cloud certifications.",
        "salaryRange": "£25,000 - £40,000"
      },
      {
        "title": "Sub-contracted Bookkeeping",
        "shortDescription": "Outsourced bookkeeping for accountants.",
        "typicalEntryRoute": "AAT + experience.",
        "salaryRange": "£25 - £45 / hr"
      },
      {
        "title": "Construction CIS Bookkeeping",
        "shortDescription": "Specialist CIS and subcontractor bookkeeping.",
        "typicalEntryRoute": "AAT + CIS training.",
        "salaryRange": "£28,000 - £45,000"
      }
    ],
    "skillsNeeded": [
      "Xero / QuickBooks",
      "VAT",
      "Reconciliation",
      "Discretion",
      "Communication"
    ],
    "selfEmployable": true
  },
  {
    "name": "Finance Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Run a company's day-to-day finance function - reporting, controls, planning, team.",
    "whyItFits": "You're a qualified accountant who wants a leadership role inside a business.",
    "startingSalary": "£45,000 - £60,000",
    "midSalary": "£60,000 - £85,000",
    "seniorSalary": "£95,000 - £140,000",
    "timeToEntry": "5 - 8 years (ACA/ACCA/CIMA)",
    "degreeRequired": "Usually required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Management Accounts",
        "shortDescription": "Monthly reporting and variance analysis.",
        "typicalEntryRoute": "ACCA/CIMA qualified.",
        "salaryRange": "£50,000 - £80,000"
      },
      {
        "title": "Group Finance",
        "shortDescription": "Group consolidation and statutory accounts.",
        "typicalEntryRoute": "ACA/ACCA + group experience.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "FP&A",
        "shortDescription": "Forecasting, budgeting and business partnering.",
        "typicalEntryRoute": "CIMA + FP&A experience.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "Treasury",
        "shortDescription": "Cash, FX and funding management.",
        "typicalEntryRoute": "Finance + AMCT route.",
        "salaryRange": "£55,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "Management accounting",
      "Controls",
      "Leadership",
      "Reporting",
      "Systems"
    ]
  },
  {
    "name": "Procurement Officer",
    "category": "Business, Finance and Legal",
    "shortDescription": "Source and buy goods and services for an organisation at the right price, quality and risk.",
    "whyItFits": "You like commercial work with a structured, process-driven side.",
    "startingSalary": "£26,000 - £35,000",
    "midSalary": "£38,000 - £55,000",
    "seniorSalary": "£65,000 - £95,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Indirect Procurement",
        "shortDescription": "Marketing, IT and facilities procurement.",
        "typicalEntryRoute": "CIPS Level 4+.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "Direct / Goods for Resale",
        "shortDescription": "Buying for manufacturers and retailers.",
        "typicalEntryRoute": "CIPS + commercial background.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "Public Sector Procurement",
        "shortDescription": "Run OJEU / Find a Tender procurements.",
        "typicalEntryRoute": "CIPS + public sector experience.",
        "salaryRange": "£35,000 - £62,000"
      },
      {
        "title": "Construction Procurement",
        "shortDescription": "Subcontractor and materials procurement.",
        "typicalEntryRoute": "CIPS + construction background.",
        "salaryRange": "£40,000 - £70,000"
      }
    ],
    "skillsNeeded": [
      "Tendering",
      "Supplier mgmt",
      "Negotiation",
      "Contracts",
      "Reporting"
    ]
  },
  {
    "name": "Contract Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Manage commercial contracts post-signature - performance, risk, change, renewals.",
    "whyItFits": "You're detail-oriented, commercially literate and like long-term supplier or client work.",
    "startingSalary": "£30,000 - £42,000",
    "midSalary": "£48,000 - £70,000",
    "seniorSalary": "£80,000 - £120,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Commercial Contracts",
        "shortDescription": "Manage major commercial supplier contracts.",
        "typicalEntryRoute": "Commercial + IACCM training.",
        "salaryRange": "£45,000 - £80,000"
      },
      {
        "title": "Construction Contracts",
        "shortDescription": "NEC / JCT contract administration.",
        "typicalEntryRoute": "QS or commercial background.",
        "salaryRange": "£50,000 - £85,000"
      },
      {
        "title": "IT Outsourcing Contracts",
        "shortDescription": "Manage large IT outsourcing agreements.",
        "typicalEntryRoute": "Commercial + IT background.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Government Contracts",
        "shortDescription": "Manage large central / local government contracts.",
        "typicalEntryRoute": "Commercial + public sector experience.",
        "salaryRange": "£50,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "Contracts",
      "Risk",
      "Stakeholder mgmt",
      "Negotiation",
      "Reporting"
    ]
  },
  {
    "name": "Company Secretary",
    "category": "Business, Finance and Legal",
    "shortDescription": "Make sure a company meets all its legal, regulatory and governance obligations.",
    "whyItFits": "You're meticulous and like the formal, board-level side of corporate life.",
    "startingSalary": "£35,000 - £48,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £150,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Listed Company Secretary",
        "shortDescription": "Run plc board and listing rules compliance.",
        "typicalEntryRoute": "ICSA / CGI qualification.",
        "salaryRange": "£70,000 - £150,000+"
      },
      {
        "title": "Private Company Sec",
        "shortDescription": "Company secretarial for PE / large private firms.",
        "typicalEntryRoute": "CGI qualification.",
        "salaryRange": "£50,000 - £95,000"
      },
      {
        "title": "Subsidiary Governance",
        "shortDescription": "Manage subsidiary governance across groups.",
        "typicalEntryRoute": "Senior cosec + group experience.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Charity / NFP Cosec",
        "shortDescription": "Governance for charities and NFPs.",
        "typicalEntryRoute": "CGI + charity governance training.",
        "salaryRange": "£40,000 - £75,000"
      }
    ],
    "skillsNeeded": [
      "Governance",
      "Companies Act",
      "Board comms",
      "Drafting",
      "Discretion"
    ]
  },
  {
    "name": "Legal Secretary",
    "category": "Business, Finance and Legal",
    "shortDescription": "Support solicitors with admin, documents, scheduling and case management.",
    "whyItFits": "You're organised, professional and want a way into the legal world without a law degree.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£28,000 - £36,000",
    "seniorSalary": "£38,000 - £48,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Litigation Secretary",
        "shortDescription": "Support litigation teams with bundles and filings.",
        "typicalEntryRoute": "ILEX/CILEx route or experience.",
        "salaryRange": "£25,000 - £40,000"
      },
      {
        "title": "Corporate Secretary",
        "shortDescription": "Support M&A and corporate teams.",
        "typicalEntryRoute": "Legal secretarial experience.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Property Secretary",
        "shortDescription": "Conveyancing and property team support.",
        "typicalEntryRoute": "Legal sec + property experience.",
        "salaryRange": "£25,000 - £42,000"
      },
      {
        "title": "Family Law Secretary",
        "shortDescription": "Support family law solicitors.",
        "typicalEntryRoute": "Legal sec experience.",
        "salaryRange": "£24,000 - £40,000"
      }
    ],
    "skillsNeeded": [
      "Audio typing",
      "Legal terminology",
      "Discretion",
      "Case mgmt systems",
      "Scheduling"
    ]
  },
  {
    "name": "Barrister",
    "category": "Business, Finance and Legal",
    "shortDescription": "Specialist court advocate - argue cases, advise on complex legal issues, draft pleadings.",
    "whyItFits": "You're a confident performer who loves argument, reading and intellectual rigour.",
    "startingSalary": "£15,000 - £45,000 (pupillage varies)",
    "midSalary": "£60,000 - £150,000",
    "seniorSalary": "£200,000 - £1,000,000+",
    "timeToEntry": "5 - 7 years (LLB + Bar + pupillage)",
    "degreeRequired": "Required",
    "difficulty": "High",
    "competitionLevel": "Extremely High",
    "workEnvironment": "Chambers, court, mostly self-employed",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Criminal Bar",
        "shortDescription": "Defence and prosecution work.",
        "typicalEntryRoute": "BPC + pupillage at criminal set.",
        "salaryRange": "£20,000 - £200,000+"
      },
      {
        "title": "Commercial Bar",
        "shortDescription": "Commercial disputes and arbitration.",
        "typicalEntryRoute": "BPC + pupillage at commercial set.",
        "salaryRange": "£60,000 - £1,000,000+"
      },
      {
        "title": "Family Bar",
        "shortDescription": "Family law including children and ancillary relief.",
        "typicalEntryRoute": "BPC + pupillage at family set.",
        "salaryRange": "£40,000 - £250,000"
      },
      {
        "title": "Employment Bar",
        "shortDescription": "Employment tribunal and EAT work.",
        "typicalEntryRoute": "BPC + pupillage at employment set.",
        "salaryRange": "£40,000 - £250,000"
      }
    ],
    "skillsNeeded": [
      "Advocacy",
      "Legal research",
      "Drafting",
      "Stamina",
      "Resilience"
    ],
    "selfEmployable": true
  },
  {
    "name": "Patent Attorney",
    "category": "Business, Finance and Legal",
    "shortDescription": "Draft and prosecute patents - protect inventions for companies and inventors worldwide.",
    "whyItFits": "You combine a STEM degree with strong writing and love niche, well-paid work.",
    "startingSalary": "£35,000 - £50,000",
    "midSalary": "£70,000 - £110,000",
    "seniorSalary": "£130,000 - £250,000+",
    "timeToEntry": "5 - 7 years (qualifying exams)",
    "degreeRequired": "STEM degree required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Engineering Patents",
        "shortDescription": "Draft and prosecute engineering patents.",
        "typicalEntryRoute": "STEM degree + CIPA exams.",
        "salaryRange": "£40,000 - £120,000+"
      },
      {
        "title": "Life Sciences Patents",
        "shortDescription": "Biotech and pharma patent practice.",
        "typicalEntryRoute": "Bio/chem PhD route + CIPA.",
        "salaryRange": "£45,000 - £130,000+"
      },
      {
        "title": "Electronics and Software",
        "shortDescription": "EE / software patent practice.",
        "typicalEntryRoute": "Engineering + CIPA route.",
        "salaryRange": "£45,000 - £130,000+"
      },
      {
        "title": "In-house Patent Counsel",
        "shortDescription": "Patent strategy inside large tech companies.",
        "typicalEntryRoute": "Qualified attorney + senior experience.",
        "salaryRange": "£90,000 - £180,000+"
      }
    ],
    "skillsNeeded": [
      "Technical writing",
      "Patent law",
      "Analysis",
      "Client comms",
      "Exam stamina"
    ]
  },
  {
    "name": "Immigration Adviser",
    "category": "Business, Finance and Legal",
    "shortDescription": "Help people and employers navigate UK visa, sponsorship and citizenship applications.",
    "whyItFits": "You care about people, like complex rules and want a clear regulated path.",
    "startingSalary": "£25,000 - £32,000",
    "midSalary": "£35,000 - £55,000",
    "seniorSalary": "£60,000 - £100,000",
    "timeToEntry": "1 - 3 years (OISC levels or law route)",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "OISC Level 1 Adviser",
        "shortDescription": "Initial immigration advice for individuals.",
        "typicalEntryRoute": "OISC Level 1 accreditation.",
        "salaryRange": "£25,000 - £40,000"
      },
      {
        "title": "OISC Level 2/3 Adviser",
        "shortDescription": "Complex casework including appeals.",
        "typicalEntryRoute": "OISC Level 2/3 accreditation.",
        "salaryRange": "£35,000 - £70,000"
      },
      {
        "title": "Business Immigration",
        "shortDescription": "Sponsor licences and skilled worker visas.",
        "typicalEntryRoute": "OISC or solicitor + business background.",
        "salaryRange": "£40,000 - £90,000"
      },
      {
        "title": "Asylum and Human Rights",
        "shortDescription": "Asylum and appeals work in legal aid firms.",
        "typicalEntryRoute": "OISC L3 / solicitor + LAA experience.",
        "salaryRange": "£35,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "Immigration rules",
      "Client comms",
      "Casework",
      "Empathy",
      "Drafting"
    ],
    "selfEmployable": true
  },
  {
    "name": "Insolvency Practitioner",
    "category": "Business, Finance and Legal",
    "shortDescription": "Manage company insolvencies and personal bankruptcies under the Insolvency Act.",
    "whyItFits": "You're commercially sharp, like crisis work and want a regulated specialism.",
    "startingSalary": "£28,000 - £40,000",
    "midSalary": "£55,000 - £90,000",
    "seniorSalary": "£100,000 - £200,000+",
    "timeToEntry": "5 - 7 years (JIEB exams)",
    "degreeRequired": "Usually required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Corporate Insolvency",
        "shortDescription": "Administrations and liquidations of companies.",
        "typicalEntryRoute": "JIEB exams + R3 membership.",
        "salaryRange": "£60,000 - £150,000+"
      },
      {
        "title": "Personal Insolvency",
        "shortDescription": "IVA, bankruptcy and personal insolvency.",
        "typicalEntryRoute": "JIEB exams + IPA route.",
        "salaryRange": "£45,000 - £100,000"
      },
      {
        "title": "Restructuring Advisory",
        "shortDescription": "Pre-insolvency restructuring advisory.",
        "typicalEntryRoute": "Senior IP + advisory experience.",
        "salaryRange": "£80,000 - £180,000+"
      },
      {
        "title": "Forensic Insolvency",
        "shortDescription": "Investigations and director conduct work.",
        "typicalEntryRoute": "IP + forensic accounting background.",
        "salaryRange": "£70,000 - £140,000"
      }
    ],
    "skillsNeeded": [
      "Insolvency law",
      "Negotiation",
      "Stakeholder mgmt",
      "Investigation",
      "Reporting"
    ]
  },
  {
    "name": "Business Continuity Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Plan how a business keeps running through crises - cyber, fire, pandemic, supplier failure.",
    "whyItFits": "You think in worst-case scenarios and like work that quietly protects everything else.",
    "startingSalary": "£35,000 - £48,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £140,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "BC Programme Manager",
        "shortDescription": "Run the BC programme across a large org.",
        "typicalEntryRoute": "BCI Diploma / MBCI.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "Operational Resilience",
        "shortDescription": "PRA/FCA operational resilience programmes.",
        "typicalEntryRoute": "BC + financial services background.",
        "salaryRange": "£65,000 - £115,000"
      },
      {
        "title": "Crisis Management",
        "shortDescription": "Lead crisis exercises and response.",
        "typicalEntryRoute": "BC + crisis training.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "IT Disaster Recovery",
        "shortDescription": "DR planning and testing for IT estates.",
        "typicalEntryRoute": "IT + BC background.",
        "salaryRange": "£55,000 - £100,000"
      }
    ],
    "skillsNeeded": [
      "Risk assessment",
      "BCP/DRP",
      "Workshops",
      "Exercises",
      "Reporting"
    ]
  },
  {
    "name": "Internal Auditor",
    "category": "Business, Finance and Legal",
    "shortDescription": "Independently check that a company's controls, finances and processes are working as they should.",
    "whyItFits": "You're sceptical, detail-driven and like getting under the bonnet of how businesses really run.",
    "startingSalary": "£32,000 - £45,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £140,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Financial Audit",
        "shortDescription": "Test financial controls across the group.",
        "typicalEntryRoute": "ACA / ACCA / CIA route.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Operational Audit",
        "shortDescription": "Audit business processes and ops.",
        "typicalEntryRoute": "CIA + operational background.",
        "salaryRange": "£42,000 - £75,000"
      },
      {
        "title": "IT Audit",
        "shortDescription": "Audit IT controls and systems.",
        "typicalEntryRoute": "CISA + IT background.",
        "salaryRange": "£50,000 - £90,000"
      },
      {
        "title": "Financial Crime Audit",
        "shortDescription": "Audit AML, sanctions and fraud controls.",
        "typicalEntryRoute": "Audit + financial crime background.",
        "salaryRange": "£50,000 - £90,000"
      }
    ],
    "skillsNeeded": [
      "Controls testing",
      "Risk",
      "Interviewing",
      "Reporting",
      "Stakeholder mgmt"
    ]
  },
  {
    "name": "External Auditor",
    "category": "Business, Finance and Legal",
    "shortDescription": "Audit company accounts as part of a regulated practice - assurance work for investors and HMRC.",
    "whyItFits": "You want the classic accountancy grad route with a clear qualification path.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£45,000 - £70,000",
    "seniorSalary": "£90,000 - £150,000",
    "timeToEntry": "3 - 5 years (ACA/ACCA)",
    "degreeRequired": "Usually required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Audit Trainee",
        "shortDescription": "Train as auditor in Big 4 / mid-tier firm.",
        "typicalEntryRoute": "Strong degree + ACA training contract.",
        "salaryRange": "£28,000 - £45,000"
      },
      {
        "title": "Manager Audit",
        "shortDescription": "Manage audit fieldwork and clients.",
        "typicalEntryRoute": "ACA + 3-5 years post qualification.",
        "salaryRange": "£55,000 - £85,000"
      },
      {
        "title": "Specialist Audit",
        "shortDescription": "Banking, insurance or public sector specialty.",
        "typicalEntryRoute": "ACA + specialty experience.",
        "salaryRange": "£55,000 - £95,000"
      },
      {
        "title": "Audit Partner Track",
        "shortDescription": "Senior manager to director route to partner.",
        "typicalEntryRoute": "Senior ACA + portfolio building.",
        "salaryRange": "£100,000 - £400,000+"
      }
    ],
    "skillsNeeded": [
      "Auditing standards",
      "Accounting",
      "Analysis",
      "Client comms",
      "Stamina"
    ]
  },
  {
    "name": "Quality Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Run quality systems (ISO 9001, sector standards) to keep products and services consistent.",
    "whyItFits": "You're process-driven and like work where standards and improvement matter.",
    "startingSalary": "£32,000 - £42,000",
    "midSalary": "£45,000 - £65,000",
    "seniorSalary": "£70,000 - £100,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "ISO 9001 Quality Mgmt",
        "shortDescription": "Run ISO 9001 QMS across an organisation.",
        "typicalEntryRoute": "CQI Lead Auditor + experience.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "Pharma / Medical Devices QA",
        "shortDescription": "Quality in regulated GxP environments.",
        "typicalEntryRoute": "QA + GxP / ISO 13485 background.",
        "salaryRange": "£50,000 - £90,000"
      },
      {
        "title": "Aerospace AS9100",
        "shortDescription": "Quality in aerospace manufacturing.",
        "typicalEntryRoute": "QA + AS9100 training.",
        "salaryRange": "£45,000 - £80,000"
      },
      {
        "title": "Construction Quality",
        "shortDescription": "Quality on major infrastructure projects.",
        "typicalEntryRoute": "QA + construction background.",
        "salaryRange": "£45,000 - £80,000"
      }
    ],
    "skillsNeeded": [
      "ISO 9001",
      "Audit",
      "Continuous improvement",
      "Documentation",
      "Stakeholder mgmt"
    ]
  },
  {
    "name": "Office Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Run the day-to-day of an office - facilities, suppliers, people support, events.",
    "whyItFits": "You're organised, calm and like being the person everyone relies on quietly.",
    "startingSalary": "£25,000 - £32,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£48,000 - £65,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Single Site Office Manager",
        "shortDescription": "Run day-to-day office for one site.",
        "typicalEntryRoute": "Office admin + supervisory experience.",
        "salaryRange": "£30,000 - £50,000"
      },
      {
        "title": "Practice Manager (Professional Services)",
        "shortDescription": "Run a small law/accountancy firm office.",
        "typicalEntryRoute": "Senior office manager.",
        "salaryRange": "£35,000 - £60,000"
      },
      {
        "title": "EA + Office Manager",
        "shortDescription": "Combined exec assistant + office manager role.",
        "typicalEntryRoute": "Senior EA / office mgr background.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "Multi-site Office Manager",
        "shortDescription": "Coordinate offices across multiple cities.",
        "typicalEntryRoute": "Senior office manager.",
        "salaryRange": "£40,000 - £75,000"
      }
    ],
    "skillsNeeded": [
      "Operations",
      "People skills",
      "Supplier mgmt",
      "Event basics",
      "IT systems"
    ]
  },
  {
    "name": "Client Success Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Own client relationships post-sale - drive adoption, renewals and growth.",
    "whyItFits": "You're relationship-led, commercially aware and like keeping happy clients happy.",
    "startingSalary": "£30,000 - £42,000",
    "midSalary": "£45,000 - £70,000",
    "seniorSalary": "£80,000 - £130,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "people": 3,
      "income": 2,
      "structure": 1,
      "freedom": 1,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "SaaS CSM",
        "shortDescription": "Manage SaaS customer accounts and renewals.",
        "typicalEntryRoute": "CS background + product expertise.",
        "salaryRange": "£40,000 - £80,000"
      },
      {
        "title": "Enterprise CSM",
        "shortDescription": "Manage strategic enterprise accounts.",
        "typicalEntryRoute": "Senior CS background.",
        "salaryRange": "£60,000 - £110,000 + variable"
      },
      {
        "title": "Technical CSM",
        "shortDescription": "Embedded technical success for developer tools.",
        "typicalEntryRoute": "Technical + CS background.",
        "salaryRange": "£55,000 - £105,000"
      },
      {
        "title": "CS Operations",
        "shortDescription": "Build CS playbooks, tooling and analytics.",
        "typicalEntryRoute": "CS + ops background.",
        "salaryRange": "£50,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "Account mgmt",
      "Communication",
      "Negotiation",
      "Product knowledge",
      "Reporting"
    ]
  },
  {
    "name": "Partnerships Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Build and run business-to-business partnerships that drive revenue or distribution.",
    "whyItFits": "You're a relationship-driven deal-maker who likes long-term commercial work.",
    "startingSalary": "£35,000 - £48,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £140,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "people": 3,
      "income": 3,
      "freedom": 2,
      "creativity": 1,
      "structure": 0
    },
    "specialisations": [
      {
        "title": "Channel Partnerships",
        "shortDescription": "Manage reseller and distributor relationships.",
        "typicalEntryRoute": "Sales / BD background.",
        "salaryRange": "£50,000 - £90,000 + variable"
      },
      {
        "title": "Tech Partnerships",
        "shortDescription": "Integration and co-marketing with tech partners.",
        "typicalEntryRoute": "BD + technical product background.",
        "salaryRange": "£55,000 - £100,000"
      },
      {
        "title": "Strategic Alliances",
        "shortDescription": "Senior partnership work with strategic accounts.",
        "typicalEntryRoute": "Senior BD background.",
        "salaryRange": "£70,000 - £140,000"
      },
      {
        "title": "Affiliate / Influencer Partnerships",
        "shortDescription": "Manage affiliate and creator partner programmes.",
        "typicalEntryRoute": "Marketing + partnerships background.",
        "salaryRange": "£40,000 - £80,000"
      }
    ],
    "skillsNeeded": [
      "BD",
      "Negotiation",
      "Account mgmt",
      "Strategy",
      "Communication"
    ]
  },
  {
    "name": "Property Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Manage residential or commercial property portfolios for landlords or investors.",
    "whyItFits": "You're organised, people-friendly and like a tangible asset class.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£35,000 - £50,000",
    "seniorSalary": "£55,000 - £80,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Block Management",
        "shortDescription": "Manage residential blocks and leaseholders.",
        "typicalEntryRoute": "IRPM / TPI qualification.",
        "salaryRange": "£28,000 - £50,000"
      },
      {
        "title": "Commercial Property Mgmt",
        "shortDescription": "Manage commercial buildings and tenants.",
        "typicalEntryRoute": "RICS / IRPM route.",
        "salaryRange": "£35,000 - £65,000"
      },
      {
        "title": "BTR / Build-to-Rent",
        "shortDescription": "Run BTR developments end to end.",
        "typicalEntryRoute": "Senior property mgmt + BTR experience.",
        "salaryRange": "£40,000 - £75,000"
      },
      {
        "title": "Asset Management",
        "shortDescription": "Manage property portfolios for landlords/funds.",
        "typicalEntryRoute": "Senior PM + asset management training.",
        "salaryRange": "£50,000 - £95,000"
      }
    ],
    "skillsNeeded": [
      "Tenancy law",
      "Contractor mgmt",
      "Customer comms",
      "Finance basics",
      "Driving licence"
    ],
    "selfEmployable": true
  },
  {
    "name": "Lettings Negotiator",
    "category": "Business, Finance and Legal",
    "shortDescription": "Find tenants for landlords - viewings, references, paperwork, deal close.",
    "whyItFits": "You like a high-energy sales role with strong commission upside.",
    "startingSalary": "£20,000 - £26,000 + commission",
    "midSalary": "£30,000 - £45,000",
    "seniorSalary": "£50,000 - £75,000",
    "timeToEntry": "0 - 6 months",
    "degreeRequired": "Not required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "people": 3,
      "income": 3,
      "freedom": 1,
      "creativity": 0,
      "structure": 0
    },
    "specialisations": [
      {
        "title": "High-Street Lettings",
        "shortDescription": "Negotiate residential lettings for a local agency.",
        "typicalEntryRoute": "Sales aptitude + ARLA training.",
        "salaryRange": "£22,000 - £45,000 + comm"
      },
      {
        "title": "Prime Central London",
        "shortDescription": "Lettings in PCL and high-end markets.",
        "typicalEntryRoute": "Senior lettings background.",
        "salaryRange": "£35,000 - £100,000+ + comm"
      },
      {
        "title": "Build-to-Rent Lettings",
        "shortDescription": "Letting BTR developments at scale.",
        "typicalEntryRoute": "Lettings + BTR experience.",
        "salaryRange": "£28,000 - £55,000"
      },
      {
        "title": "Corporate Relocations",
        "shortDescription": "Lettings for corporate relocation clients.",
        "typicalEntryRoute": "Lettings + corporate background.",
        "salaryRange": "£32,000 - £65,000"
      }
    ],
    "skillsNeeded": [
      "Sales",
      "Customer comms",
      "Negotiation",
      "Local market knowledge",
      "Driving licence"
    ]
  },
  {
    "name": "Auctioneer",
    "category": "Business, Finance and Legal",
    "shortDescription": "Run live and online auctions across property, art, vehicles or commercial assets.",
    "whyItFits": "You're confident on stage, fast on your feet and like the theatre of a live sale.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£35,000 - £55,000",
    "seniorSalary": "£65,000 - £100,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Property Auctioneer",
        "shortDescription": "Run residential and commercial property auctions.",
        "typicalEntryRoute": "RICS or auction house training.",
        "salaryRange": "£35,000 - £100,000+"
      },
      {
        "title": "Fine Art Auctioneer",
        "shortDescription": "Auction art and antiques.",
        "typicalEntryRoute": "Art history degree + auction house route.",
        "salaryRange": "£30,000 - £150,000+"
      },
      {
        "title": "Livestock Auctioneer",
        "shortDescription": "Run livestock and farm auctions.",
        "typicalEntryRoute": "Agricultural background + auctioneering training.",
        "salaryRange": "£30,000 - £70,000"
      },
      {
        "title": "Charity Auctioneer",
        "shortDescription": "Charity benefit and fundraising auctions.",
        "typicalEntryRoute": "Auctioneer + presentation skills.",
        "salaryRange": "£300 - £2,000 per event"
      }
    ],
    "skillsNeeded": [
      "Public speaking",
      "Valuation",
      "Negotiation",
      "Industry knowledge",
      "Stamina"
    ],
    "selfEmployable": true
  },
  {
    "name": "Estate Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Run a private country estate - staff, properties, land, events, finances.",
    "whyItFits": "You're operationally strong and want a high-trust, varied role outside corporate life.",
    "startingSalary": "£35,000 - £50,000",
    "midSalary": "£55,000 - £80,000",
    "seniorSalary": "£90,000 - £150,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Private estate, often with accommodation",
    "stability": "Strong across sectors",
    "weights": {
      "structure": 2,
      "income": 2,
      "people": 1,
      "creativity": 0,
      "freedom": 0
    },
    "specialisations": [
      {
        "title": "Country Estate Manager",
        "shortDescription": "Run a private country estate and its enterprises.",
        "typicalEntryRoute": "Land management background + experience.",
        "salaryRange": "£45,000 - £100,000+"
      },
      {
        "title": "Sporting Estate Manager",
        "shortDescription": "Game, shoot and sporting estate management.",
        "typicalEntryRoute": "Estate background + gamekeeping knowledge.",
        "salaryRange": "£40,000 - £80,000"
      },
      {
        "title": "Heritage Estate Manager",
        "shortDescription": "Manage stately home or trust-owned estates.",
        "typicalEntryRoute": "Estate mgmt + heritage background.",
        "salaryRange": "£40,000 - £80,000"
      },
      {
        "title": "Farming Estate Manager",
        "shortDescription": "In-hand farming and tenanted estate mgmt.",
        "typicalEntryRoute": "Senior estate manager + farming background.",
        "salaryRange": "£50,000 - £100,000"
      }
    ],
    "skillsNeeded": [
      "Operations",
      "Land management",
      "Staff mgmt",
      "Finance basics",
      "Discretion"
    ]
  },
  {
    "name": "Customer Success Manager",
    "category": "Business, Finance and Legal",
    "shortDescription": "Make sure customers get value from a product so they stay, expand and refer.",
    "whyItFits": "You're customer-obsessed, commercial and like a mix of strategy and hands-on support.",
    "startingSalary": "£30,000 - £42,000",
    "midSalary": "£48,000 - £75,000",
    "seniorSalary": "£90,000 - £140,000",
    "timeToEntry": "2 - 5 years",
    "degreeRequired": "Often required",
    "difficulty": "High",
    "competitionLevel": "High",
    "workEnvironment": "Office or hybrid",
    "stability": "Strong across sectors",
    "weights": {
      "people": 3,
      "income": 2,
      "structure": 1,
      "freedom": 1,
      "creativity": 0
    },
    "specialisations": [
      {
        "title": "Onboarding CSM",
        "shortDescription": "Specialist onboarding for new customers.",
        "typicalEntryRoute": "CS background + product expertise.",
        "salaryRange": "£40,000 - £70,000"
      },
      {
        "title": "Renewals CSM",
        "shortDescription": "Focus on renewals, expansion and churn reduction.",
        "typicalEntryRoute": "CS background + commercial skills.",
        "salaryRange": "£45,000 - £85,000"
      },
      {
        "title": "Strategic CSM",
        "shortDescription": "Senior CSM for strategic accounts.",
        "typicalEntryRoute": "Senior CS background.",
        "salaryRange": "£60,000 - £110,000"
      },
      {
        "title": "Scaled / Digital CSM",
        "shortDescription": "Run 1:many programmes for SMB segments.",
        "typicalEntryRoute": "CS + ops / programme background.",
        "salaryRange": "£45,000 - £85,000"
      }
    ],
    "skillsNeeded": [
      "Account mgmt",
      "Onboarding",
      "Communication",
      "Analytics",
      "Product knowledge"
    ]
  },
  {
    "name": "Library Assistant",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Help run a public, school or university library - lending, events, helping users.",
    "whyItFits": "You like quiet, community-focused work and a stable public sector role.",
    "startingSalary": "£20,000 - £24,000",
    "midSalary": "£24,000 - £30,000",
    "seniorSalary": "£30,000 - £36,000",
    "timeToEntry": "0 - 6 months",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Customer service",
      "Cataloguing",
      "Computer literacy",
      "Patience",
      "Communication"
    ]
  },
  {
    "name": "Librarian",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Curate collections, run services and lead the team in a public, academic or specialist library.",
    "whyItFits": "You love books and information and want a meaningful, mission-led career.",
    "startingSalary": "£26,000 - £32,000",
    "midSalary": "£35,000 - £45,000",
    "seniorSalary": "£50,000 - £70,000",
    "timeToEntry": "3 - 5 years (often MA)",
    "degreeRequired": "Often required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Information mgmt",
      "Cataloguing",
      "Service design",
      "Leadership",
      "Tech literacy"
    ]
  },
  {
    "name": "Museum Curator",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Look after collections, lead research and design exhibitions in museums and galleries.",
    "whyItFits": "You're scholarly, creative and love bringing history or art to wide audiences.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £75,000",
    "timeToEntry": "3 - 6 years (often MA)",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Extremely High",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Subject expertise",
      "Research",
      "Writing",
      "Exhibition design",
      "Stakeholder mgmt"
    ]
  },
  {
    "name": "Museum Technician",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Install, maintain and protect exhibits and collections behind the scenes at museums.",
    "whyItFits": "You're practical, careful and like working close to objects without curatorial admin.",
    "startingSalary": "£22,000 - £28,000",
    "midSalary": "£28,000 - £36,000",
    "seniorSalary": "£40,000 - £55,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Handling",
      "AV / lighting",
      "Conservation basics",
      "Practical skills",
      "Health & safety"
    ]
  },
  {
    "name": "Archivist",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Preserve and catalogue historical records so researchers and the public can use them.",
    "whyItFits": "You're detail-driven, love history and want quiet, meaningful information work.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£32,000 - £42,000",
    "seniorSalary": "£48,000 - £65,000",
    "timeToEntry": "3 - 5 years (often MA)",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Cataloguing",
      "Conservation basics",
      "Research",
      "Writing",
      "Discretion"
    ]
  },
  {
    "name": "Heritage Officer",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Protect historic buildings, sites and landscapes through advice, planning and grants.",
    "whyItFits": "You care about place and history and like a public-sector specialism.",
    "startingSalary": "£26,000 - £32,000",
    "midSalary": "£35,000 - £48,000",
    "seniorSalary": "£55,000 - £75,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Heritage law",
      "Conservation",
      "Community engagement",
      "Reporting",
      "Field work"
    ]
  },
  {
    "name": "Youth Offending Officer",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Work with young people in the justice system to reduce reoffending and support change.",
    "whyItFits": "You're resilient, relational and want challenging, meaningful frontline work.",
    "startingSalary": "£28,000 - £35,000",
    "midSalary": "£35,000 - £45,000",
    "seniorSalary": "£50,000 - £65,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Often required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Safeguarding",
      "Risk assessment",
      "Empathy",
      "Multi-agency working",
      "Resilience"
    ]
  },
  {
    "name": "Housing Officer",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Manage council or housing association properties and support tenants.",
    "whyItFits": "You like community-focused work with practical and people sides.",
    "startingSalary": "£25,000 - £32,000",
    "midSalary": "£32,000 - £42,000",
    "seniorSalary": "£48,000 - £62,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Tenancy law",
      "Customer service",
      "Conflict handling",
      "Reporting",
      "Safeguarding"
    ]
  },
  {
    "name": "Community Development Worker",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Help local communities organise, access services and tackle issues that matter to them.",
    "whyItFits": "You're a connector who loves grassroots, mission-led work.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£30,000 - £40,000",
    "seniorSalary": "£45,000 - £58,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Community engagement",
      "Project work",
      "Empathy",
      "Partnership building",
      "Reporting"
    ]
  },
  {
    "name": "Charity Manager",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Run a charity or programme - strategy, fundraising, team, impact and reporting.",
    "whyItFits": "You want leadership in a mission-led organisation rather than the corporate world.",
    "startingSalary": "£30,000 - £40,000",
    "midSalary": "£45,000 - £65,000",
    "seniorSalary": "£70,000 - £110,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Leadership",
      "Fundraising",
      "Reporting",
      "Strategy",
      "Stakeholder mgmt"
    ]
  },
  {
    "name": "Campaigns Officer",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Plan and run campaigns for charities, political organisations or unions.",
    "whyItFits": "You're persuasive, organised and care deeply about an issue.",
    "startingSalary": "£25,000 - £32,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £70,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Strategy",
      "Comms",
      "Mobilising supporters",
      "Project mgmt",
      "Writing"
    ]
  },
  {
    "name": "Political Researcher",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Research policy and politics for MPs, think tanks, parties or media.",
    "whyItFits": "You're a politics nerd who likes fast-paced research and writing.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£55,000 - £80,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Usually required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Research",
      "Writing",
      "Policy knowledge",
      "Networks",
      "Briefing skills"
    ]
  },
  {
    "name": "Local Government Officer",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Deliver council services - planning, housing, environment, social care administration.",
    "whyItFits": "You want stable public service work that touches your local community.",
    "startingSalary": "£24,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£50,000 - £75,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Public service",
      "Casework",
      "Reporting",
      "Stakeholder mgmt",
      "Policy"
    ]
  },
  {
    "name": "Education Welfare Officer",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Work with families and schools to improve children's attendance and engagement.",
    "whyItFits": "You're empathetic, organised and want education-focused safeguarding work.",
    "startingSalary": "£26,000 - £32,000",
    "midSalary": "£32,000 - £42,000",
    "seniorSalary": "£45,000 - £55,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Safeguarding",
      "Family work",
      "Reporting",
      "Multi-agency",
      "Resilience"
    ]
  },
  {
    "name": "Careers Adviser",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Help young people and adults make decisions about education, training and work.",
    "whyItFits": "You like a 1:1 advisory role with clear impact and a mix of schools, colleges and adult settings.",
    "startingSalary": "£26,000 - £32,000",
    "midSalary": "£32,000 - £42,000",
    "seniorSalary": "£45,000 - £58,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Coaching",
      "Labour market knowledge",
      "Empathy",
      "Comms",
      "Reporting"
    ],
    "selfEmployable": true
  },
  {
    "name": "School Business Manager",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Run the non-teaching side of a school - finance, HR, premises, contracts, compliance.",
    "whyItFits": "You like operational leadership and want a stable role inside education.",
    "startingSalary": "£32,000 - £42,000",
    "midSalary": "£45,000 - £60,000",
    "seniorSalary": "£65,000 - £85,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Sometimes required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Finance",
      "HR",
      "Procurement",
      "Compliance",
      "Leadership"
    ]
  },
  {
    "name": "Learning Mentor",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Support pupils who need extra help with learning, behaviour, attendance or emotional needs.",
    "whyItFits": "You're patient, relational and want to work with young people in schools.",
    "startingSalary": "£20,000 - £25,000",
    "midSalary": "£25,000 - £30,000",
    "seniorSalary": "£30,000 - £36,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Mentoring",
      "Safeguarding",
      "Patience",
      "Empathy",
      "Boundaries"
    ]
  },
  {
    "name": "SEN Teacher",
    "category": "Public Sector and Emergency Services",
    "shortDescription": "Teach children with special educational needs in mainstream or specialist settings.",
    "whyItFits": "You combine teaching skill with patience and want specialist, meaningful work.",
    "startingSalary": "£30,000 - £36,000",
    "midSalary": "£38,000 - £48,000",
    "seniorSalary": "£55,000 - £70,000",
    "timeToEntry": "3 - 5 years (QTS + experience)",
    "degreeRequired": "Required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office, community or institutional setting",
    "stability": "Very stable - public funding driven",
    "weights": {
      "people": 2,
      "structure": 2,
      "income": 0,
      "creativity": 0,
      "freedom": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "SEND expertise",
      "Differentiation",
      "Patience",
      "Safeguarding",
      "Multi-agency"
    ]
  },
  {
    "name": "Event Manager",
    "category": "Hospitality and Food",
    "shortDescription": "Run corporate, brand and private events end-to-end - venues, suppliers, talent and on-the-night ops.",
    "whyItFits": "You're organised, calm under pressure and love bringing rooms of people together.",
    "startingSalary": "£24,000 - £32,000",
    "midSalary": "£35,000 - £55,000",
    "seniorSalary": "£65,000 - £100,000",
    "timeToEntry": "0 - 2 years",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Office + venues, intense event-week hours",
    "stability": "Strong for skilled operators - seasonal at entry",
    "weights": {
      "people": 2,
      "creativity": 1,
      "freedom": 1,
      "structure": 0,
      "income": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "Project mgmt",
      "Supplier mgmt",
      "Budgeting",
      "Crisis solving",
      "Client comms"
    ],
    "selfEmployable": true
  },
  {
    "name": "Wedding Planner",
    "category": "Hospitality and Food",
    "shortDescription": "Plan and run weddings end-to-end - venue, suppliers, timeline, family management and on-the-day delivery.",
    "whyItFits": "You're calm under pressure, deeply organised and love creating once-in-a-lifetime moments.",
    "startingSalary": "£18,000 - £28,000 (often part-time start)",
    "midSalary": "£35,000 - £60,000",
    "seniorSalary": "£70,000 - £150,000+",
    "timeToEntry": "0 - 2 years",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Client meetings + venues, weekends-heavy",
    "stability": "Strong for skilled operators - seasonal at entry",
    "weights": {
      "people": 3,
      "creativity": 2,
      "freedom": 3,
      "income": 1,
      "structure": 1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Project mgmt",
      "Supplier network",
      "Client comms",
      "Crisis solving",
      "Aesthetic sense"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£500 - £2,500 (website, photo shoot, software, marketing, insurance)",
      "timeToFirstClient": "2 - 6 months",
      "monthOneIncome": "£0 - £1,000",
      "yearOneIncome": "£15,000 - £45,000 part-time first year is normal",
      "scaledIncome": "£60,000 - £150,000+ with 12-25 weddings/yr + add-ons",
      "commonPitfalls": [
        "Under-charging - planners earn from packages not hours",
        "Not specialising (luxury / outdoor / cultural)",
        "Saying yes to weddings outside your real radius",
        "No clear contract = scope creep + payment fights"
      ],
      "firstFiveClients": [
        "Style 2-3 free shoots with photographers - publish in blogs",
        "Get listed on Hitched / Bridebook",
        "Partner with 5 venues as a preferred planner",
        "Run an Instagram showing the planning process, not just photos",
        "Niche down - 'small luxury weddings' or 'multicultural weddings'"
      ],
      "toolsAndCosts": [
        "Aisle Planner / HoneyBook £30-£50/mo",
        "Canva Pro £10/mo",
        "Cloud storage £10/mo",
        "Website £20/mo",
        "Professional photos £500-£2,000"
      ],
      "legalBasics": [
        "Sole trader UTR or Ltd",
        "Public liability + professional indemnity £30-£60/mo",
        "Detailed contract with payment schedule",
        "Deposit non-refundable terms"
      ]
    }
  },
  {
    "name": "Travel Agent",
    "category": "Hospitality and Food",
    "shortDescription": "Plan and book holidays for clients - flights, hotels, experiences, ground logistics.",
    "whyItFits": "You love travel and want a relationship-led, commission-paying role you can run remotely.",
    "startingSalary": "£20,000 - £26,000 + commission",
    "midSalary": "£28,000 - £45,000",
    "seniorSalary": "£50,000 - £90,000",
    "timeToEntry": "0 - 12 months",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Venue, hotel, travel or events",
    "stability": "Strong for skilled operators - seasonal at entry",
    "weights": {
      "people": 3,
      "freedom": 2,
      "creativity": 2,
      "income": 1,
      "structure": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "GDS systems",
      "Destination knowledge",
      "Sales",
      "Customer comms",
      "Problem solving"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£300 - £1,500 (host agency setup, marketing, IATA registration via host)",
      "timeToFirstClient": "1 - 3 months",
      "monthOneIncome": "£0 - £1,500",
      "yearOneIncome": "£15,000 - £40,000",
      "scaledIncome": "£60,000 - £150,000+ specialising in luxury or niche travel",
      "commonPitfalls": [
        "Trying to compete with Booking.com on cheap flights",
        "Not joining a host agency - kills your commission rates",
        "No specialism (family / honeymoon / luxury / safari)",
        "Free planning that doesn't convert"
      ],
      "firstFiveClients": [
        "Friends, family, colleagues - plan one full trip free for testimonials",
        "Niche Instagram around one destination type",
        "Get on a host agency's preferred-list with a supplier",
        "Run a referral programme - 5% commission share",
        "Speak in local Facebook groups (school mums, expat groups)"
      ],
      "toolsAndCosts": [
        "Host agency fees £30-£100/mo",
        "CRM £30/mo",
        "Canva £10/mo",
        "Website £20/mo",
        "FAM trips - mostly comped"
      ],
      "legalBasics": [
        "Trade under a host agency for ATOL/ABTA cover",
        "Public liability via host",
        "Clear T&Cs with deposit schedule",
        "PCI compliance handled by host"
      ]
    }
  },
  {
    "name": "Tour Manager",
    "category": "Hospitality and Food",
    "shortDescription": "Lead groups on tours - from coach trips to high-end cultural and adventure travel.",
    "whyItFits": "You love travel, people and being the calm leader of a group on the road.",
    "startingSalary": "£22,000 - £28,000 + tips",
    "midSalary": "£30,000 - £45,000",
    "seniorSalary": "£50,000 - £75,000",
    "timeToEntry": "0 - 2 years",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "On the road, often months at a time",
    "stability": "Strong for skilled operators - seasonal at entry",
    "weights": {
      "people": 3,
      "freedom": 3,
      "creativity": 1,
      "income": 0,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Group leadership",
      "Logistics",
      "Local knowledge",
      "Languages helpful",
      "Crisis solving"
    ]
  },
  {
    "name": "Cruise Ship Worker",
    "category": "Hospitality and Food",
    "shortDescription": "Work on cruise ships in hospitality, entertainment or technical roles - travel while you earn.",
    "whyItFits": "You want to travel, save money fast and live in a tight-knit work community.",
    "startingSalary": "£18,000 - £25,000 (board+lodging)",
    "midSalary": "£28,000 - £40,000",
    "seniorSalary": "£45,000 - £70,000",
    "timeToEntry": "0 - 2 years",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "On ship - long contracts away from home",
    "stability": "Strong for skilled operators - seasonal at entry",
    "weights": {
      "people": 2,
      "freedom": 1,
      "income": 0,
      "structure": 2,
      "creativity": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "Customer service",
      "Resilience",
      "Teamwork",
      "Adaptability",
      "Long-hours stamina"
    ]
  },
  {
    "name": "Concierge",
    "category": "Hospitality and Food",
    "shortDescription": "Be the go-to fixer for guests at luxury hotels or private clients - reservations, experiences, problem solving.",
    "whyItFits": "You're charming, resourceful and like work where your network is your asset.",
    "startingSalary": "£24,000 - £32,000 + tips",
    "midSalary": "£35,000 - £55,000",
    "seniorSalary": "£60,000 - £90,000",
    "timeToEntry": "0 - 2 years",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Luxury hotel or private setting",
    "stability": "Strong for skilled operators - seasonal at entry",
    "weights": {
      "people": 3,
      "freedom": 1,
      "creativity": 1,
      "income": 1,
      "structure": 1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Local knowledge",
      "Network building",
      "Discretion",
      "Languages helpful",
      "Calm under pressure"
    ]
  },
  {
    "name": "Luxury Travel Advisor",
    "category": "Hospitality and Food",
    "shortDescription": "Design bespoke high-end trips for wealthy clients - private villas, safaris, private jets.",
    "whyItFits": "You're detail-driven, well-travelled and like the high-touch, high-trust end of travel.",
    "startingSalary": "£28,000 - £40,000 + commission",
    "midSalary": "£50,000 - £85,000",
    "seniorSalary": "£100,000 - £200,000+",
    "timeToEntry": "0 - 2 years",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Venue, hotel, travel or events",
    "stability": "Strong for skilled operators - seasonal at entry",
    "weights": {
      "people": 3,
      "freedom": 2,
      "creativity": 2,
      "income": 3,
      "structure": 1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Destination expertise",
      "Supplier network",
      "Discretion",
      "Sales",
      "Client comms"
    ],
    "selfEmployable": true
  },
  {
    "name": "Venue Manager",
    "category": "Hospitality and Food",
    "shortDescription": "Run a hotel, conference centre, restaurant or events venue - team, P&L, guest experience.",
    "whyItFits": "You're operationally strong, like hospitality and want clear leadership.",
    "startingSalary": "£28,000 - £38,000",
    "midSalary": "£40,000 - £60,000",
    "seniorSalary": "£70,000 - £110,000",
    "timeToEntry": "0 - 2 years",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Venue - long, anti-social hours",
    "stability": "Strong for skilled operators - seasonal at entry",
    "weights": {
      "people": 2,
      "creativity": 1,
      "freedom": 1,
      "structure": 0,
      "income": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "Operations",
      "P&L",
      "Team mgmt",
      "Customer experience",
      "Compliance"
    ]
  },
  {
    "name": "Interior Designer",
    "category": "Design and Creative",
    "shortDescription": "Design residential or commercial interiors - layout, materials, lighting, furniture and finish.",
    "whyItFits": "You love space, materials and the craft of making rooms feel right.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£35,000 - £55,000",
    "seniorSalary": "£70,000 - £130,000+",
    "timeToEntry": "2 - 4 years",
    "degreeRequired": "Not required but helpful",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Studio + client sites",
    "stability": "Strong for specialists - portfolio led",
    "weights": {
      "creativity": 3,
      "people": 2,
      "freedom": 2,
      "income": 1,
      "structure": 1
    },
    "specialisations": [],
    "skillsNeeded": [
      "CAD / SketchUp",
      "Mood boards",
      "Materials",
      "Client comms",
      "Project mgmt"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£800 - £3,000 (CAD subscriptions, sample boards, portfolio shoot, website)",
      "timeToFirstClient": "2 - 6 months",
      "monthOneIncome": "£0 - £2,000",
      "yearOneIncome": "£20,000 - £55,000",
      "scaledIncome": "£80,000 - £200,000+ with full-room or whole-house projects",
      "commonPitfalls": [
        "Hourly billing instead of design fee + procurement margin",
        "Doing 'consultations' for free that don't convert",
        "No clear style/specialism in the portfolio",
        "Underbilling for site visits"
      ],
      "firstFiveClients": [
        "Style and shoot 2-3 rooms for free for portfolio",
        "Get on Houzz with at least 10 photos",
        "Partner with 2-3 builders and a property stager",
        "Niche Instagram around one style (Scandi / modern country / urban luxe)",
        "Local Facebook groups - 'home renovation' threads"
      ],
      "toolsAndCosts": [
        "SketchUp / AutoCAD £25-£60/mo",
        "Adobe £55/mo",
        "Material samples library £200-£800",
        "Loom £12/mo",
        "Pro photography £500-£2,000 per shoot"
      ],
      "legalBasics": [
        "Sole trader UTR or Ltd",
        "Professional indemnity insurance £30-£60/mo",
        "Detailed contract with scope + milestones",
        "Procurement T&Cs and deposit schedule"
      ]
    }
  },
  {
    "name": "Home Stager",
    "category": "Design and Creative",
    "shortDescription": "Style empty or lived-in homes for sale or rent so they shoot beautifully and sell faster.",
    "whyItFits": "You combine interior style with a commercial, sales-driven goal.",
    "startingSalary": "£22,000 - £30,000",
    "midSalary": "£32,000 - £45,000",
    "seniorSalary": "£55,000 - £85,000",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Studio, client site or remote",
    "stability": "Strong for specialists - portfolio led",
    "weights": {
      "creativity": 3,
      "people": 2,
      "freedom": 2,
      "income": 1,
      "structure": 1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Styling",
      "Furniture sourcing",
      "Logistics",
      "Photography eye",
      "Estate agent relationships"
    ],
    "selfEmployable": true
  },
  {
    "name": "Personal Shopper",
    "category": "Design and Creative",
    "shortDescription": "Style and source clothes (or homewares, lifestyle) for individual clients in person and online.",
    "whyItFits": "You have great taste, love clothes and like 1:1 work with a small loyal client base.",
    "startingSalary": "£20,000 - £28,000",
    "midSalary": "£32,000 - £50,000",
    "seniorSalary": "£60,000 - £120,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Studio, client site or remote",
    "stability": "Strong for specialists - portfolio led",
    "weights": {
      "creativity": 3,
      "people": 3,
      "freedom": 3,
      "income": 1,
      "structure": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "Styling eye",
      "Body shape knowledge",
      "Sourcing",
      "Client comms",
      "Discretion"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£300 - £1,500 (website, branded photo shoot, basic styling kit)",
      "timeToFirstClient": "1 - 3 months",
      "monthOneIncome": "£0 - £1,200",
      "yearOneIncome": "£15,000 - £40,000",
      "scaledIncome": "£60,000 - £150,000+ with high-net-worth clients + wardrobe edits + virtual styling",
      "commonPitfalls": [
        "Pricing per hour instead of per session / package",
        "No niche (men / mums / professional women / plus-size)",
        "Trying to do walk-up retail commission and personal styling",
        "No referral programme"
      ],
      "firstFiveClients": [
        "Style 3 friends for free in exchange for testimonials + photos",
        "Partner with a department store personal-shopping team",
        "Run free 'style audit' sessions on Instagram Live",
        "Niche down hard - one client type only",
        "Offer a paid 30-min Zoom intro session"
      ],
      "toolsAndCosts": [
        "Calendly £8/mo",
        "Stripe payments (2%/0.20p)",
        "Pinterest premium (free)",
        "Branded clothing bags £100",
        "Pro photography £500-£1,500"
      ],
      "legalBasics": [
        "Sole trader UTR or Ltd",
        "Public liability insurance £15-£25/mo",
        "Clear T&Cs around purchase commissions",
        "Deposit + cancellation policy"
      ]
    }
  },
  {
    "name": "Stylist",
    "category": "Design and Creative",
    "shortDescription": "Style shoots, brands or individuals - fashion, editorial, commercial or personal work.",
    "whyItFits": "You have a strong visual point of view and want creative project-based work.",
    "startingSalary": "£18,000 - £26,000",
    "midSalary": "£32,000 - £55,000",
    "seniorSalary": "£65,000 - £150,000+",
    "timeToEntry": "1 - 3 years",
    "degreeRequired": "Not required - portfolio matters most",
    "difficulty": "Medium",
    "competitionLevel": "High",
    "workEnvironment": "Studio, client site or remote",
    "stability": "Strong for specialists - portfolio led",
    "weights": {
      "creativity": 3,
      "people": 2,
      "freedom": 3,
      "income": 1,
      "structure": -1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Visual references",
      "Sourcing",
      "On-set styling",
      "Client comms",
      "Logistics"
    ],
    "selfEmployable": true
  },
  {
    "name": "Private PA",
    "category": "Self-Employed and Business Owner",
    "shortDescription": "Run the life of a busy individual or family - diary, travel, household, projects.",
    "whyItFits": "You're hyper-organised, discreet and want a high-trust, high-paying private role.",
    "startingSalary": "£30,000 - £45,000",
    "midSalary": "£50,000 - £80,000",
    "seniorSalary": "£90,000 - £160,000+",
    "timeToEntry": "0 - 12 months",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "Client home / office, often live-in option",
    "stability": "Income tied to client book - feast or famine early on",
    "weights": {
      "people": 3,
      "structure": 2,
      "freedom": 1,
      "income": 2,
      "creativity": 1
    },
    "specialisations": [],
    "skillsNeeded": [
      "Diary mgmt",
      "Travel planning",
      "Discretion",
      "Tech literacy",
      "Calm under pressure"
    ],
    "selfEmployable": true
  },
  {
    "name": "Chauffeur",
    "category": "Self-Employed and Business Owner",
    "shortDescription": "Drive private clients - executives, celebrities or families - safely, smoothly and on schedule.",
    "whyItFits": "You're a calm, professional driver who values discretion and steady client work.",
    "startingSalary": "£28,000 - £38,000 + tips",
    "midSalary": "£40,000 - £55,000",
    "seniorSalary": "£60,000 - £85,000",
    "timeToEntry": "0 - 6 months",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "On the road, often anti-social hours",
    "stability": "Income tied to client book - feast or famine early on",
    "weights": {
      "people": 2,
      "structure": 2,
      "freedom": 1,
      "income": 1,
      "creativity": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "Advanced driving",
      "Route planning",
      "Discretion",
      "Customer comms",
      "Vehicle care"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£3,000 - £40,000 (suit/uniform, advanced driver training, licensed vehicle if self-supplied)",
      "timeToFirstClient": "2 - 8 weeks via agencies",
      "monthOneIncome": "£1,500 - £4,000",
      "yearOneIncome": "£30,000 - £60,000",
      "scaledIncome": "£70,000 - £120,000 with private retainers + executive contracts",
      "commonPitfalls": [
        "Buying an expensive car too early before clients are committed",
        "Working only nights/weekends - burns out fast",
        "No agency relationships - feast or famine",
        "Skimping on insurance class"
      ],
      "firstFiveClients": [
        "Sign with 2-3 reputable chauffeur agencies",
        "Wedding work via Hitched + local florists / planners",
        "Cold email local executive PAs with rate card",
        "Partner with a 5-star hotel concierge desk",
        "Airport transfers via app-based platforms first"
      ],
      "toolsAndCosts": [
        "Hire and reward insurance £2,000-£5,000/yr",
        "Suit/uniform £400-£1,200",
        "Advanced driving qual £400-£1,000",
        "PCO/private hire licence (varies by council)",
        "Vehicle valeting £80-£150/mo"
      ],
      "legalBasics": [
        "Private hire licence with local council",
        "Hire and reward insurance",
        "DBS check",
        "Clear T&Cs and cancellation policy"
      ]
    }
  },
  {
    "name": "Security Driver",
    "category": "Self-Employed and Business Owner",
    "shortDescription": "Drive at-risk clients securely - executives, diplomats, journalists - blending driving and protection.",
    "whyItFits": "You're security-trained, a strong driver and want a serious, well-paid niche.",
    "startingSalary": "£35,000 - £50,000",
    "midSalary": "£55,000 - £85,000",
    "seniorSalary": "£90,000 - £150,000+",
    "timeToEntry": "1 - 3 years (advanced + close protection)",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "On the road, often overseas",
    "stability": "Income tied to client book - feast or famine early on",
    "weights": {
      "people": 1,
      "structure": 3,
      "freedom": 1,
      "income": 2,
      "creativity": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "Advanced driving",
      "Threat awareness",
      "Route planning",
      "Discretion",
      "First aid"
    ],
    "selfEmployable": true
  },
  {
    "name": "Close Protection Officer",
    "category": "Self-Employed and Business Owner",
    "shortDescription": "Provide personal security for high-profile or at-risk clients - on the move and at residences.",
    "whyItFits": "You have a military/police background and want a high-trust, high-pay private role.",
    "startingSalary": "£35,000 - £55,000",
    "midSalary": "£60,000 - £100,000",
    "seniorSalary": "£120,000 - £250,000+",
    "timeToEntry": "1 - 3 years (SIA CP licence + experience)",
    "degreeRequired": "Not required",
    "difficulty": "Medium",
    "competitionLevel": "Moderate",
    "workEnvironment": "With client - UK and overseas",
    "stability": "Income tied to client book - feast or famine early on",
    "weights": {
      "people": 1,
      "structure": 3,
      "freedom": 1,
      "income": 3,
      "creativity": 0
    },
    "specialisations": [],
    "skillsNeeded": [
      "SIA CP licence",
      "Threat assessment",
      "Driving",
      "First aid",
      "Discretion"
    ],
    "selfEmployable": true,
    "soloMeta": {
      "startupCostGBP": "£3,000 - £8,000 (SIA CP course, first aid, kit, basic comms gear, suits)",
      "timeToFirstClient": "1 - 6 months through agencies and ex-services networks",
      "monthOneIncome": "£0 - £5,000 (day rates £200-£500)",
      "yearOneIncome": "£35,000 - £80,000",
      "scaledIncome": "£100,000 - £250,000+ with HNW retainers or overseas tasking",
      "commonPitfalls": [
        "Treating it like ex-services - clients want polished and discreet",
        "Not investing in advanced driving + medical",
        "Working only via one agency",
        "Sloppy social media"
      ],
      "firstFiveClients": [
        "Register with 3-5 reputable CP agencies",
        "Network with PA / family office community",
        "Specialise (women's CP, journalists, family CP) for differentiation",
        "Get on celebrity tour security rosters via fixers",
        "Build relationships with HNW family offices"
      ],
      "toolsAndCosts": [
        "SIA CP course £1,500-£2,500",
        "Advanced driving £1,000-£3,000",
        "Medical (FREC 3+) £500-£1,200",
        "Comms / earpiece kit £200-£500",
        "Suits and travel kit £800-£2,000"
      ],
      "legalBasics": [
        "SIA CP licence (required)",
        "Public liability + professional indemnity",
        "Clear contract per assignment",
        "Tax treatment of overseas earnings"
      ]
    }
  }
] as CareerSeed[];
